import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
 
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  FlatList,
  Dimensions,
  Alert,
  Modal,
  TextInput,
  Switch,
  RefreshControl,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Animated,
  SectionList,
  Image,
 
 Share, Linking, StatusBar,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as ImagePicker from 'expo-image-picker';
import { LinearGradient } from 'expo-linear-gradient';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import * as Contacts from 'expo-contacts';
import * as Calendar from 'expo-calendar';
import { ManageAPI, PayrollIntegrationAPI } from '../../../services/employeeApi';
import { registerCallback } from '../../../utils/navigationCallbacks';

const { width, height } = Dimensions.get('window');

// Constants
const STORAGE_KEYS = {
  EMPLOYEE_DATA: 'employeeManagementData', // NOTE: company-scoped at runtime
  BACKUP_DATA: 'employeeBackupData',
  SETTINGS: 'employeeManagementSettings'
};

const EMPLOYEE_STATUS = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  ON_LEAVE: 'On Leave',
  TERMINATED: 'Terminated',
  PROBATION: 'Probation'
};

const WARNING_SEVERITY = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical'
};

const EmployeeManagementScreen = ({ navigation, route }) => {
  const { companyProfile: routeCompanyProfile, companyId: routeCompanyId } = route?.params || {};

  // ── Permissions (controlled by AdminHRManagementScreen) ──
  const [perms, setPerms] = useState({
    add_employee: true, edit_employee: true, delete_employee: false,
    approve_leave: true, process_payroll: false, view_payroll: true,
    post_job: true, issue_warning: false, terminate_employee: false,
    schedule_review: true, create_onboarding: true, bulk_actions: false,
    export_reports: true, view_financials: true, view_analytics: true,
    view_employee_details: true, full_employee_mgmt: true,
    resolve_warnings: false, delete_warnings: false,
  });
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  // ── Screen Password Gate (set by AdminHRManagementScreen) ──
  const [screenPasswordVerified, setScreenPasswordVerified] = useState(false);
  const [screenHasPassword, setScreenHasPassword] = useState(false);
  const [screenPassword, setScreenPassword] = useState('');
  const [screenPasswordError, setScreenPasswordError] = useState('');
  const [screenPasswordAttempts, setScreenPasswordAttempts] = useState(0);
  const [screenLockoutUntil, setScreenLockoutUntil] = useState(null);
  const [showScreenPassword, setShowScreenPassword] = useState(false);
  const [screenPasswordChecked, setScreenPasswordChecked] = useState(false);
  const can = (action) => isAdminUser || !!perms[action];

  // State Management
  // ── Company logo ──────────────────────────────────────────────────────────
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [employeeData, setEmployeeData] = useState({
    overview: {
      totalEmployees: 0,
      activeEmployees: 0,
      onLeave: 0,
      newHires: 0,
      pendingActions: 0,
      warningLetters: 0,
      promotions: 0,
      performanceReviews: 0,
      turnoverRate: 0,
      avgTenure: 0
    },
    employees: [],
    warningLetters: [],
    promotions: [],
    performanceReviews: [],
    employeeCodes: [],
    departments: [],
    analytics: {
      departmentDistribution: {},
      statusDistribution: {},
      tenureDistribution: {},
      salaryDistribution: {}
    },
    settings: {
      autoBackup: true,
      backupFrequency: 'daily',
      exportFormat: 'csv',
      notifications: true
    },
    lastBackup: null,
    version: '1.0.0'
  });
  
  // UI State
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [showEditEmployeeModal, setShowEditEmployeeModal] = useState(false);
  const [showWarningLetterModal, setShowWarningLetterModal] = useState(false);
  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);
  const [showEmployeeCodeModal, setShowEmployeeCodeModal] = useState(false);
  const [showBulkActionsModal, setShowBulkActionsModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showPayrollSyncModal, setShowPayrollSyncModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterEmployeeCode, setFilterEmployeeCode] = useState('All');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [fadeAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(0));

  // Form States with Enhanced Validation
  const [newEmployee, setNewEmployee] = useState({
    id: '',
    employeeId: '',
    name: '',
    email: '',
    phone: '',
    department: 'Engineering',
    position: '',
    employeeCode: 'FULL_TIME',
    salary: '',
    startDate: new Date().toISOString().split('T')[0],
    manager: '',
    location: 'Office',
    status: EMPLOYEE_STATUS.ACTIVE,
    emergencyContact: '',
    emergencyPhone: '',
    address: '',
    dateOfBirth: '',
    nationalId: '',
    bankAccount: '',
    skills: [],
    notes: '',
    avatar: `https://ui-avatars.com/api/?name=Employee&background=0e4e96&color=fff&size=128`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [importCsvText, setImportCsvText] = useState('');

  const [warningLetter, setWarningLetter] = useState({
    id: '',
    employeeId: '',
    employeeName: '',
    type: 'Verbal Warning',
    reason: 'Attendance',
    description: '',
    issuedBy: '',
    issuedDate: new Date().toISOString().split('T')[0],
    acknowledgmentRequired: true,
    followUpDate: '',
    severity: WARNING_SEVERITY.LOW,
    status: 'Active',
    notes: '',
    attachments: [],
    createdAt: new Date().toISOString()
  });

  const [promotion, setPromotion] = useState({
    id: '',
    employeeId: '',
    employeeName: '',
    currentPosition: '',
    newPosition: '',
    currentDepartment: '',
    newDepartment: '',
    currentSalary: '',
    newSalary: '',
    effectiveDate: new Date().toISOString().split('T')[0],
    reason: '',
    approvedBy: '',
    type: 'Promotion',
    status: 'Pending',
    notes: '',
    createdAt: new Date().toISOString()
  });

  const [performanceReview, setPerformanceReview] = useState({
    id: '',
    employeeId: '',
    employeeName: '',
    reviewPeriod: `Q${Math.floor((new Date().getMonth() + 3) / 3)} ${new Date().getFullYear()}`,
    reviewDate: new Date().toISOString().split('T')[0],
    reviewer: '',
    overallRating: '3',
    goals: '',
    achievements: '',
    areasForImprovement: '',
    developmentPlan: '',
    nextReviewDate: '',
    status: 'Draft',
    ratings: {
      communication: '3',
      teamwork: '3',
      leadership: '3',
      technical: '3',
      productivity: '3',
      initiative: '3'
    },
    comments: '',
    createdAt: new Date().toISOString()
  });

  const [employeeCode, setEmployeeCode] = useState({
    id: '',
    code: '',
    name: '',
    description: '',
    category: 'Employment Type',
    color: '#0e4e96',
    isActive: true,
    benefits: '',
    requirements: '',
    createdAt: new Date().toISOString()
  });

  // Data Constants
  const departments = ['All', 'Engineering', 'Marketing', 'Sales', 'HR', 'Finance', 'Operations', 'Customer Service', 'IT', 'Legal', 'Research', 'Support'];
  const employeeStatuses = ['All', ...Object.values(EMPLOYEE_STATUS)];
  const employeeCodes = ['All', 'FULL_TIME', 'PART_TIME', 'CONTRACT', 'INTERN', 'CONSULTANT', 'REMOTE', 'HYBRID', 'FREELANCE'];
  const warningTypes = ['Verbal Warning', 'Written Warning', 'Final Warning', 'Suspension', 'Termination Notice'];
  const warningReasons = ['Attendance', 'Performance', 'Conduct', 'Policy Violation', 'Safety', 'Insubordination', 'Harassment', 'Theft', 'Other'];
  const severityLevels = Object.values(WARNING_SEVERITY);
  const promotionTypes = ['Promotion', 'Lateral Move', 'Demotion', 'Transfer', 'Role Change'];
  const reviewStatuses = ['Draft', 'In Progress', 'Completed', 'Approved', 'Archived'];
  const ratingScale = ['1', '2', '3', '4', '5'];
  const codeCategories = ['Employment Type', 'Skill Level', 'Security Clearance', 'Department Code', 'Location Code', 'Project Code'];
  
  const sortOptions = [
    { key: 'name', label: 'Name' },
    { key: 'department', label: 'Department' },
    { key: 'position', label: 'Position' },
    { key: 'salary', label: 'Salary' },
    { key: 'startDate', label: 'Start Date' },
    { key: 'status', label: 'Status' },
    { key: 'performanceRating', label: 'Performance' }
  ];

  const tabs = [
    { id: 'overview', title: 'Overview', icon: 'view-dashboard', color: '#0e4e96' },
    { id: 'employees', title: 'Employees', icon: 'account-group', color: '#10b981' },
    { id: 'warnings', title: 'Warnings', icon: 'alert-circle', color: '#dc2626' },
    { id: 'promotions', title: 'Promotions', icon: 'trending-up', color: '#f59e0b' },
    { id: 'performance', title: 'Performance', icon: 'chart-line', color: '#8b5cf6' },
    { id: 'codes', title: 'Codes', icon: 'code-tags', color: '#06b6d4' },
    { id: 'analytics', title: 'Analytics', icon: 'chart-bar', color: '#ec4899' },
    { id: 'evaluation', title: 'Evaluation', icon: 'clipboard-check', color: '#7C3AED' },
  ];

  // Animation Effects

  // ── Load permissions from AdminHR settings ──
  const loadPermissions = async () => {
    try {
      const cid = routeCompanyId || await AsyncStorage.getItem('company_id') || await AsyncStorage.getItem('company_id');
      const key = `hr_permissions_${cid || 'default'}`;
      const cached = await AsyncStorage.getItem(key);
      if (cached) setPerms(prev => ({ ...prev, ...JSON.parse(cached) }));
    } catch {}
  };

  const checkAdminStatus = async () => {
    try {
      const userData = await AsyncStorage.getItem('user_data');
      if (userData) {
        const user = JSON.parse(userData);
        const role = (user.user_role || user.role || user.user_type || user.business_role || user.account_type || '').toLowerCase();
        const ADMIN_ROLES = ['admin', 'hr_manager', 'hr', 'owner', 'super_admin', 'director', 'ceo', 'cto', 'coo', 'manager', 'business', 'business_owner'];
        setIsAdminUser(ADMIN_ROLES.some(r => role.includes(r)) || user.is_admin === true || user.is_admin === 1);
      }
    } catch {}
    finally { setAuthChecked(true); }
  };

// ── Screen Password Functions (DB-backed  // ── Screen Password (employee_management) — DB: admin_passwords ──
  const SCREEN_MAX_ATTEMPTS = 5;
  const SCREEN_LOCKOUT_MINS = 15;
  const SCREEN_NAME = 'employee_management';

  const checkScreenPassword = async () => {
    try {
      const bizId = routeCompanyId || await AsyncStorage.getItem('company_id') || await AsyncStorage.getItem('company_id');
      if (!bizId) { setScreenPasswordVerified(true); setScreenPasswordChecked(true); return; }
      const headers = { 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' };
      // 1. Try screen-specific password
      let rec = null;
      try {
        const r = await fetch(`https://api.jobeng.ai/api/data/admin_passwords?company_id=${bizId}&screen_name=${SCREEN_NAME}`, { headers });
        if (r.ok) { const j = await r.json(); const recs = (j?.data || []).filter(x => x.screen_name === SCREEN_NAME); if (recs.length) rec = recs[0]; }
      } catch {}
      // 2. Fallback to global password
      if (!rec) {
        try {
          const r2 = await fetch(`https://api.jobeng.ai/api/data/admin_passwords?company_id=${bizId}&screen_name=global`, { headers });
          if (r2.ok) { const j2 = await r2.json(); const recs2 = (j2?.data || []).filter(x => x.screen_name === 'global'); if (recs2.length) rec = recs2[0]; }
        } catch {}
      }
      // 3. Also try legacy (no screen_name)
      if (!rec) {
        try {
          const r3 = await fetch(`https://api.jobeng.ai/api/data/admin_passwords?company_id=${bizId}`, { headers });
          if (r3.ok) { const j3 = await r3.json(); if ((j3?.data || []).length) rec = j3.data[0]; }
        } catch {}
      }
      if (rec) {
        setScreenHasPassword(true);
        if (rec.locked_until && new Date(rec.locked_until) > new Date()) setScreenLockoutUntil(new Date(rec.locked_until).getTime());
      } else { setScreenHasPassword(false); setScreenPasswordVerified(true); }
    } catch { setScreenPasswordVerified(true); }
    setScreenPasswordChecked(true);
  };

  const verifyScreenPassword = async () => {
    setScreenPasswordError('');
    if (screenLockoutUntil && Date.now() < screenLockoutUntil) {
      setScreenPasswordError(`Locked. Try again in ${Math.ceil((screenLockoutUntil - Date.now()) / 60000)} min.`);
      return;
    }
    if (!screenPassword || screenPassword.length < 4) { setScreenPasswordError('Min 4 characters'); return; }
    try {
      const bizId = routeCompanyId || await AsyncStorage.getItem('company_id') || await AsyncStorage.getItem('company_id');
      const tok = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef', ...(tok ? { Authorization: `Bearer ${tok}` } : {}) };
      // Find the right password record (screen-specific → global → legacy)
      let rec = null;
      for (const sn of [SCREEN_NAME, 'global', undefined]) {
        const url = sn ? `https://api.jobeng.ai/api/data/admin_passwords?company_id=${bizId}&screen_name=${sn}` : `https://api.jobeng.ai/api/data/admin_passwords?company_id=${bizId}`;
        try {
          const r = await fetch(url, { headers });
          if (r.ok) { const j = await r.json(); const recs = sn ? (j?.data || []).filter(x => x.screen_name === sn) : (j?.data || []); if (recs.length) { rec = recs[0]; break; } }
        } catch {}
      }
      if (!rec) { setScreenPasswordVerified(true); return; }
      if (rec.locked_until && new Date(rec.locked_until) > new Date()) {
        setScreenLockoutUntil(new Date(rec.locked_until).getTime());
        setScreenPasswordError('Account locked. Try again later.');
        return;
      }
      if (screenPassword === rec.password_hash) {
        setScreenPasswordVerified(true); setScreenPasswordAttempts(0); setScreenPassword('');
        try { await fetch(`https://api.jobeng.ai/api/data/admin_passwords/${rec.id}`, { method: 'PUT', headers, body: JSON.stringify({ attempts: 0, locked_until: null }) }); } catch {}
      } else {
        const na = (rec.attempts || 0) + 1;
        setScreenPasswordAttempts(na);
        let lu = null;
        if (na >= SCREEN_MAX_ATTEMPTS) { lu = new Date(Date.now() + SCREEN_LOCKOUT_MINS * 60 * 1000).toISOString(); setScreenLockoutUntil(Date.now() + SCREEN_LOCKOUT_MINS * 60 * 1000); setScreenPasswordError(`Locked for ${SCREEN_LOCKOUT_MINS} min.`); }
        else { setScreenPasswordError(`Incorrect. ${SCREEN_MAX_ATTEMPTS - na} attempt${SCREEN_MAX_ATTEMPTS - na > 1 ? 's' : ''} left.`); }
        try { await fetch(`https://api.jobeng.ai/api/data/admin_passwords/${rec.id}`, { method: 'PUT', headers, body: JSON.stringify({ attempts: na, locked_until: lu }) }); } catch {}
      }
    } catch { setScreenPasswordError('Verification failed.'); }
  };;

  useEffect(() => {
    loadPermissions();
    checkAdminStatus();
    checkScreenPassword();
    loadCompanyProfile();
    loadEmployeeData();
    
    // Setup auto-backup if enabled
    const backupInterval = setInterval(() => {
      if (employeeData.settings?.autoBackup) {
        autoBackupData();
      }
    }, 24 * 60 * 60 * 1000); // Daily backup

    return () => clearInterval(backupInterval);
  }, []);

  useEffect(() => {
    if (!loading) {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(slideAnim, {
          toValue: 1,
          duration: 600,
          useNativeDriver: true,
        })
      ]).start();
    }
  }, [loading]);

  // Data Management Functions
  const loadCompanyProfile = async () => {
    try {
      const cached = await AsyncStorage.getItem('company_profile');
      if (cached) {
        const cp = JSON.parse(cached);
        setCompanyName(cp.company_name || cp.name || '');
        const l = cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url || null;
        if (l && typeof l === 'string') {
          if (l.startsWith('http') || l.startsWith('data:')) setCompanyLogo(l);
          else if (l.startsWith('/')) setCompanyLogo('https://api.jobeng.ai' + l);
        }
      }
      const cid = await AsyncStorage.getItem('company_id');
      if (!cid) return;
      const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
      const res = await fetch('https://api.jobeng.ai/api/company-profile/' + (parseInt(cid) || 0),
        { headers: { 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef', ...(token ? { Authorization: 'Bearer ' + token } : {}) } });
      if (!res.ok) return;
      const r = await res.json();
      const cp = Array.isArray(r) ? r[0] : (r.data?.[0] ?? r.data ?? r);
      if (cp) {
        setCompanyName(cp.company_name || cp.name || '');
        const l = cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url || null;
        if (l && typeof l === 'string') {
          if (l.startsWith('http') || l.startsWith('data:')) setCompanyLogo(l);
          else if (l.startsWith('/')) setCompanyLogo('https://api.jobeng.ai' + l);
        }
        await AsyncStorage.setItem('company_profile', JSON.stringify(cp));
      }
    } catch (_) {}
  };

  const loadEmployeeData = async () => {
    if (!refreshing) setLoading(true);
    try {
      // ── 1. Fetch from API (ManageAPI with /api/data fallback) ──────────────
      let apiEmployees = null;
      let apiDepts     = null;
      let apiAnalytics = null;
      try {
        const cid = await AsyncStorage.getItem('company_id');
        const [empRes, deptRes, analyticsRes] = await Promise.allSettled([
          ManageAPI.list(cid ? { company_id: cid } : {}),
          ManageAPI.getDepartments(cid ? { company_id: cid } : {}),
          ManageAPI.getAnalytics(cid ? { company_id: cid } : {}),
        ]);
        if (empRes.status === 'fulfilled') {
          const d = empRes.value?.data?.data ?? empRes.value?.data ?? empRes.value ?? null;
          apiEmployees = Array.isArray(d) ? d : [];
          if (apiEmployees) await AsyncStorage.setItem('mgmt_emp_cache_' + (cid||''), JSON.stringify(apiEmployees));
        }
        if (deptRes.status === 'fulfilled') {
          const raw = deptRes.value;
          const d = Array.isArray(raw) ? raw : (raw?.data?.data ?? raw?.data ?? raw ?? null);
          apiDepts = Array.isArray(d) && d.length > 0 ? d : null;
          // If server has no departments table, derive departments from employees
          if (!apiDepts && apiEmployees) {
            const deptNames = [...new Set((apiEmployees||[]).map(e => e.department).filter(Boolean))];
            if (deptNames.length > 0) {
              apiDepts = deptNames.map((name, i) => ({ id: i+1, name, department: name }));
            }
          }
          if (apiDepts) await AsyncStorage.setItem('mgmt_dept_cache_' + (cid||''), JSON.stringify(apiDepts));
        }
        if (analyticsRes.status === 'fulfilled') {
          apiAnalytics = analyticsRes.value?.data?.data ?? analyticsRes.value?.data ?? null;
        }
      } catch (apiErr) {
        console.warn('[MgmtAPI] fetch failed:', apiErr.message);
      }

      // ── 1b. Fallback to /api/data/employees if ManageAPI returned nothing ──
      if (apiEmployees === null) {
        try {
          const cid = await AsyncStorage.getItem('company_id');
          const tok = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
          if (cid) {
            const headers = { 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef', ...(tok ? { Authorization: `Bearer ${tok}` } : {}) };
            const [empR, appR] = await Promise.allSettled([
              fetch(`https://api.jobeng.ai/api/data/employees?company_id=${parseInt(cid)||0}`, { headers }),
              fetch(`https://api.jobeng.ai/api/data/applications?company_id=${parseInt(cid)||0}`, { headers }),
            ]);
            for (const r of [empR, appR]) {
              if (r.status === 'fulfilled' && r.value?.ok) {
                const json = await r.value.json().catch(() => null);
                const rows = Array.isArray(json) ? json : (json?.data ?? []);
                if (rows.length > 0) { apiEmployees = rows; break; }
              }
            }
            if (apiEmployees) {
              await AsyncStorage.setItem('mgmt_emp_cache_' + (cid||''), JSON.stringify(apiEmployees));
              console.log(`[MgmtAPI] fallback loaded ${apiEmployees.length} employees from /api/data`);
            }
          }
        } catch (fbErr) {
          console.warn('[MgmtAPI] fallback also failed:', fbErr.message);
        }
      }

      // ── 2. If API gave us employees, build data object ─────────────────────
      if (Array.isArray(apiEmployees) && apiEmployees.length > 0) {
        // Normalize API response to internal shape
        const normalized = apiEmployees.map(e => {
          // Server returns snake_case (first_name, last_name, base_salary, hire_date)
          const firstName = e.first_name  || e.firstName  || (e.name||'').split(' ')[0] || '';
          const lastName  = e.last_name   || e.lastName   || (e.name||'').split(' ').slice(1).join(' ') || '';
          const fullName  = (firstName + ' ' + lastName).trim() || e.name || e.full_name || 'Employee';
          const salary    = parseFloat(e.base_salary || e.salary || e.baseSalary || 0);
          const position  = e.position   || e.job_title  || e.jobTitle || '';
          const dept      = e.department || '';
          const status    = e.status     || (e.is_active === false ? 'Inactive' : 'Active');
          const startDate = e.hire_date  || e.start_date || e.startDate || e.joinDate || '';
          const rawPhoto  = e.profile_image || e.avatar || null;
          // If server returns a relative path (/uploads/...), prepend the base URL
          const photoUrl  = rawPhoto
            ? (rawPhoto.startsWith('http') ? rawPhoto : `https://api.jobeng.ai${rawPhoto}`)
            : null;
          const avatar    = photoUrl ||
            `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}&background=0e4e96&color=fff&size=128`;
          return {
            ...e,                          // keep all raw server fields
            id:           String(e.id || `e_${Date.now()}`),
            serverId:     e.id ? String(e.id) : null,
            name:         fullName,
            firstName,    lastName,
            email:        e.email || e.contact?.email || '',
            phone:        e.phone || e.contact?.phone || '',
            department:   dept,
            position:     position,
            jobTitle:     position,
            status,
            startDate,
            salary,
            avatar,
            employeeNumber: e.employee_number || e.employee_id || e.employeeId || '',
            employeeCode: e.employee_code   || e.employeeCode || '',
            niNumber:     e.ni_number       || e.niNumber || '',
            taxCode:      e.tax_code        || e.taxCode  || '1257L',
            skills:       Array.isArray(e.skills) ? e.skills : [],
            createdAt:    e.created_at || e.createdAt || new Date().toISOString(),
            updatedAt:    e.updated_at || e.updatedAt || new Date().toISOString(),
          };
        });

        const calcResult = apiAnalytics || calculateAnalytics(normalized);
        const analytics = calcResult.analytics || calcResult;
        const overview = calcResult.overview || {
          totalEmployees: normalized.length,
          activeEmployees: normalized.filter(e => (e.status || '').toLowerCase() === 'active').length,
          onLeave: normalized.filter(e => (e.status || '').toLowerCase().includes('leave')).length,
          newHires: normalized.filter(e => e.startDate && new Date(e.startDate) > new Date(Date.now() - 30*24*60*60*1000)).length,
          pendingActions: 0, warningLetters: 0, promotions: 0, performanceReviews: 0,
        };
        const departments = Array.isArray(apiDepts) ? apiDepts : [];

        const data = {
          employees:   normalized,
          departments,
          analytics,
          overview,
          lastUpdated: new Date().toISOString(),
          version: '1.0.0',
        };

        // Load settings from storage
        const settingsRaw = await AsyncStorage.getItem(STORAGE_KEYS.SETTINGS);
        if (settingsRaw) data.settings = JSON.parse(settingsRaw);

        setEmployeeData(data);
        await AsyncStorage.setItem(STORAGE_KEYS.EMPLOYEE_DATA, JSON.stringify(data));
        return;
      }

      // ── 3. AsyncStorage fallback (only if API was unreachable) ─────────────
      if (Array.isArray(apiEmployees)) { setEmployeeData({ employees: [], departments: [], analytics: calculateAnalytics([]), version: '1.0.0' }); return; }
      const [storedData, backupData, settings] = await Promise.all([
        AsyncStorage.getItem(STORAGE_KEYS.EMPLOYEE_DATA),
        AsyncStorage.getItem(STORAGE_KEYS.BACKUP_DATA),
        AsyncStorage.getItem(STORAGE_KEYS.SETTINGS),
      ]);

      let data;
      if (storedData) {
        data = JSON.parse(storedData);
        data = migrateData(data);
        data = validateData(data);
        if (!data.analytics) data.analytics = calculateAnalytics(data.employees || []);
        setEmployeeData(data);
      } else {
        const mockData = generateMockEmployeeData();
        setEmployeeData(mockData);
        await AsyncStorage.setItem(STORAGE_KEYS.EMPLOYEE_DATA, JSON.stringify(mockData));
      }

      if (settings) {
        const parsedSettings = JSON.parse(settings);
        setEmployeeData(prev => ({ ...prev, settings: { ...prev.settings, ...parsedSettings } }));
      }
    } catch (error) {
      console.error('Error loading employee data:', error);
      Alert.alert('Error', 'Failed to load employee data. Would you like to restore from backup?', [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Restore', onPress: restoreFromBackup }
      ]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const migrateData = (data) => {
    // Add missing fields for backward compatibility
    if (!data.version) {
      data.version = '1.0.0';
      data.employees = (data.employees||[]).map(emp => ({
        ...emp,
        avatar: emp.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(emp.name)}&background=0e4e96&color=fff&size=128`,
        skills: Array.isArray(emp.skills) ? emp.skills : [emp.skills].filter(Boolean),
        createdAt: emp.createdAt || new Date().toISOString(),
        updatedAt: emp.updatedAt || new Date().toISOString()
      }));
    }
    return data;
  };

  const validateData = (data) => {
    // Validate and clean data
    data.employees = (data.employees||[]).filter(emp => 
      emp && emp.name && emp.email && emp.department
    );
    return data;
  };

  const saveEmployeeData = async (updatedData) => {
    try {
      const dataWithTimestamp = {
        ...updatedData,
        lastUpdated: new Date().toISOString(),
        version: '1.0.0'
      };
      setEmployeeData(dataWithTimestamp);
      // Use safe serialiser to avoid circular ref errors from Animated values
      const seen2 = new WeakSet();
      const serialised = JSON.stringify(dataWithTimestamp, (k, v) => {
        if (typeof v === 'function') return undefined;
        if (v && typeof v === 'object') {
          if (v._animatedNode !== undefined) return undefined;
          if (seen2.has(v)) return undefined;
          seen2.add(v);
        }
        return v;
      });
      await AsyncStorage.setItem(STORAGE_KEYS.EMPLOYEE_DATA, serialised);
      
      // Auto-backup if enabled
      if (dataWithTimestamp.settings?.autoBackup) {
        await createBackup(dataWithTimestamp);
      }
      
      return true;
    } catch (error) {
      console.error('Error saving employee data:', error);
      Alert.alert('Error', 'Failed to save employee data');
      return false;
    }
  };

  // Safe JSON serialiser — strips functions, Animated values, circular refs
  const safeStringify = (obj) => {
    const seen = new WeakSet();
    return JSON.stringify(obj, (key, value) => {
      if (typeof value === 'function') return undefined;
      if (value && typeof value === 'object') {
        // Strip Animated values (they have internal circular refs)
        if (value._animatedNode !== undefined || value.__isAnimatedValue) return undefined;
        if (seen.has(value)) return '[Circular]';
        seen.add(value);
      }
      return value;
    });
  };

  const createBackup = async (data = employeeData) => {
    try {
      // Only back up the serialisable parts — exclude analytics functions etc.
      const safeData = {
        employees:          data.employees || [],
        warningLetters:     data.warningLetters || [],
        promotions:         data.promotions || [],
        performanceReviews: data.performanceReviews || [],
        employeeCodes:      data.employeeCodes || [],
        departments:        data.departments || [],
        overview:           data.overview || {},
        settings:           data.settings || {},
        version:            data.version || '1.0.0',
        backupCreated:      new Date().toISOString(),
        backupId:           `backup_${Date.now()}`,
      };
      await AsyncStorage.setItem(STORAGE_KEYS.BACKUP_DATA, safeStringify(safeData));
      setEmployeeData(prev => ({ ...prev, lastBackup: new Date().toISOString() }));
      return true;
    } catch (error) {
      console.error('Error creating backup:', error);
      return false;
    }
  };

  const restoreFromBackup = async () => {
    try {
      const backup = await AsyncStorage.getItem(STORAGE_KEYS.BACKUP_DATA);
      if (backup) {
        const backupData = JSON.parse(backup);
        setEmployeeData(backupData);
        await AsyncStorage.setItem(STORAGE_KEYS.EMPLOYEE_DATA, JSON.stringify(backupData));
        Alert.alert('Success', 'Data restored from backup');
      } else {
        Alert.alert('No Backup', 'No backup data found');
      }
    } catch (error) {
      console.error('Error restoring from backup:', error);
      Alert.alert('Error', 'Failed to restore from backup');
    }
  };

  const autoBackupData = async () => {
    const success = await createBackup();
    if (success) {
      console.log('Auto-backup completed successfully');
    }
  };

  // Enhanced Data Generation with Realistic Data
  const generateMockEmployeeData = () => {
    const departments = ['Engineering', 'Marketing', 'Sales', 'HR', 'Finance', 'Operations', 'IT'];
    const positions = {
      'Engineering': ['Software Engineer', 'Senior Developer', 'Tech Lead', 'Architect', 'QA Engineer'],
      'Marketing': ['Marketing Manager', 'Content Specialist', 'SEO Analyst', 'Social Media Manager'],
      'Sales': ['Sales Executive', 'Account Manager', 'Sales Director', 'Business Development'],
      'HR': ['HR Manager', 'Recruiter', 'HR Specialist', 'Talent Acquisition'],
      'Finance': ['Financial Analyst', 'Accountant', 'Finance Manager', 'Controller'],
      'Operations': ['Operations Manager', 'Project Coordinator', 'Logistics Specialist'],
      'IT': ['System Administrator', 'Network Engineer', 'IT Support', 'Security Analyst']
    };
    
    const firstNames = ['James', 'Mary', 'John', 'Patricia', 'Robert', 'Jennifer', 'Michael', 'Linda', 'William', 'Elizabeth'];
    const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez'];
    
    const locations = ['New York', 'London', 'Tokyo', 'Berlin', 'Sydney', 'Toronto', 'Paris'];
    const skillsPool = ['JavaScript', 'React', 'Node.js', 'Python', 'Java', 'SQL', 'AWS', 'Docker', 'Kubernetes', 'TypeScript'];

    const employees = Array.from({ length: 50 }, (_, index) => {
      const department = departments[Math.floor(Math.random() * departments.length)];
      const position = positions[department][Math.floor(Math.random() * positions[department].length)];
      const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
      const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
      const name = `${firstName} ${lastName}`;
      
      return {
        id: `emp_${Date.now()}_${index}`,
        serverId: null,
        employeeNumber: `EMP${String(index + 1).padStart(4, '0')}`,
        name,
        email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@company.com`,
        phone: `+1-555-${Math.floor(100 + Math.random() * 900)}-${Math.floor(1000 + Math.random() * 9000)}`,
        department,
        position,
        employeeCode: ['FULL_TIME', 'PART_TIME', 'CONTRACT'][Math.floor(Math.random() * 3)],
        salary: Math.floor(Math.random() * 100000) + 50000,
        startDate: new Date(2020 + Math.floor(Math.random() * 4), Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString().split('T')[0],
        manager: `Manager ${Math.floor(Math.random() * 10) + 1}`,
        location: locations[Math.floor(Math.random() * locations.length)],
        status: Object.values(EMPLOYEE_STATUS)[Math.floor(Math.random() * Object.values(EMPLOYEE_STATUS).length)],
        emergencyContact: `Emergency Contact ${index + 1}`,
        emergencyPhone: `+1-555-${Math.floor(100 + Math.random() * 900)}-${Math.floor(1000 + Math.random() * 9000)}`,
        address: `${Math.floor(Math.random() * 9999) + 1} Main St, City, State`,
        dateOfBirth: new Date(1980 + Math.floor(Math.random() * 20), Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString().split('T')[0],
        nationalId: `ID${Math.floor(Math.random() * 1000000000)}`,
        bankAccount: `****${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`,
        skills: skillsPool.sort(() => 0.5 - Math.random()).slice(0, 3),
        notes: Math.random() > 0.7 ? 'Excellent performer with great team skills' : '',
        warningCount: Math.floor(Math.random() * 3),
        promotionCount: Math.floor(Math.random() * 2),
        lastReviewDate: new Date(2024, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString().split('T')[0],
        performanceRating: Math.floor(Math.random() * 5) + 1,
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=0e4e96&color=fff&size=128`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    });

    // Calculate analytics
    const departmentDistribution = employees.reduce((acc, emp) => {
      acc[emp.department] = (acc[emp.department] || 0) + 1;
      return acc;
    }, {});

    const statusDistribution = employees.reduce((acc, emp) => {
      acc[emp.status] = (acc[emp.status] || 0) + 1;
      return acc;
    }, {});

    const overview = {
      totalEmployees: employees.length,
      activeEmployees: employees.filter(emp => emp.status === EMPLOYEE_STATUS.ACTIVE).length,
      onLeave: employees.filter(emp => emp.status === EMPLOYEE_STATUS.ON_LEAVE).length,
      newHires: employees.filter(emp => new Date(emp.startDate) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)).length,
      pendingActions: 12,
      warningLetters: 8,
      promotions: 5,
      performanceReviews: 15,
      turnoverRate: 2.5,
      avgTenure: 2.8
    };

    return {
      overview,
      employees,
      warningLetters: [],
      promotions: [],
      performanceReviews: [],
      employeeCodes: [
        {
          id: 'code_1',
          code: 'FULL_TIME',
          name: 'Full-Time Employee',
          description: 'Regular full-time employee with full benefits',
          category: 'Employment Type',
          color: '#10b981',
          isActive: true,
          benefits: 'Health insurance, retirement plan, paid time off',
          requirements: '40 hours per week minimum',
          createdAt: new Date().toISOString(),
        },
        {
          id: 'code_2',
          code: 'PART_TIME',
          name: 'Part-Time Employee',
          description: 'Part-time employee with limited benefits',
          category: 'Employment Type',
          color: '#f59e0b',
          isActive: true,
          benefits: 'Prorated benefits based on hours worked',
          requirements: 'Less than 30 hours per week',
          createdAt: new Date().toISOString(),
        }
      ],
      departments: departments,
      analytics: {
        departmentDistribution,
        statusDistribution,
        tenureDistribution: {},
        salaryDistribution: {}
      },
      settings: {
        autoBackup: true,
        backupFrequency: 'daily',
        exportFormat: 'csv',
        notifications: true
      },
      lastBackup: null,
      version: '1.0.0'
    };
  };

  // Enhanced Form Validation
  const validateEmployeeForm = (employee) => {
    const errors = {};
    
    if (!employee.name.trim()) errors.name = 'Name is required';
    if (!employee.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(employee.email)) {
      errors.email = 'Email is invalid';
    }
    if (!employee.department) errors.department = 'Department is required';
    if (!employee.position.trim()) errors.position = 'Position is required';
    if (employee.salary && isNaN(employee.salary)) errors.salary = 'Salary must be a number';
    if (employee.phone && !/^\+?[\d\s-()]+$/.test(employee.phone)) errors.phone = 'Phone number is invalid';
    
    return errors;
  };

  const validateEmail = async (email) => {
    // Check if email already exists
    const existingEmployee = (employeeData.employees||[]).find(emp => 
      String(emp.email ?? '').toLowerCase() === email.toLowerCase() && emp.id !== newEmployee.id
    );
    return !existingEmployee;
  };

  // Enhanced CRUD Operations

  // ─── Payroll Integration Functions ────────────────────────────────────────
  const syncEmployeeToPayroll = async (employee) => {
    try {
      // Write to payroll_employee_sync
      const syncRaw = await AsyncStorage.getItem('payroll_employee_sync');
      const syncData = syncRaw ? JSON.parse(syncRaw) : {};
      const key = employee.serverId || employee.id || employee.email;
      syncData[key] = {
        ...syncData[key],
        employeeId: employee.serverId || employee.id,
        employeeNumber: employee.employeeNumber || employee.employeeId,
        name: employee.name,
        email: employee.email,
        department: employee.department,
        position: employee.position,
        salary: parseInt(employee.salary) || 0,
        status: employee.status,
        payFrequency: 'monthly',
        paymentDate: 28,
        taxCode: syncData[key]?.taxCode || '1257L',
        niCategory: syncData[key]?.niCategory || 'A',
        pensionEnrolled: true,
        updatedAt: new Date().toISOString(),
        source: 'employee_management',
      };
      await AsyncStorage.setItem('payroll_employee_sync', JSON.stringify(syncData));

      // Also sync to hired_employees
      const hiredRaw = await AsyncStorage.getItem('hired_employees');
      const hired = hiredRaw ? JSON.parse(hiredRaw) : [];
      const hIdx = hired.findIndex(h => h.id === employee.id || h.email === employee.email);
      if (hIdx !== -1) {
        hired[hIdx] = { ...hired[hIdx], ...employee };
      } else {
        hired.push(employee);
      }
      await AsyncStorage.setItem('hired_employees', JSON.stringify(hired));

    } catch (err) {
      console.error('Error syncing employee to payroll:', err);
    }
  };

  const syncAllEmployeesToPayroll = async () => {
    try {
      for (const emp of employeeData.employees) {
        await syncEmployeeToPayroll(emp);
      }
      Alert.alert('Sync Complete', `${(employeeData.employees||[]).length} employees synced to payroll successfully`);
    } catch (err) {
      Alert.alert('Sync Error', 'Failed to sync some employees to payroll');
    }
  };

  const navigateToPayroll = () => {
    navigation?.navigate('PayrollManagementScreen');
  };

  const navigateToEmployeeDetails = (employee) => {
    navigation?.navigate('EmployeeDetailsScreen', {
      employee: {
        ...employee,
        baseSalary: employee.salary,
        totalPackage: employee.salary,
      },
      employeeId: employee.id,
    });
  };

  const getPayrollStatus = (employee) => {
    // Check if employee is synced to payroll
    return employee.salary > 0 ? 'synced' : 'pending';
  };

  const calculateTotalPayrollCost = () => {
    const active = (employeeData.employees||[]).filter(e => e.status === 'Active');
    const monthly = active.reduce((sum, e) => sum + (parseInt(e.salary) || 0) / 12, 0);
    const annual = active.reduce((sum, e) => sum + (parseInt(e.salary) || 0), 0);
    return { monthly, annual, count: active.length };
  };

  const addEmployee = async () => {
    if (!can('add_employee')) { Alert.alert('Restricted', 'Adding employees is restricted by admin.'); return; }
    const errors = validateEmployeeForm(newEmployee);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      Alert.alert('Validation Error', 'Please fix the form errors before submitting');
      return;
    }

    const isEmailUnique = await validateEmail(newEmployee.email);
    if (!isEmailUnique) {
      Alert.alert('Error', 'An employee with this email already exists');
      return;
    }

    setIsSubmitting(true);

    try {
      const employee = {
        ...newEmployee,
        id: `emp_${Date.now()}`,
        serverId: null,
        employeeNumber: newEmployee.employeeNumber || newEmployee.employeeId || `EMP${String((employeeData.employees||[]).length + 1).padStart(4, '0')}`,
        salary: parseInt(newEmployee.salary) || 0,
        warningCount: 0,
        promotionCount: 0,
        lastReviewDate: '',
        performanceRating: 3,
        skills: Array.isArray(newEmployee.skills) ? newEmployee.skills : newEmployee.skills.split(',').map(s => s.trim()).filter(Boolean),
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(newEmployee.name)}&background=0e4e96&color=fff&size=128`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const updatedData = {
        ...employeeData,
        employees: [...employeeData.employees, employee],
        overview: {
          ...employeeData.overview,
          totalEmployees: ((employeeData.overview || {}).totalEmployees || 0) + 1,
          activeEmployees: employee.status === EMPLOYEE_STATUS.ACTIVE ? ((employeeData.overview || {}).activeEmployees || 0) + 1 : ((employeeData.overview || {}).activeEmployees || 0),
          newHires: ((employeeData.overview || {}).newHires || 0) + 1
        }
      };

      // Update analytics
      updatedData.analytics = calculateAnalytics(updatedData.employees);

      const success = await saveEmployeeData(updatedData);
      if (success) {
        // ── POST to API — map to server snake_case schema ────────────────
        try {
          const nameParts = (employee.name || '').trim().split(' ');
          const serverPayload = {
            first_name:      nameParts[0] || employee.name || '',
            last_name:       nameParts.slice(1).join(' ') || '.',
            email:           employee.email || `emp.${Date.now()}@placeholder.jobeng.ai`,
            phone:           employee.phone || null,
            department:      employee.department || null,
            position:        employee.position || null,
            hire_date:       employee.startDate || employee.hireDate || null,
            status:          (employee.status || 'Active').toLowerCase(),
            employment_type: employee.employeeCode || 'full_time',
            salary:          employee.salary ? parseFloat(employee.salary) : null,
            notes:           employee.notes || null,
            profile_image:   employee.avatar || null,
            company_id:      companyId ? parseInt(companyId) : null,
            manager:         employee.manager || null,
            national_id:     employee.nationalId || null,
            bank_account:    employee.bankAccount || null,
            address:         employee.address || null,
          };
          const res = await ManageAPI.create(serverPayload);
          if (res?.error) {
            console.warn('[MgmtAPI] POST employee failed (saved locally):', res.error?.message);
          } else {
            const saved = res?.data?.data ?? res?.data;
            const serverId = saved?.id ? String(saved.id) : null;
            if (serverId && serverId !== String(employee.id)) {
              const withServerId = (updatedData.employees||[]).map(e =>
                e.id === employee.id ? { ...e, id: serverId, serverId: serverId } : e
              );
              await saveEmployeeData({ ...updatedData, employees: withServerId });
            }
            const empId = serverId ?? employee.id;
            PayrollIntegrationAPI.syncEmployee(empId).catch(() => {});
          }
        } catch (apiErr) {
          console.warn('[MgmtAPI] POST employee failed (saved locally):', apiErr.message);
        }
        resetNewEmployee();
        setFormErrors({});
        setShowAddEmployeeModal(false);
        Alert.alert('Success', 'Employee added successfully', [
          { text: 'OK', onPress: () => setActiveTab('employees') }
        ]);
      }
    } catch (error) {
      console.error('Error adding employee:', error);
      Alert.alert('Error', 'Failed to add employee');
    } finally {
      setIsSubmitting(false);
    }
  };

  const editEmployee = async () => {
    if (!selectedEmployee) return;

    const errors = validateEmployeeForm(selectedEmployee);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      Alert.alert('Validation Error', 'Please fix the form errors before submitting');
      return;
    }

    setIsSubmitting(true);

    try {
      const updatedEmployees = (employeeData.employees||[]).map(emp =>
        emp.id === selectedEmployee.id ? {
          ...selectedEmployee,
          salary: parseInt(selectedEmployee.salary) || emp.salary,
          skills: Array.isArray(selectedEmployee.skills) ? selectedEmployee.skills : selectedEmployee.skills.split(',').map(s => s.trim()).filter(Boolean),
          updatedAt: new Date().toISOString()
        } : emp
      );

      const wasActive = (employeeData.employees||[]).find(emp => emp.id === selectedEmployee.id)?.status === EMPLOYEE_STATUS.ACTIVE;
      const isActive = selectedEmployee.status === EMPLOYEE_STATUS.ACTIVE;

      const updatedData = {
        ...employeeData,
        employees: updatedEmployees,
        overview: {
          ...employeeData.overview,
          activeEmployees: wasActive && !isActive ? ((employeeData.overview || {}).activeEmployees || 0) - 1 :
                         !wasActive && isActive ? ((employeeData.overview || {}).activeEmployees || 0) + 1 :
                         ((employeeData.overview || {}).activeEmployees || 0)
        }
      };

      // Update analytics
      updatedData.analytics = calculateAnalytics(updatedData.employees);

      const success = await saveEmployeeData(updatedData);
      if (success) {
        // ── PUT to API ───────────────────────────────────────────────────────
        const empId = selectedEmployee.serverId || selectedEmployee.id;
        try {
          const editPayload = {
            first_name:      (selectedEmployee.name || '').split(' ')[0] || selectedEmployee.firstName,
            last_name:       (selectedEmployee.name || '').split(' ').slice(1).join(' ') || selectedEmployee.lastName || '.',
            email:           selectedEmployee.email,
            phone:           selectedEmployee.phone || null,
            department:      selectedEmployee.department || null,
            position:        selectedEmployee.position || null,
            hire_date:       selectedEmployee.startDate || selectedEmployee.hireDate || null,
            status:          (selectedEmployee.status || 'Active').toLowerCase(),
            employment_type: selectedEmployee.employeeCode || 'full_time',
            salary:          selectedEmployee.salary ? parseFloat(selectedEmployee.salary) : null,
            notes:           selectedEmployee.notes || null,
            profile_image:   selectedEmployee.avatar || null,
            manager:         selectedEmployee.manager || null,
            national_id:     selectedEmployee.nationalId || null,
            address:         selectedEmployee.address || null,
          };
          await ManageAPI.update(empId, editPayload);
          // Sync salary/role changes to payroll
          if (selectedEmployee.salary || selectedEmployee.jobTitle) {
            PayrollIntegrationAPI.syncEmployee(empId).catch(() => {});
          }
        } catch (apiErr) {
          console.warn('[MgmtAPI] PUT employee failed (saved locally):', apiErr.message);
        }
        setShowEditEmployeeModal(false);
        setSelectedEmployee(null);
        setFormErrors({});
        Alert.alert('Success', 'Employee updated successfully');
      }
    } catch (error) {
      console.error('Error updating employee:', error);
      Alert.alert('Error', 'Failed to update employee');
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteEmployee = (employeeId) => {
    if (!can('delete_employee')) { Alert.alert('Restricted', 'Deleting employees is restricted by admin.'); return; }
    Alert.alert(
      'Delete Employee',
      'Are you sure you want to delete this employee? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const updatedEmployees = (employeeData.employees||[]).filter(emp => emp.id !== employeeId);
              const deletedEmployee = (employeeData.employees||[]).find(emp => emp.id === employeeId);
              
              const overview = employeeData.overview || {};
              const updatedData = {
                ...employeeData,
                employees: updatedEmployees,
                overview: {
                  ...overview,
                  totalEmployees: Math.max(0, (overview.totalEmployees || updatedEmployees.length + 1) - 1),
                  activeEmployees: deletedEmployee?.status === EMPLOYEE_STATUS.ACTIVE
                    ? Math.max(0, (overview.activeEmployees || 0) - 1)
                    : (overview.activeEmployees || 0),
                }
              };

              // Update analytics
              updatedData.analytics = calculateAnalytics(updatedData.employees);

              await saveEmployeeData(updatedData);
              // ── DELETE on API ─────────────────────────────────────────────
              // Use server ID if available (local IDs like 'emp_123' won't work on server)
              const emp = (employeeData.employees||[]).find(e => e.id === employeeId);
              const serverIdToDelete = emp?.serverId || emp?.server_id || employeeId;
              if (String(serverIdToDelete).startsWith('emp_')) {
                console.log('[ManageAPI] Skipping DELETE — no server ID assigned yet for:', employeeId);
              } else {
                ManageAPI.delete(serverIdToDelete)
                  .catch(err => console.warn('[ManageAPI] DELETE employee failed:', err.message));
              }
              Alert.alert('Success', 'Employee deleted successfully');
            } catch (error) {
              console.error('Error deleting employee:', error);
              Alert.alert('Error', 'Failed to delete employee');
            }
          }
        }
      ]
    );
  };

  const bulkDeleteEmployees = async () => {
    if (!can('bulk_actions')) { Alert.alert('Restricted', 'Bulk actions are restricted by admin.'); return; }
    if (selectedEmployees.length === 0) return;

    Alert.alert(
      'Delete Employees',
      `Are you sure you want to delete ${selectedEmployees.length} employees? This action cannot be undone.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const updatedEmployees = (employeeData.employees||[]).filter(emp => !selectedEmployees.includes(emp.id));
              
              const updatedData = {
                ...employeeData,
                employees: updatedEmployees,
                overview: {
                  ...employeeData.overview,
                  totalEmployees: ((employeeData.overview || {}).totalEmployees || 0) - selectedEmployees.length,
                  // Note: Would need to recalculate active employees based on deleted employees' status
                }
              };

              // Update analytics
              updatedData.analytics = calculateAnalytics(updatedData.employees);

              await saveEmployeeData(updatedData);
              setSelectedEmployees([]);
              Alert.alert('Success', `${selectedEmployees.length} employees deleted successfully`);
            } catch (error) {
              console.error('Error deleting employees:', error);
              Alert.alert('Error', 'Failed to delete employees');
            }
          }
        }
      ]
    );
  };

  // Payroll Cost Analytics  
  const renderPayrollCostBanner = () => {
    const cost = calculateTotalPayrollCost();
    return (
      <View style={{
        backgroundColor: '#0e4e96',
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <View>
          <Text style={{ color: '#94a3b8', fontSize: 12, marginBottom: 2 }}>Monthly Payroll Cost</Text>
          <Text style={{ color: '#ffffff', fontSize: 22, fontWeight: '800' }}>
            £{Math.round(cost.monthly).toLocaleString()}
          </Text>
          <Text style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>
            {cost.count} active employees · Annual: £{Math.round(cost.annual).toLocaleString()}
          </Text>
        </View>
        <TouchableOpacity
          onPress={navigateToPayroll}
          style={{
            backgroundColor: 'rgba(255,255,255,0.15)',
            borderRadius: 8,
            paddingVertical: 10,
            paddingHorizontal: 14,
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <MaterialCommunityIcons name="calculator" size={18} color="#ffffff" />
          <Text style={{ color: '#ffffff', fontSize: 13, fontWeight: '600', marginLeft: 6 }}>Payroll</Text>
        </TouchableOpacity>
      </View>
    );
  };

  // Analytics Calculation
  const calculateAnalytics = (employees = []) => {
    const departmentDistribution = employees.reduce((acc, emp) => {
      if (emp && emp.department) {
        acc[emp.department] = (acc[emp.department] || 0) + 1;
      }
      return acc;
    }, {});

    const statusDistribution = employees.reduce((acc, emp) => {
      if (emp && emp.status) {
        acc[emp.status] = (acc[emp.status] || 0) + 1;
      }
      return acc;
    }, {});

    // Calculate tenure distribution
    const now = new Date();
    const tenureDistribution = {
      '0-1 years': 0,
      '1-3 years': 0,
      '3-5 years': 0,
      '5+ years': 0
    };

    employees.forEach(emp => {
      if (emp && emp.startDate) {
        try {
          const startDate = new Date(emp.startDate);
          const tenureYears = (now - startDate) / (365 * 24 * 60 * 60 * 1000);
          
          if (tenureYears < 1) tenureDistribution['0-1 years']++;
          else if (tenureYears < 3) tenureDistribution['1-3 years']++;
          else if (tenureYears < 5) tenureDistribution['3-5 years']++;
          else tenureDistribution['5+ years']++;
        } catch (error) {
          console.error('Error calculating tenure:', error);
        }
      }
    });

    // Calculate salary distribution
    const salaries = employees.map(emp => emp.salary).filter(Boolean);
    const avgSalary = salaries.length ? salaries.reduce((a, b) => a + b, 0) / salaries.length : 0;

    return {
      departmentDistribution,
      statusDistribution,
      tenureDistribution,
      salaryDistribution: {
        average: avgSalary,
        min: salaries.length ? Math.min(...salaries) : 0,
        max: salaries.length ? Math.max(...salaries) : 0
      }
    };
  };

  // Enhanced Filtering and Sorting
  const getFilteredEmployees = useMemo(() => {
    const q = (searchQuery || '').toLowerCase();
    const safe = (v) => (v == null ? '' : String(v)).toLowerCase();

    let filtered = (employeeData.employees || []).filter(emp => {
      if (!emp) return false;

      const matchesSearch = !q || (
        safe(emp.name).includes(q) ||
        safe(emp.employeeId).includes(q) ||
        safe(emp.department).includes(q) ||
        safe(emp.position).includes(q) ||
        safe(emp.email).includes(q) ||
        (Array.isArray(emp.skills) && emp.skills.some(s => safe(s).includes(q)))
      );

      const matchesDepartment = filterDepartment === 'All' || emp.department === filterDepartment;
      const matchesStatus     = filterStatus     === 'All' || emp.status     === filterStatus;
      const matchesCode       = filterEmployeeCode === 'All' || emp.employeeCode === filterEmployeeCode;

      return matchesSearch && matchesDepartment && matchesStatus && matchesCode;
    });

    // Sort — null-safe comparisons
    filtered.sort((a, b) => {
      let aV = a[sortBy] ?? '';
      let bV = b[sortBy] ?? '';
      if (sortBy === 'salary')    { aV = parseInt(aV) || 0; bV = parseInt(bV) || 0; }
      if (sortBy === 'startDate') { aV = new Date(aV || 0); bV = new Date(bV || 0); }
      if (aV < bV) return sortOrder === 'asc' ? -1 : 1;
      if (aV > bV) return sortOrder === 'asc' ?  1 : -1;
      return 0;
    });

    return filtered;
  }, [employeeData.employees, searchQuery, filterDepartment, filterStatus, filterEmployeeCode, sortBy, sortOrder]);

  const getEmployeesByDepartment = () => {
    const filtered = getFilteredEmployees;
    const grouped = {};
    
    filtered.forEach(employee => {
      if (!grouped[employee.department]) {
        grouped[employee.department] = [];
      }
      grouped[employee.department].push(employee);
    });
    
    return Object.entries(grouped).map(([department, employees]) => ({
      title: department,
      data: employees
    }));
  };

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortOrder('asc');
    }
  };

  // Export and Import Functions
  const exportEmployeeData = async (format = 'csv') => {
    if (!can('export_reports')) { Alert.alert('Restricted', 'Exporting data is restricted by admin.'); return; }
    try {
      const data = getFilteredEmployees;
      
      if (format === 'csv') {
        const csvHeaders = ['Employee ID', 'Name', 'Email', 'Department', 'Position', 'Status', 'Salary', 'Start Date', 'Phone', 'Location'];
        const csvRows = data.map(emp => [
          emp.employeeId,
          `"${emp.name}"`,
          emp.email,
          emp.department,
          emp.position,
          emp.status,
          emp.salary,
          emp.startDate,
          emp.phone,
          emp.location
        ]);
        
        const csvContent = [csvHeaders, ...csvRows].map(row => row.join(',')).join('\n');
        
        // Save file locally
        const fileUri = FileSystem.documentDirectory + `employees_export_${new Date().getTime()}.csv`;
        await FileSystem.writeAsStringAsync(fileUri, csvContent);
        
        // Share file
        if (await Sharing.isAvailableAsync()) {
          await Sharing.shareAsync(fileUri, {
            mimeType: 'text/csv',
            dialogTitle: 'Export Employees Data'
          });
        } else {
          Alert.alert('Export Ready', `CSV file saved: ${fileUri}`);
        }
      } else if (format === 'json') {
        const jsonData = JSON.stringify(data, null, 2);
        const fileUri = FileSystem.documentDirectory + `employees_export_${new Date().getTime()}.json`;
        await FileSystem.writeAsStringAsync(fileUri, jsonData);
        
        if (await Sharing.isAvailableAsync()) {
          await Sharing.shareAsync(fileUri);
        } else {
          Alert.alert('Export Ready', `JSON file saved: ${fileUri}`);
        }
      }
      
      Alert.alert('Success', `${data.length} employees exported successfully`);
    } catch (error) {
      console.error('Error exporting data:', error);
      Alert.alert('Error', 'Failed to export employee data');
    }
  };

  const importEmployeeData = async () => {
    // Show import options - CSV paste or sample data seed
    Alert.alert(
      'Import Employees',
      'Choose import method:',
      [
        {
          text: 'Paste CSV Data',
          onPress: () => {
            setShowImportModal(true);
          }
        },
        {
          text: 'Sync from Payroll',
          onPress: async () => {
            try {
              // Pull employees from payroll_employee_sync
              const syncRaw = await AsyncStorage.getItem('payroll_employee_sync');
              const syncData = syncRaw ? JSON.parse(syncRaw) : {};
              const imported = Object.values(syncData).filter(e => e.name || e.employeeName);
              if (imported.length === 0) {
                Alert.alert('No Data', 'No payroll sync data found. Process a payroll first to sync employees.');
                return;
              }
              const normalized = imported.map((e, i) => ({
                id: e.employeeId || e.id || `sync_${Date.now()}_${i}`,
                employeeId: e.employeeNumber || e.employeeId || `EMP${String(i + 1).padStart(4, '0')}`,
                name: e.name || e.employeeName || 'Unknown',
                email: e.email || '',
                phone: e.phone || '',
                department: e.department || 'General',
                position: e.position || e.jobTitle || '',
                salary: parseInt(e.salary) || 0,
                status: e.status || 'Active',
                startDate: e.startDate || new Date().toISOString().split('T')[0],
                employeeCode: e.employmentType || 'FULL_TIME',
                location: e.location || 'Office',
                manager: e.manager || '',
                performanceRating: 3,
                attendanceRate: 95,
                leaveBalance: 25,
                warningCount: 0,
                skills: [],
                avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(e.name || 'E')}&background=0e4e96&color=fff&size=128`,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                source: 'payroll_sync',
              }));
              // Merge with existing, skip duplicates by email
              const existing = employeeData.employees || [];
              const existingEmails = new Set(existing.map(e => e.email?.toLowerCase()).filter(Boolean));
              const newOnes = normalized.filter(e => !existingEmails.has(e.email?.toLowerCase()));
              const merged = [...existing, ...newOnes];
              const updatedData = {
                ...employeeData,
                employees: merged,
                overview: {
                  ...employeeData.overview,
                  totalEmployees: merged.length,
                  activeEmployees: merged.filter(e => e.status === 'Active').length,
                },
              };
              updatedData.analytics = calculateAnalytics(merged);
              await saveEmployeeData(updatedData);
              Alert.alert('✅ Synced', `${newOnes.length} employees imported from payroll. ${imported.length - newOnes.length} duplicates skipped.`);
            } catch (err) {
              Alert.alert('Error', 'Failed to sync from payroll: ' + err.message);
            }
          }
        },
        {
          text: 'Seed Sample Data',
          onPress: async () => {
            const mockData = generateMockEmployeeData();
            const merged = [...(employeeData.employees || []), ...mockData.employees];
            const updatedData = { ...employeeData, employees: merged };
            updatedData.analytics = calculateAnalytics(merged);
            updatedData.overview = { ...employeeData.overview, totalEmployees: merged.length };
            await saveEmployeeData(updatedData);
            Alert.alert('✅ Seeded', `${mockData.employees.length} sample employees added.`);
          }
        },
        { text: 'Cancel', style: 'cancel' }
      ]
    );
  };

  // Bulk Operations
  const bulkUpdateEmployees = async (updates) => {
    try {
      const updatedEmployees = (employeeData.employees||[]).map(emp =>
        selectedEmployees.includes(emp.id) ? { 
          ...emp, 
          ...updates, 
          updatedAt: new Date().toISOString() 
        } : emp
      );

      const updatedData = {
        ...employeeData,
        employees: updatedEmployees
      };

      // Update analytics
      updatedData.analytics = calculateAnalytics(updatedData.employees);

      const success = await saveEmployeeData(updatedData);
      if (success) {
        setSelectedEmployees([]);
        setShowBulkActionsModal(false);
        Alert.alert('Success', `${selectedEmployees.length} employees updated successfully`);
      }
    } catch (error) {
      console.error('Error in bulk update:', error);
      Alert.alert('Error', 'Failed to update employees');
    }
  };

  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId) 
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const selectAllEmployees = () => {
    const filteredEmployees = getFilteredEmployees;
    setSelectedEmployees(filteredEmployees.map(emp => emp.id));
  };

  const clearSelection = () => {
    setSelectedEmployees([]);
  };

  // Reset Functions
  const resetNewEmployee = () => {
    setNewEmployee({
      id: '',
      employeeId: '',
      name: '',
      email: '',
      phone: '',
      department: 'Engineering',
      position: '',
      employeeCode: 'FULL_TIME',
      salary: '',
      startDate: new Date().toISOString().split('T')[0],
      manager: '',
      location: 'Office',
      status: EMPLOYEE_STATUS.ACTIVE,
      emergencyContact: '',
      emergencyPhone: '',
      address: '',
      dateOfBirth: '',
      nationalId: '',
      bankAccount: '',
      skills: [],
      notes: '',
      avatar: `https://ui-avatars.com/api/?name=Employee&background=0e4e96&color=fff&size=128`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
  };

  // Enhanced UI Components with Animations
  const renderStatCard = (title, value, icon, color, subtitle = null, onPress = null) => (
    <Animated.View 
      style={[
        styles.statCard, 
        { borderLeftColor: color },
        {
          opacity: fadeAnim,
          transform: [{
            translateY: fadeAnim.interpolate({
              inputRange: [0, 1],
              outputRange: [30, 0],
            }),
          }],
        }
      ]}
    >
      <TouchableOpacity
        style={styles.statCardContent}
        onPress={onPress}
        disabled={!onPress}
      >
        <View style={styles.statContent}>
          <Text style={styles.statValue}>{value}</Text>
          <Text style={styles.statTitle}>{title}</Text>
          {subtitle && <Text style={styles.statSubtitle}>{subtitle}</Text>}
        </View>
        <View style={[styles.statIconContainer, { backgroundColor: color + '20' }]}>
          <MaterialCommunityIcons name={icon} size={24} color={color} />
        </View>
      </TouchableOpacity>
    </Animated.View>
  );

  const renderEmployeeCard = ({ item, index }) => {
    const isSelected = selectedEmployees.includes(item.id);
    const employeeCodeData = (employeeData.employeeCodes||[]).find(code => code.code === item.employeeCode);

    return (
      <Animated.View
        style={[
          styles.employeeCard,
          isSelected && styles.selectedCard,
          {
            opacity: fadeAnim,
            transform: [{
              translateY: fadeAnim.interpolate({
                inputRange: [0, 1],
                outputRange: [50 * (index + 1), 0],
              }),
            }],
          }
        ]}
      >
        <View style={styles.employeeHeader}>
          <TouchableOpacity
            style={styles.selectionCheckbox}
            onPress={() => toggleEmployeeSelection(item.id)}
          >
            <MaterialCommunityIcons 
              name={isSelected ? "checkbox-marked" : "checkbox-blank-outline"} 
              size={24} 
              color={isSelected ? "#0e4e96" : "#64748b"} 
            />
          </TouchableOpacity>
          
          <TouchableOpacity
            onPress={() => {
              if (selectedEmployees.length > 0) return;
              showPhotoOptions(uri => {
                const updated = (employeeData.employees||[]).map(e =>
                  e.id === item.id ? { ...e, avatar: uri } : e
                );
                const updatedData = { ...employeeData, employees: updated };
                saveEmployeeData(updatedData);
                // Also push to API
                AsyncStorage.getItem('auth_token').then(async tok => {
                  const hdrs = { 'Content-Type':'application/json','X-API-Key':'16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',...(tok?{Authorization:`Bearer ${tok}`}:{}) };
                  const empId = item.serverId || item.id;
                  if (String(empId).startsWith('emp_')) {
                    // No server ID yet — store URI only locally
                    return;
                  }
                  // Upload via base64 endpoint
                  try {
                    const { FileSystem } = require('expo-file-system');
                    const b64 = await FileSystem.readAsStringAsync(uri, { encoding: FileSystem.EncodingType.Base64 });
                    await fetch(`https://api.jobeng.ai/api/employees/${empId}/photo`, {
                      method: 'POST', headers: hdrs,
                      body: JSON.stringify({ image_data: `data:image/jpeg;base64,${b64}`, image_type: 'jpeg' }),
                    });
                  } catch (_) {
                    // Fallback to PUT with URI
                    fetch(`https://api.jobeng.ai/api/employees/${empId}`, {
                      method: 'PUT', headers: hdrs, body: JSON.stringify({ profile_image: uri }),
                    }).catch(() => {});
                  }
                }).catch(() => {});
              });
            }}
            style={{ position:'relative' }}>
            <Image
              source={{ uri: item.avatar && item.avatar.startsWith('http') ? item.avatar : `https://ui-avatars.com/api/?name=${encodeURIComponent(item.name||'E')}&background=0e4e96&color=fff&size=128` }}
              style={styles.employeeAvatar}
              onError={() => {}}
            />
            <View style={{ position:'absolute', bottom:0, right:0, width:18, height:18, borderRadius:9,
              backgroundColor:'#0e4e96', borderWidth:1.5, borderColor:'#fff',
              alignItems:'center', justifyContent:'center' }}>
              <MaterialCommunityIcons name="camera" size={10} color="#fff"/>
            </View>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.employeeInfo}
            onPress={() => selectedEmployees.length === 0 && navigation.navigate('EmployeeDashboard', {
              employee: item,
              employeeId: item.serverId || item.id,
            })}
            activeOpacity={selectedEmployees.length > 0 ? 1 : 0.7}
          >
            <Text style={styles.employeeName}>{item.name}</Text>
            <Text style={styles.employeePosition}>{item.position}</Text>
            <Text style={styles.employeeDepartment}>{item.department} • {item.employeeId}</Text>
            <Text style={styles.employeeEmail}>{item.email}</Text>
            
            <View style={styles.employeeTags}>
              <View style={[styles.employeeCodeTag, { backgroundColor: employeeCodeData?.color || '#64748b' }]}>
                <Text style={styles.employeeCodeText}>{item.employeeCode}</Text>
              </View>
              <View style={[
                styles.statusTag,
                { backgroundColor: 
                  item.status === EMPLOYEE_STATUS.ACTIVE ? '#10b981' : 
                  item.status === EMPLOYEE_STATUS.ON_LEAVE ? '#f59e0b' : 
                  item.status === EMPLOYEE_STATUS.PROBATION ? '#dc2626' : '#64748b'
                }
              ]}>
                <Text style={styles.statusText}>{item.status}</Text>
              </View>
            </View>
          </TouchableOpacity>
          
          <View style={styles.employeeActions}>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => {
                setSelectedEmployee(item);
                setFormErrors({});
                setShowEditEmployeeModal(true);
              }}
            >
              <MaterialCommunityIcons name="pencil" size={20} color="#0e4e96" />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => can('view_employee_details') ? navigation.navigate('EmployeeDetailsScreen', { employee: { ...item, id: item.serverId || item.id }, employeeId: item.serverId || item.id }) : Alert.alert('Restricted', 'Employee details access is restricted.')}
            >
              <MaterialCommunityIcons name="eye" size={20} color="#8b5cf6" />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => navigation.navigate('EmployeeDashboard', {
                employee: item,
                employeeId: item.serverId || item.id,
              })}
            >
              <MaterialCommunityIcons name="view-dashboard" size={20} color="#10b981" />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => can('delete_employee') ? deleteEmployee(item.id) : Alert.alert('Restricted', 'Deleting employees is restricted.')}
            >
              <MaterialCommunityIcons name="delete" size={20} color="#dc2626" />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.actionButton, { backgroundColor: '#7C3AED15' }]}
              onPress={() => navigation.navigate('EvaluationAssessmentScreen', {
                companyLogo: companyLogo,
                companyName: companyName,
                employeeId: item.serverId || item.id,
                employeeName: item.name || item.employee_name || '',
                employeePosition: item.position || item.job_title || '',
                employeeDepartment: item.department || '',
                employeeEmail: item.email || '',
                employeeSalary: item.salary || '',
              })}
            >
              <MaterialCommunityIcons name="clipboard-check" size={20} color="#7C3AED" />
            </TouchableOpacity>
          </View>
        </View>
        
        <View style={styles.employeeDetails}>
          <View style={styles.detailRow}>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="cash" size={16} color="#64748b" />
              <Text style={styles.detailText}>${item.salary?.toLocaleString()}</Text>
            </View>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="calendar" size={16} color="#64748b" />
              <Text style={styles.detailText}>{new Date(item.startDate).toLocaleDateString()}</Text>
            </View>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="map-marker" size={16} color="#64748b" />
              <Text style={styles.detailText}>{item.location}</Text>
            </View>
          </View>
        </View>

        <View style={styles.performanceRow}>
          <View style={styles.performanceItem}>
            <Text style={styles.performanceLabel}>Warnings:</Text>
            <View style={styles.warningBadge}>
              <MaterialCommunityIcons 
                name="alert-circle" 
                size={14} 
                color={item.warningCount > 0 ? '#dc2626' : '#10b981'} 
              />
              <Text style={[
                styles.warningCount,
                { color: item.warningCount > 0 ? '#dc2626' : '#10b981' }
              ]}>
                {item.warningCount}
              </Text>
            </View>
          </View>
          
          <View style={styles.performanceItem}>
            <Text style={styles.performanceLabel}>Performance:</Text>
            <View style={styles.ratingContainer}>
              {[1, 2, 3, 4, 5].map((star) => (
                <MaterialCommunityIcons
                  key={star}
                  name={star <= item.performanceRating ? "star" : "star-outline"}
                  size={16}
                  color={star <= item.performanceRating ? "#f59e0b" : "#d1d5db"}
                />
              ))}
            </View>
          </View>
        </View>

        {item.skills && item.skills.length > 0 && (
          <View style={styles.skillsContainer}>
            <Text style={styles.skillsLabel}>Skills:</Text>
            <View style={styles.skillsList}>
              {item.skills.slice(0, 3).map((skill, index) => (
                <View key={index} style={styles.skillTag}>
                  <Text style={styles.skillText}>{skill}</Text>
                </View>
              ))}
              {item.skills.length > 3 && (
                <View style={styles.moreSkillsTag}>
                  <Text style={styles.moreSkillsText}>+{item.skills.length - 3} more</Text>
                </View>
              )}
            </View>
          </View>
        )}

        <View style={styles.quickActions}>
          <TouchableOpacity
            style={styles.quickActionButton}
            onPress={() => {
              setWarningLetter(prev => ({
                ...prev,
                employeeId: item.employeeId,
                employeeName: item.name,
                issuedDate: new Date().toISOString().split('T')[0]
              }));
              setShowWarningLetterModal(true);
            }}
          >
            <MaterialCommunityIcons name="alert-circle" size={16} color="#dc2626" />
            <Text style={styles.quickActionText}>Warning</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.quickActionButton}
            onPress={() => {
              setPromotion(prev => ({
                ...prev,
                employeeId: item.employeeId,
                employeeName: item.name,
                currentPosition: item.position,
                currentDepartment: item.department,
                currentSalary: item.salary?.toString(),
                effectiveDate: new Date().toISOString().split('T')[0]
              }));
              setShowPromotionModal(true);
            }}
          >
            <MaterialCommunityIcons name="trending-up" size={16} color="#10b981" />
            <Text style={styles.quickActionText}>Promote</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.quickActionButton}
            onPress={() => {
              setPerformanceReview(prev => ({
                ...prev,
                employeeId: item.employeeId,
                employeeName: item.name,
                reviewer: item.manager,
                reviewDate: new Date().toISOString().split('T')[0]
              }));
              setShowPerformanceModal(true);
            }}
          >
            <MaterialCommunityIcons name="chart-line" size={16} color="#8b5cf6" />
            <Text style={styles.quickActionText}>Review</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.quickActionButton}
            onPress={() => Linking.openURL(`mailto:${item.email}`)}
          >
            <MaterialCommunityIcons name="email" size={16} color="#0e4e96" />
            <Text style={styles.quickActionText}>Email</Text>
          </TouchableOpacity>
        </View>
      </Animated.View>
    );
  };

  // Enhanced Tab Renderers
  // Add evaluation shortcut
  const navigateToEvaluation = (employee = null) => {
    const params = { companyLogo: companyLogo, companyName: companyName };
    if (employee) { params.employeeId = employee.serverId || employee.id; params.employeeName = employee.name || employee.employee_name; }
    navigation.navigate('EvaluationAssessmentScreen', params);
  };

  const renderOverviewTab = () => (
    <ScrollView 
      style={styles.tabContent}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={loadEmployeeData} />
      }
      showsVerticalScrollIndicator={false}
    >
      {/* Quick Stats */}
      <View style={styles.statsGrid}>
        {renderStatCard(
          'Total Employees',
          employeeData.overview?.totalEmployees || 0,
          'account-group',
          '#0e4e96',
          'All employees'
        )}
        {renderStatCard(
          'Active Employees',
          employeeData.overview?.activeEmployees || 0,
          'account-check',
          '#10b981',
          'Currently working'
        )}
        {renderStatCard(
          'On Leave',
          employeeData.overview?.onLeave || 0,
          'account-clock',
          '#f59e0b',
          'Temporary absence'
        )}
        {renderStatCard(
          'New Hires',
          employeeData.overview?.newHires || 0,
          'account-plus',
          '#8b5cf6',
          'Last 30 days'
        )}
        {renderStatCard(
          'Warning Letters',
          employeeData.overview?.warningLetters || 0,
          'alert-circle',
          '#dc2626',
          'Active warnings',
          () => setActiveTab('warnings')
        )}
        {renderStatCard(
          'Pending Promotions',
          employeeData.overview?.promotions || 0,
          'trending-up',
          '#06b6d4',
          'Awaiting approval',
          () => setActiveTab('promotions')
        )}
      </View>

      {/* Payroll Cost Banner */}
      {renderPayrollCostBanner()}

      {/* Quick Actions */}
      <View style={styles.quickActionsSection}>
        <Text style={styles.sectionTitle}>Quick Actions</Text>
        <View style={styles.quickActionsGrid}>
          <TouchableOpacity
            style={[styles.quickAction, { borderColor: '#10b981', borderWidth: 1 }]}
            onPress={navigateToPayroll}
          >
            <View style={[styles.quickActionIcon, { backgroundColor: '#d1fae5' }]}>
              <MaterialCommunityIcons name="calculator" size={22} color="#10b981" />
            </View>
            <Text style={styles.quickActionText}>Payroll</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.quickAction, { borderColor: '#3b82f6', borderWidth: 1 }]}
            onPress={syncAllEmployeesToPayroll}
          >
            <View style={[styles.quickActionIcon, { backgroundColor: '#dbeafe' }]}>
              <MaterialCommunityIcons name="account-sync" size={22} color="#3b82f6" />
            </View>
            <Text style={styles.quickActionText}>Sync Payroll</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.quickAction}
            onPress={() => {
              resetNewEmployee();
              setFormErrors({});
              (async () => {
                const cid = await AsyncStorage.getItem('company_id').catch(() => null);
                const cbKey = `emp_save_${Date.now()}`;
                registerCallback(cbKey, async (newEmp) => {
                  const updatedData = { ...employeeData, employees: [newEmp, ...(employeeData.employees||[])] };
                  updatedData.analytics = calculateAnalytics(updatedData.employees);
                  await saveEmployeeData(updatedData);
                });
                navigation.navigate('AddEmployeeScreen', {
                  companyId: cid,
                  existingCount: (employeeData.employees||[]).length,
                  callbackKey: cbKey,
                });
              })();
            }}
          >
            <MaterialCommunityIcons name="account-plus" size={24} color="#10b981" />
            <Text style={styles.quickActionText}>Add Employee</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.quickAction}
            onPress={() => setShowImportModal(true)}
          >
            <MaterialCommunityIcons name="import" size={24} color="#0e4e96" />
            <Text style={styles.quickActionText}>Import Data</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.quickAction}
            onPress={() => setShowExportModal(true)}
          >
            <MaterialCommunityIcons name="export" size={24} color="#f59e0b" />
            <Text style={styles.quickActionText}>Export Data</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.quickAction}
            onPress={() => setShowAnalyticsModal(true)}
          >
            <MaterialCommunityIcons name="chart-bar" size={24} color="#8b5cf6" />
            <Text style={styles.quickActionText}>Analytics</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Department Overview */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Department Overview</Text>
        <View style={styles.departmentList}>
          {Object.entries(employeeData.analytics?.departmentDistribution || {}).map(([dept, count]) => {
            const percentage = employeeData.overview?.totalEmployees > 0 
              ? ((employeeData.overview || {}).totalEmployees > 0 ? (count / (employeeData.overview || {}).totalEmployees * 100).toFixed(1) : "0.0")
              : 0;
            const departmentEmployees = employeeData.employees?.filter(emp => emp.department === dept) || [];
            const activeCount = departmentEmployees.filter(emp => emp.status === EMPLOYEE_STATUS.ACTIVE).length;
            
            return (
              <TouchableOpacity
                key={dept}
                style={styles.departmentItem}
                onPress={() => {
                  setFilterDepartment(dept);
                  setActiveTab('employees');
                }}
              >
                <View style={styles.departmentHeader}>
                  <Text style={styles.departmentName}>{dept}</Text>
                  <Text style={styles.departmentCount}>{count} employees ({percentage}%)</Text>
                </View>
                <View style={styles.departmentStats}>
                  <Text style={styles.departmentStat}>
                    {activeCount} active • {count - activeCount} other
                  </Text>
                  <Text style={styles.departmentStat}>
                    Avg salary: ${Math.floor(
                      departmentEmployees.reduce((sum, emp) => sum + (emp.salary || 0), 0) / (count || 1)
                    ).toLocaleString()}
                  </Text>
                </View>
                <View style={styles.progressBar}>
                  <View 
                    style={[
                      styles.progressFill, 
                      { width: `${percentage}%` }
                    ]} 
                  />
                </View>
              </TouchableOpacity>
            );
          })}
          {/* Show empty state if no departments */}
          {Object.keys(employeeData.analytics?.departmentDistribution || {}).length === 0 && (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>No department data available</Text>
            </View>
          )}
        </View>
      </View>

      {/* Recent Activity */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Recent Activity</Text>
        <View style={styles.activityList}>
          <View style={styles.activityItem}>
            <MaterialCommunityIcons name="account-plus" size={20} color="#10b981" />
            <View style={styles.activityContent}>
              <Text style={styles.activityText}>3 new employees added this week</Text>
              <Text style={styles.activityTime}>2 days ago</Text>
            </View>
          </View>
          <View style={styles.activityItem}>
            <MaterialCommunityIcons name="alert-circle" size={20} color="#dc2626" />
            <View style={styles.activityContent}>
              <Text style={styles.activityText}>2 warning letters issued</Text>
              <Text style={styles.activityTime}>1 week ago</Text>
            </View>
          </View>
          <View style={styles.activityItem}>
            <MaterialCommunityIcons name="trending-up" size={20} color="#0e4e96" />
            <View style={styles.activityContent}>
              <Text style={styles.activityText}>1 promotion approved</Text>
              <Text style={styles.activityTime}>3 days ago</Text>
            </View>
          </View>
        </View>
      </View>
    </ScrollView>
  );

  const renderEmployeesTab = () => {
    const filteredEmployees = getFilteredEmployees;
    
    return (
      <View style={styles.tabContentFull}>
        {/* Search and Filter Section */}
        <View style={styles.searchSection}>
          <View style={styles.searchContainer}>
            <MaterialCommunityIcons name="magnify" size={20} color="#64748b" />
            <TextInput
              style={styles.searchInput}
              placeholder="Search employees by name, email, department, skills..."
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
            {searchQuery ? (
              <TouchableOpacity onPress={() => setSearchQuery('')}>
                <MaterialCommunityIcons name="close" size={20} color="#64748b" />
              </TouchableOpacity>
            ) : null}
          </View>
          
          <View style={styles.viewToggle}>
            <TouchableOpacity
              style={[styles.viewToggleButton, viewMode === 'grid' && styles.viewToggleButtonActive]}
              onPress={() => setViewMode('grid')}
            >
              <MaterialCommunityIcons 
                name="view-grid" 
                size={20} 
                color={viewMode === 'grid' ? '#0e4e96' : '#64748b'} 
              />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.viewToggleButton, viewMode === 'list' && styles.viewToggleButtonActive]}
              onPress={() => setViewMode('list')}
            >
              <MaterialCommunityIcons 
                name="format-list-bulleted" 
                size={20} 
                color={viewMode === 'list' ? '#0e4e96' : '#64748b'} 
              />
            </TouchableOpacity>
          </View>
        </View>

        {/* Enhanced Filters */}
        <View style={styles.filtersSection}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <View style={styles.filterGroup}>
              <Text style={styles.filterLabel}>Department:</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                {departments.map((dept) => (
                  <TouchableOpacity
                    key={dept}
                    style={[
                      styles.filterOption,
                      filterDepartment === dept && styles.filterOptionSelected
                    ]}
                    onPress={() => setFilterDepartment(dept)}
                  >
                    <Text style={[
                      styles.filterOptionText,
                      filterDepartment === dept && styles.filterOptionTextSelected
                    ]}>
                      {dept}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
            
            <View style={styles.filterGroup}>
              <Text style={styles.filterLabel}>Status:</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                {employeeStatuses.map((status) => (
                  <TouchableOpacity
                    key={status}
                    style={[
                      styles.filterOption,
                      filterStatus === status && styles.filterOptionSelected
                    ]}
                    onPress={() => setFilterStatus(status)}
                  >
                    <Text style={[
                      styles.filterOptionText,
                      filterStatus === status && styles.filterOptionTextSelected
                    ]}>
                      {status}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>

            <View style={styles.filterGroup}>
              <Text style={styles.filterLabel}>Employee Code:</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                {employeeCodes.map((code) => (
                  <TouchableOpacity
                    key={code}
                    style={[
                      styles.filterOption,
                      filterEmployeeCode === code && styles.filterOptionSelected
                    ]}
                    onPress={() => setFilterEmployeeCode(code)}
                  >
                    <Text style={[
                      styles.filterOptionText,
                      filterEmployeeCode === code && styles.filterOptionTextSelected
                    ]}>
                      {code}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </ScrollView>
        </View>

        {/* Sort Section */}
        <View style={styles.sortSection}>
          <Text style={styles.sortLabel}>Sort by:</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {sortOptions.map(option => (
              <TouchableOpacity
                key={option.key}
                style={[
                  styles.sortOption,
                  sortBy === option.key && styles.sortOptionSelected
                ]}
                onPress={() => handleSort(option.key)}
              >
                <Text style={[
                  styles.sortOptionText,
                  sortBy === option.key && styles.sortOptionTextSelected
                ]}>
                  {option.label}
                </Text>
                {sortBy === option.key && (
                  <MaterialCommunityIcons 
                    name={sortOrder === 'asc' ? 'arrow-up' : 'arrow-down'} 
                    size={16} 
                    color="#0e4e96" 
                  />
                )}
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        {/* Actions Section */}
        <View style={styles.actionsSection}>
          <View style={styles.selectionActions}>
            <Text style={styles.sectionTitle}>
              Employees ({filteredEmployees.length})
              {selectedEmployees.length > 0 && ` • ${selectedEmployees.length} selected`}
            </Text>
            <View style={styles.selectionButtons}>
              {selectedEmployees.length > 0 ? (
                <>
                  <TouchableOpacity
                    style={styles.bulkActionButton}
                    onPress={() => setShowBulkActionsModal(true)}
                  >
                    <MaterialCommunityIcons name="cog-box" size={20} color="#fff" />
                    <Text style={styles.bulkActionButtonText}>Bulk Actions</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.clearButton}
                    onPress={clearSelection}
                  >
                    <MaterialCommunityIcons name="close" size={20} color="#fff" />
                    <Text style={styles.clearButtonText}>Clear</Text>
                  </TouchableOpacity>
                </>
              ) : (
                <>
                  <TouchableOpacity
                    style={styles.selectAllButton}
                    onPress={selectAllEmployees}
                  >
                    <MaterialCommunityIcons name="checkbox-multiple-marked" size={20} color="#fff" />
                    <Text style={styles.selectAllButtonText}>Select All</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.addButton}
                    onPress={() => {
                      resetNewEmployee();
                      setFormErrors({});
                      (async () => {
                const cid = await AsyncStorage.getItem('company_id').catch(() => null);
                const cbKey = `emp_save_${Date.now()}`;
                registerCallback(cbKey, async (newEmp) => {
                  const updatedData = { ...employeeData, employees: [newEmp, ...(employeeData.employees||[])] };
                  updatedData.analytics = calculateAnalytics(updatedData.employees);
                  await saveEmployeeData(updatedData);
                });
                navigation.navigate('AddEmployeeScreen', {
                  companyId: cid,
                  existingCount: (employeeData.employees||[]).length,
                  callbackKey: cbKey,
                });
              })();
                    }}
                  >
                    <MaterialCommunityIcons name="plus" size={20} color="#fff" />
                    <Text style={styles.addButtonText}>Add Employee</Text>
                  </TouchableOpacity>
                </>
              )}
            </View>
          </View>
        </View>
        
        {/* Employees List */}
        {filteredEmployees.length > 0 ? (
          <FlatList
            data={filteredEmployees}
            renderItem={renderEmployeeCard}
            keyExtractor={(item) => item.id}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.employeesList}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={loadEmployeeData} />
            }
            ListFooterComponent={<View style={styles.listFooter} />}
          />
        ) : (
          <View style={styles.emptyContainer}>
            <MaterialCommunityIcons name="account-search" size={64} color="#94a3b8" />
            <Text style={styles.emptyText}>No employees found</Text>
            <Text style={styles.emptySubtext}>Try adjusting your search or filters</Text>
            <TouchableOpacity 
              style={styles.emptyActionButton}
              onPress={() => {
                setSearchQuery('');
                setFilterDepartment('All');
                setFilterStatus('All');
                setFilterEmployeeCode('All');
              }}
            >
              <Text style={styles.emptyActionText}>Clear all filters</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    );
  };

  // ═══════ EVALUATION TAB ═══════
  const renderEvaluationTab = () => (
    <ScrollView style={{ flex: 1, padding: 16 }} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={loadEmployeeData} />}>
      <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#1F2937', marginBottom: 16 }}>Employee Evaluation & Assessment</Text>

      {/* Quick Actions */}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        {[
          { l: 'Full Evaluation', i: 'clipboard-check', c: '#7C3AED', fn: () => navigation.navigate('EvaluationAssessmentScreen', { companyLogo: companyLogo, companyName: companyName }) },
          { l: 'Performance Review', i: 'chart-line', c: '#8B5CF6', fn: () => { navigation.navigate('EvaluationAssessmentScreen', { companyLogo: companyLogo, companyName: companyName }); } },
          { l: '360° Feedback', i: 'account-group', c: '#EC4899', fn: () => navigation.navigate('EvaluationAssessmentScreen', { companyLogo: companyLogo, companyName: companyName }) },
          { l: 'Growth Plans', i: 'trending-up', c: '#10B981', fn: () => navigation.navigate('EvaluationAssessmentScreen', { companyLogo: companyLogo, companyName: companyName }) },
        ].map((item, i) => (
          <TouchableOpacity key={i} style={{ width: (width - 48) / 2, backgroundColor: '#FFF', borderRadius: 12, padding: 16, borderWidth: 1, borderColor: '#E5E7EB', alignItems: 'center' }} onPress={item.fn}>
            <View style={{ width: 48, height: 48, borderRadius: 24, backgroundColor: item.c + '15', justifyContent: 'center', alignItems: 'center', marginBottom: 8 }}>
              <MaterialCommunityIcons name={item.i} size={24} color={item.c} /></View>
            <Text style={{ fontSize: 13, fontWeight: '600', color: '#1F2937', textAlign: 'center' }}>{item.l}</Text>
          </TouchableOpacity>))}
      </View>

      {/* Employee Quick Evaluate */}
      <Text style={{ fontSize: 16, fontWeight: '700', color: '#1F2937', marginBottom: 12 }}>Quick Evaluate Employee</Text>
      {(employeeData.employees || []).slice(0, 10).map((emp, i) => (
        <TouchableOpacity key={emp.id || i} style={{ backgroundColor: '#FFF', padding: 12, borderRadius: 10, borderWidth: 1, borderColor: '#E5E7EB', marginBottom: 8, flexDirection: 'row', alignItems: 'center' }}
          onPress={() => navigation.navigate('EvaluationAssessmentScreen', { companyLogo: companyLogo, companyName: companyName, employeeId: emp.serverId || emp.id, employeeName: emp.name || emp.employee_name })}>
          <View style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: '#7C3AED', justifyContent: 'center', alignItems: 'center', marginRight: 10 }}>
            <Text style={{ color: '#FFF', fontWeight: 'bold', fontSize: 14 }}>{((emp.name || emp.employee_name || '?').split(' ').map(w => w[0]).join('').toUpperCase()).slice(0, 2)}</Text></View>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 14, fontWeight: '600', color: '#1F2937' }}>{emp.name || emp.employee_name || 'Employee'}</Text>
            <Text style={{ fontSize: 12, color: '#6B7280' }}>{emp.position || emp.job_title || ''}{emp.department ? ' \u00B7 ' + emp.department : ''}</Text></View>
          <View style={{ flexDirection: 'row', gap: 6 }}>
            <TouchableOpacity style={{ paddingHorizontal: 10, paddingVertical: 5, borderRadius: 12, backgroundColor: '#7C3AED15' }}
              onPress={() => navigation.navigate('EvaluationAssessmentScreen', { companyLogo: companyLogo, companyName: companyName, employeeId: emp.serverId || emp.id, employeeName: emp.name || emp.employee_name })}>
              <Text style={{ fontSize: 11, color: '#7C3AED', fontWeight: '600' }}>Evaluate</Text></TouchableOpacity>
          </View>
        </TouchableOpacity>))}

      {/* Evaluation Overview */}
      <Text style={{ fontSize: 16, fontWeight: '700', color: '#1F2937', marginTop: 16, marginBottom: 12 }}>Evaluation Features</Text>
      {[
        { title: 'Performance Assessment', desc: 'Quality, productivity, job knowledge, reliability, decision making', icon: 'chart-line', color: '#3B82F6' },
        { title: 'Behavior Assessment', desc: 'Professionalism, teamwork, communication, punctuality, initiative', icon: 'account-heart', color: '#EC4899' },
        { title: 'Skills Assessment', desc: 'Technical, soft skills, domain knowledge, project management', icon: 'brain', color: '#8B5CF6' },
        { title: 'Goal Tracking', desc: 'Set objectives, track progress, deadlines, priority levels', icon: 'target', color: '#10B981' },
        { title: '360\u00B0 Feedback', desc: 'Peer, manager, direct report, cross-team, client reviews', icon: 'account-multiple', color: '#F59E0B' },
        { title: 'Attendance Tracker', desc: 'Present, absent, late, WFH, sick leave, annual leave', icon: 'calendar-check', color: '#059669' },
        { title: 'Disciplinary Records', desc: 'Warnings, suspensions, PIPs, commendations, recognition', icon: 'shield-alert', color: '#EF4444' },
        { title: 'Career Growth Plans', desc: 'Objectives, skills development, training, mentoring, milestones', icon: 'trending-up', color: '#14B8A6' },
        { title: 'PDF Reports', desc: 'Full evaluation reports with company letterhead, print support', icon: 'file-pdf-box', color: '#DC2626' },
      ].map((feature, i) => (
        <TouchableOpacity key={i} style={{ backgroundColor: '#FFF', padding: 14, borderRadius: 10, borderWidth: 1, borderColor: '#E5E7EB', marginBottom: 8, flexDirection: 'row', alignItems: 'center' }}
          onPress={() => navigation.navigate('EvaluationAssessmentScreen', { companyLogo: companyLogo, companyName: companyName })}>
          <View style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: feature.color + '15', justifyContent: 'center', alignItems: 'center', marginRight: 12 }}>
            <MaterialCommunityIcons name={feature.icon} size={20} color={feature.color} /></View>
          <View style={{ flex: 1 }}><Text style={{ fontSize: 14, fontWeight: '600', color: '#1F2937' }}>{feature.title}</Text>
            <Text style={{ fontSize: 11, color: '#6B7280', marginTop: 1 }}>{feature.desc}</Text></View>
          <MaterialCommunityIcons name="chevron-right" size={20} color="#9CA3AF" />
        </TouchableOpacity>))}

      {/* Open Full Evaluation */}
      <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#7C3AED', borderRadius: 10, padding: 14, marginTop: 12 }}
        onPress={() => navigation.navigate('EvaluationAssessmentScreen', { companyLogo: companyLogo, companyName: companyName })}>
        <MaterialCommunityIcons name="clipboard-check" size={20} color="#FFF" />
        <Text style={{ color: '#FFF', fontWeight: '700', fontSize: 16, marginLeft: 8 }}>Open Full Evaluation System</Text>
      </TouchableOpacity>
      <View style={{ height: 40 }} />
    </ScrollView>
  );

  const renderTabContent = () => {
    if (loading) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading employee data...</Text>
        </View>
      );
    }

    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'employees':
        return renderEmployeesTab();
      case 'warnings':
        return renderWarningsTab();
      case 'promotions':
        return renderPromotionsTab();
      case 'performance':
        return renderPerformanceTab();
      case 'codes':
        return renderCodesTab();
      case 'analytics':
        return renderAnalyticsTab();
      case 'evaluation':
        return renderEvaluationTab();
      default:
        return renderOverviewTab();
    }
  };

  // Placeholder for other tab renderers

  // ── Save functions for Warnings, Promotions, Performance, Codes ────────────
  const saveWarningLetter = async () => {
    if (!can('issue_warning')) { Alert.alert('Restricted', 'Issuing warnings is restricted by admin.'); return; }
    if (!warningLetter.employeeName || !warningLetter.description) {
      Alert.alert('Required', 'Please select an employee and enter a description.'); return;
    }
    setIsSubmitting(true);
    try {
      const letter = { ...warningLetter, id: `warn_${Date.now()}`, createdAt: new Date().toISOString() };
      const updated = { ...employeeData,
        warningLetters: [...(employeeData.warningLetters||[]), letter],
        overview: { ...employeeData.overview, warningLetters: (employeeData.overview?.warningLetters||0)+1 },
      };
      updated.employees = (updated.employees||[]).map(e =>
        e.id === warningLetter.employeeId || e.employeeId === warningLetter.employeeId
          ? { ...e, warningCount: (e.warningCount||0)+1 } : e);
      await saveEmployeeData(updated);
      setShowWarningLetterModal(false);
      Alert.alert('✅ Issued', `Warning letter issued to ${warningLetter.employeeName}`);
    } catch { Alert.alert('Error','Could not save warning.'); }
    finally { setIsSubmitting(false); }
  };

  const savePromotion = async () => {
    if (!can('schedule_review')) { Alert.alert('Restricted', 'Promotion management is restricted by admin.'); return; }
    if (!promotion.employeeName || !promotion.newPosition) {
      Alert.alert('Required', 'Select an employee and enter new position.'); return;
    }
    setIsSubmitting(true);
    try {
      const rec = { ...promotion, id: `promo_${Date.now()}`, createdAt: new Date().toISOString() };
      const updated = { ...employeeData,
        promotions: [...(employeeData.promotions||[]), rec],
        overview: { ...employeeData.overview, promotions: (employeeData.overview?.promotions||0)+1 },
      };
      if (rec.status === 'Approved') {
        updated.employees = (updated.employees||[]).map(e =>
          e.id === promotion.employeeId || e.employeeId === promotion.employeeId
            ? { ...e, position: promotion.newPosition, department: promotion.newDepartment||e.department,
                salary: parseInt(promotion.newSalary)||e.salary, promotionCount:(e.promotionCount||0)+1 } : e);
      }
      await saveEmployeeData(updated);
      setShowPromotionModal(false);
      Alert.alert('✅ Saved', `Promotion record saved for ${promotion.employeeName}`);
    } catch { Alert.alert('Error','Could not save promotion.'); }
    finally { setIsSubmitting(false); }
  };

  const savePerformanceReview = async () => {
    if (!can('schedule_review')) { Alert.alert('Restricted', 'Performance reviews are restricted by admin.'); return; }
    if (!performanceReview.employeeName) {
      Alert.alert('Required', 'Select an employee.'); return;
    }
    setIsSubmitting(true);
    try {
      const review = { ...performanceReview, id: `perf_${Date.now()}`, createdAt: new Date().toISOString() };
      const rating = parseInt(review.overallRating)||3;
      const updated = { ...employeeData,
        performanceReviews: [...(employeeData.performanceReviews||[]), review],
        overview: { ...employeeData.overview, performanceReviews: (employeeData.overview?.performanceReviews||0)+1 },
      };
      updated.employees = (updated.employees||[]).map(e =>
        e.id === performanceReview.employeeId || e.employeeId === performanceReview.employeeId
          ? { ...e, performanceRating: rating, lastReviewDate: new Date().toISOString().split('T')[0] } : e);
      await saveEmployeeData(updated);
      setShowPerformanceModal(false);
      Alert.alert('✅ Saved', `Performance review saved for ${performanceReview.employeeName}`);
    } catch { Alert.alert('Error','Could not save review.'); }
    finally { setIsSubmitting(false); }
  };

  const saveEmployeeCode = async () => {
    if (!employeeCode.code || !employeeCode.name) {
      Alert.alert('Required', 'Enter a code and name.'); return;
    }
    setIsSubmitting(true);
    try {
      const rec = { ...employeeCode, id: `code_${Date.now()}`, createdAt: new Date().toISOString() };
      const updated = { ...employeeData,
        employeeCodes: [...(employeeData.employeeCodes||[]), rec],
      };
      await saveEmployeeData(updated);
      setShowEmployeeCodeModal(false);
      Alert.alert('✅ Created', `Code "${employeeCode.code}" created.`);
    } catch { Alert.alert('Error','Could not save code.'); }
    finally { setIsSubmitting(false); }
  };

  // ── WARNINGS TAB ────────────────────────────────────────────────────────────
  const renderWarningsTab = () => {
    const warnings = employeeData.warningLetters || [];
    const sev = { Critical:'#7C3AED', High:'#EF4444', Medium:'#F59E0B', Low:'#10B981' };
    return (
      <ScrollView style={styles.tabContent} showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={loadEmployeeData}/>}>
        <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <Text style={styles.sectionTitle}>Warning Letters ({warnings.length})</Text>
          <TouchableOpacity style={{ backgroundColor:'#DC2626', borderRadius:10, paddingHorizontal:14, paddingVertical:8,
            flexDirection:'row', alignItems:'center', gap:5 }}
            onPress={()=>setShowWarningLetterModal(true)}>
            <MaterialCommunityIcons name="plus" size={16} color="#fff"/>
            <Text style={{ color:'#fff', fontWeight:'700', fontSize:12 }}>Issue Warning</Text>
          </TouchableOpacity>
        </View>

        {/* Warning stats */}
        <View style={{ flexDirection:'row', gap:10, marginBottom:16 }}>
          {[
            { label:'Total', val:warnings.length, color:'#374151' },
            { label:'Active', val:warnings.filter(w=>w.status==='Active').length, color:'#EF4444' },
            { label:'Critical', val:warnings.filter(w=>w.severity==='Critical').length, color:'#7C3AED' },
            { label:'Resolved', val:warnings.filter(w=>w.status==='Resolved').length, color:'#10B981' },
          ].map(m=>(
            <View key={m.label} style={{ flex:1, backgroundColor:'#fff', borderRadius:12, padding:12, alignItems:'center',
              borderTopWidth:3, borderTopColor:m.color, shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.05, shadowRadius:3, elevation:2 }}>
              <Text style={{ fontSize:20, fontWeight:'900', color:m.color }}>{m.val}</Text>
              <Text style={{ fontSize:10, color:'#6B7280', marginTop:2 }}>{m.label}</Text>
            </View>
          ))}
        </View>

        {warnings.length === 0 ? (
          <View style={{ alignItems:'center', paddingVertical:60 }}>
            <MaterialCommunityIcons name="shield-check" size={56} color="#E5E7EB"/>
            <Text style={{ color:'#9CA3AF', marginTop:12, fontSize:15 }}>No warning letters issued</Text>
          </View>
        ) : warnings.map((w,i) => (
          <View key={w.id||i} style={{ backgroundColor:'#fff', borderRadius:14, padding:16, marginBottom:10,
            borderLeftWidth:4, borderLeftColor:sev[w.severity]||'#F59E0B',
            shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.05, shadowRadius:4, elevation:2 }}>
            <View style={{ flexDirection:'row', alignItems:'flex-start', justifyContent:'space-between' }}>
              <View style={{ flex:1 }}>
                <Text style={{ fontSize:15, fontWeight:'800', color:'#111827' }}>{w.employeeName}</Text>
                <Text style={{ fontSize:12, color:'#6B7280', marginTop:2 }}>{w.type} · {w.reason}</Text>
                <Text style={{ fontSize:12, color:'#374151', marginTop:6, lineHeight:18 }}>{w.description}</Text>
                <View style={{ flexDirection:'row', gap:8, marginTop:8 }}>
                  <View style={{ backgroundColor:(sev[w.severity]||'#F59E0B')+'20', borderRadius:8, paddingHorizontal:8, paddingVertical:3 }}>
                    <Text style={{ fontSize:10, fontWeight:'700', color:sev[w.severity]||'#F59E0B' }}>{w.severity}</Text>
                  </View>
                  <View style={{ backgroundColor: w.status==='Active'?'#FEF2F2':'#F0FDF4', borderRadius:8, paddingHorizontal:8, paddingVertical:3 }}>
                    <Text style={{ fontSize:10, fontWeight:'700', color: w.status==='Active'?'#EF4444':'#10B981' }}>{w.status}</Text>
                  </View>
                </View>
              </View>
              <View style={{ alignItems:'flex-end', gap:4 }}>
                <Text style={{ fontSize:11, color:'#9CA3AF' }}>{w.issuedDate}</Text>
                {w.acknowledgmentRequired && (
                  <MaterialCommunityIcons name="clipboard-check" size={14} color="#F59E0B"/>
                )}
              </View>
            </View>
            {w.notes ? <Text style={{ fontSize:11, color:'#9CA3AF', marginTop:8, fontStyle:'italic' }}>{w.notes}</Text> : null}
            <View style={{ flexDirection:'row', gap:8, marginTop:12, paddingTop:10, borderTopWidth:1, borderTopColor:'#F8FAFC' }}>
              <TouchableOpacity style={{ flex:1, backgroundColor:'#FEF2F2', borderRadius:8, padding:8,
                flexDirection:'row', justifyContent:'center', alignItems:'center', gap:4 }}
                onPress={()=>{const updated={...employeeData,warningLetters:(employeeData.warningLetters||[]).map(x=>x.id===w.id?{...x,status:'Resolved'}:x)};saveEmployeeData(updated);}}>
                <MaterialCommunityIcons name="check" size={14} color="#10B981"/>
                <Text style={{ fontSize:12, color:'#10B981', fontWeight:'700' }}>Resolve</Text>
              </TouchableOpacity>
              <TouchableOpacity style={{ flex:1, backgroundColor:'#F9FAFB', borderRadius:8, padding:8,
                flexDirection:'row', justifyContent:'center', alignItems:'center', gap:4 }}
                onPress={()=>{const upd={...employeeData,warningLetters:(employeeData.warningLetters||[]).filter(x=>x.id!==w.id)};saveEmployeeData(upd);}}>
                <MaterialCommunityIcons name="delete" size={14} color="#EF4444"/>
                <Text style={{ fontSize:12, color:'#EF4444', fontWeight:'700' }}>Delete</Text>
              </TouchableOpacity>
            </View>
          </View>
        ))}
      </ScrollView>
    );
  };

  // ── PROMOTIONS TAB ──────────────────────────────────────────────────────────
  const renderPromotionsTab = () => {
    const promos = employeeData.promotions || [];
    return (
      <ScrollView style={styles.tabContent} showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={loadEmployeeData}/>}>
        <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <Text style={styles.sectionTitle}>Promotions & Transfers ({promos.length})</Text>
          <TouchableOpacity style={{ backgroundColor:'#F59E0B', borderRadius:10, paddingHorizontal:14, paddingVertical:8,
            flexDirection:'row', alignItems:'center', gap:5 }}
            onPress={()=>setShowPromotionModal(true)}>
            <MaterialCommunityIcons name="plus" size={16} color="#fff"/>
            <Text style={{ color:'#fff', fontWeight:'700', fontSize:12 }}>Add Promotion</Text>
          </TouchableOpacity>
        </View>

        <View style={{ flexDirection:'row', gap:10, marginBottom:16 }}>
          {[
            { label:'Total', val:promos.length, color:'#374151' },
            { label:'Approved', val:promos.filter(p=>p.status==='Approved').length, color:'#10B981' },
            { label:'Pending', val:promos.filter(p=>p.status==='Pending').length, color:'#F59E0B' },
            { label:'This Year', val:promos.filter(p=>new Date(p.createdAt).getFullYear()===new Date().getFullYear()).length, color:'#3B82F6' },
          ].map(m=>(
            <View key={m.label} style={{ flex:1, backgroundColor:'#fff', borderRadius:12, padding:12, alignItems:'center',
              borderTopWidth:3, borderTopColor:m.color, shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.05, shadowRadius:3, elevation:2 }}>
              <Text style={{ fontSize:20, fontWeight:'900', color:m.color }}>{m.val}</Text>
              <Text style={{ fontSize:10, color:'#6B7280', marginTop:2 }}>{m.label}</Text>
            </View>
          ))}
        </View>

        {promos.length === 0 ? (
          <View style={{ alignItems:'center', paddingVertical:60 }}>
            <MaterialCommunityIcons name="trending-up" size={56} color="#E5E7EB"/>
            <Text style={{ color:'#9CA3AF', marginTop:12, fontSize:15 }}>No promotions recorded yet</Text>
            <TouchableOpacity style={{ backgroundColor:'#F59E0B', borderRadius:12, paddingHorizontal:20, paddingVertical:11, marginTop:14,
              flexDirection:'row', gap:6, alignItems:'center' }} onPress={()=>setShowPromotionModal(true)}>
              <MaterialCommunityIcons name="plus" size={16} color="#fff"/>
              <Text style={{ color:'#fff', fontWeight:'700' }}>Record First Promotion</Text>
            </TouchableOpacity>
          </View>
        ) : promos.map((p,i)=>(
          <View key={p.id||i} style={{ backgroundColor:'#fff', borderRadius:14, padding:16, marginBottom:10,
            shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.05, shadowRadius:4, elevation:2 }}>
            <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'flex-start' }}>
              <View style={{ flex:1 }}>
                <Text style={{ fontSize:15, fontWeight:'800', color:'#111827' }}>{p.employeeName}</Text>
                <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginTop:6 }}>
                  <Text style={{ fontSize:12, color:'#6B7280', flex:1 }}>{p.currentPosition}</Text>
                  <MaterialCommunityIcons name="arrow-right" size={14} color="#10B981"/>
                  <Text style={{ fontSize:12, color:'#10B981', fontWeight:'700', flex:1 }}>{p.newPosition}</Text>
                </View>
                {p.currentSalary && p.newSalary && (
                  <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginTop:4 }}>
                    <Text style={{ fontSize:11, color:'#9CA3AF' }}>£{Number(p.currentSalary).toLocaleString()}</Text>
                    <MaterialCommunityIcons name="arrow-right" size={12} color="#3B82F6"/>
                    <Text style={{ fontSize:11, color:'#3B82F6', fontWeight:'700' }}>£{Number(p.newSalary).toLocaleString()}</Text>
                    {p.newSalary > p.currentSalary && (
                      <Text style={{ fontSize:10, color:'#10B981', fontWeight:'700' }}>
                        +£{(p.newSalary - p.currentSalary).toLocaleString()}
                      </Text>
                    )}
                  </View>
                )}
                <View style={{ flexDirection:'row', gap:6, marginTop:8 }}>
                  <View style={{ backgroundColor:p.type==='Promotion'?'#ECFDF5':p.type==='Transfer'?'#EFF6FF':'#FEF9C3',
                    borderRadius:8, paddingHorizontal:8, paddingVertical:3 }}>
                    <Text style={{ fontSize:10, fontWeight:'700',
                      color:p.type==='Promotion'?'#059669':p.type==='Transfer'?'#3B82F6':'#D97706' }}>{p.type}</Text>
                  </View>
                  <View style={{ backgroundColor:p.status==='Approved'?'#ECFDF5':p.status==='Pending'?'#FFFBEB':'#F1F5F9',
                    borderRadius:8, paddingHorizontal:8, paddingVertical:3 }}>
                    <Text style={{ fontSize:10, fontWeight:'700',
                      color:p.status==='Approved'?'#059669':p.status==='Pending'?'#D97706':'#6B7280' }}>{p.status}</Text>
                  </View>
                </View>
              </View>
              <View style={{ alignItems:'flex-end' }}>
                <Text style={{ fontSize:11, color:'#9CA3AF' }}>{p.effectiveDate}</Text>
                {p.approvedBy && <Text style={{ fontSize:10, color:'#9CA3AF', marginTop:2 }}>By: {p.approvedBy}</Text>}
              </View>
            </View>
            {p.reason && <Text style={{ fontSize:12, color:'#374151', marginTop:10, lineHeight:18 }}>{p.reason}</Text>}
            {p.status === 'Pending' && (
              <View style={{ flexDirection:'row', gap:8, marginTop:12, paddingTop:10, borderTopWidth:1, borderTopColor:'#F8FAFC' }}>
                <TouchableOpacity style={{ flex:1, backgroundColor:'#ECFDF5', borderRadius:8, padding:8,
                  flexDirection:'row', justifyContent:'center', alignItems:'center', gap:4 }}
                  onPress={()=>{const upd={...employeeData,promotions:(employeeData.promotions||[]).map(x=>x.id===p.id?{...x,status:'Approved'}:x)};saveEmployeeData(upd);}}>
                  <MaterialCommunityIcons name="check" size={14} color="#059669"/>
                  <Text style={{ fontSize:12, color:'#059669', fontWeight:'700' }}>Approve</Text>
                </TouchableOpacity>
                <TouchableOpacity style={{ flex:1, backgroundColor:'#FEF2F2', borderRadius:8, padding:8,
                  flexDirection:'row', justifyContent:'center', alignItems:'center', gap:4 }}
                  onPress={()=>{const upd={...employeeData,promotions:(employeeData.promotions||[]).map(x=>x.id===p.id?{...x,status:'Rejected'}:x)};saveEmployeeData(upd);}}>
                  <MaterialCommunityIcons name="close" size={14} color="#EF4444"/>
                  <Text style={{ fontSize:12, color:'#EF4444', fontWeight:'700' }}>Reject</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        ))}
      </ScrollView>
    );
  };

  // ── PERFORMANCE TAB ─────────────────────────────────────────────────────────
  const renderPerformanceTab = () => {
    const reviews = employeeData.performanceReviews || [];
    const ratingColors = { 5:'#059669', 4:'#10B981', 3:'#F59E0B', 2:'#F97316', 1:'#EF4444' };
    const avgRating = reviews.length
      ? (reviews.reduce((s,r)=>s+(parseInt(r.overallRating)||0),0)/reviews.length).toFixed(1) : '—';
    return (
      <ScrollView style={styles.tabContent} showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={loadEmployeeData}/>}>
        <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <Text style={styles.sectionTitle}>Performance Reviews ({reviews.length})</Text>
          <TouchableOpacity style={{ backgroundColor:'#8B5CF6', borderRadius:10, paddingHorizontal:14, paddingVertical:8,
            flexDirection:'row', alignItems:'center', gap:5 }}
            onPress={()=>setShowPerformanceModal(true)}>
            <MaterialCommunityIcons name="plus" size={16} color="#fff"/>
            <Text style={{ color:'#fff', fontWeight:'700', fontSize:12 }}>New Review</Text>
          </TouchableOpacity>
        </View>

        {/* Summary stats */}
        <View style={{ flexDirection:'row', gap:10, marginBottom:16 }}>
          {[
            { label:'Avg Rating', val:avgRating, color:'#F59E0B' },
            { label:'Completed', val:reviews.filter(r=>r.status==='Completed').length, color:'#10B981' },
            { label:'In Progress', val:reviews.filter(r=>r.status==='In Progress').length, color:'#3B82F6' },
            { label:'Pending', val:reviews.filter(r=>r.status==='Draft').length, color:'#9CA3AF' },
          ].map(m=>(
            <View key={m.label} style={{ flex:1, backgroundColor:'#fff', borderRadius:12, padding:12, alignItems:'center',
              borderTopWidth:3, borderTopColor:m.color, shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.05, shadowRadius:3, elevation:2 }}>
              <Text style={{ fontSize:20, fontWeight:'900', color:m.color }}>{m.val}</Text>
              <Text style={{ fontSize:10, color:'#6B7280', marginTop:2 }}>{m.label}</Text>
            </View>
          ))}
        </View>

        {/* Rating breakdown */}
        {reviews.length > 0 && (
          <View style={{ backgroundColor:'#fff', borderRadius:14, padding:16, marginBottom:14,
            shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.05, shadowRadius:4, elevation:2 }}>
            <Text style={{ fontSize:13, fontWeight:'800', color:'#374151', marginBottom:12 }}>Rating Distribution</Text>
            {[5,4,3,2,1].map(star=>{
              const cnt=reviews.filter(r=>parseInt(r.overallRating)===star).length;
              const pct=reviews.length?Math.round(cnt/reviews.length*100):0;
              return (
                <View key={star} style={{ flexDirection:'row', alignItems:'center', marginBottom:6 }}>
                  <View style={{ flexDirection:'row', gap:1, width:70 }}>
                    <Text>{[1,2,3,4,5].map(s=><MaterialCommunityIcons key={s} name={s<=star?'star':'star-outline'} size={12} color="#F59E0B"/>)}</Text>
                  </View>
                  <View style={{ flex:1, height:8, backgroundColor:'#F1F5F9', borderRadius:4, overflow:'hidden', marginHorizontal:8 }}>
                    <View style={{ width:`${pct}%`, height:'100%', backgroundColor:ratingColors[star], borderRadius:4 }}/>
                  </View>
                  <Text style={{ fontSize:11, color:'#6B7280', width:28, textAlign:'right' }}>{cnt}</Text>
                </View>
              );
            })}
          </View>
        )}

        {reviews.length === 0 ? (
          <View style={{ alignItems:'center', paddingVertical:60 }}>
            <MaterialCommunityIcons name="chart-line" size={56} color="#E5E7EB"/>
            <Text style={{ color:'#9CA3AF', marginTop:12, fontSize:15 }}>No performance reviews yet</Text>
            <TouchableOpacity style={{ backgroundColor:'#8B5CF6', borderRadius:12, paddingHorizontal:20, paddingVertical:11, marginTop:14,
              flexDirection:'row', gap:6, alignItems:'center' }} onPress={()=>setShowPerformanceModal(true)}>
              <MaterialCommunityIcons name="plus" size={16} color="#fff"/>
              <Text style={{ color:'#fff', fontWeight:'700' }}>Start First Review</Text>
            </TouchableOpacity>
          </View>
        ) : reviews.map((r,i)=>(
          <View key={r.id||i} style={{ backgroundColor:'#fff', borderRadius:14, padding:16, marginBottom:10,
            shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.05, shadowRadius:4, elevation:2 }}>
            <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'flex-start' }}>
              <View style={{ flex:1 }}>
                <Text style={{ fontSize:15, fontWeight:'800', color:'#111827' }}>{r.employeeName}</Text>
                <Text style={{ fontSize:12, color:'#6B7280', marginTop:2 }}>{r.reviewPeriod} · {r.reviewer}</Text>
                <View style={{ flexDirection:'row', gap:6, marginTop:8 }}>
                  <View style={{ backgroundColor:(ratingColors[parseInt(r.overallRating)]||'#9CA3AF')+'20',
                    borderRadius:8, paddingHorizontal:10, paddingVertical:4 }}>
                    <Text style={{ fontSize:11, fontWeight:'700', color:ratingColors[parseInt(r.overallRating)]||'#9CA3AF' }}>
                      Rating: {r.overallRating}/5
                    </Text>
                  </View>
                  <View style={{ backgroundColor:r.status==='Completed'?'#ECFDF5':'#EFF6FF',
                    borderRadius:8, paddingHorizontal:10, paddingVertical:4 }}>
                    <Text style={{ fontSize:11, fontWeight:'700',
                      color:r.status==='Completed'?'#059669':'#3B82F6' }}>{r.status}</Text>
                  </View>
                </View>
              </View>
              <View style={{ alignItems:'flex-end' }}>
                {[5,4,3,2,1].filter(s=>s<=parseInt(r.overallRating)).length > 0 && (
                  <View style={{ flexDirection:'row' }}>
                    <Text>{[1,2,3,4,5].map(s=><MaterialCommunityIcons key={s} name={s<=parseInt(r.overallRating)?'star':'star-outline'} size={14} color="#F59E0B"/>)}</Text>
                  </View>
                )}
                <Text style={{ fontSize:11, color:'#9CA3AF', marginTop:4 }}>{r.reviewDate}</Text>
              </View>
            </View>
            {r.achievements && <View style={{ marginTop:10, padding:10, backgroundColor:'#F0FDF4', borderRadius:8 }}>
              <Text style={{ fontSize:11, fontWeight:'700', color:'#059669' }}>Achievements</Text>
              <Text style={{ fontSize:12, color:'#374151', marginTop:3, lineHeight:17 }}>{r.achievements}</Text>
            </View>}
            {r.areasForImprovement && <View style={{ marginTop:8, padding:10, backgroundColor:'#FEF9C3', borderRadius:8 }}>
              <Text style={{ fontSize:11, fontWeight:'700', color:'#D97706' }}>Areas for Improvement</Text>
              <Text style={{ fontSize:12, color:'#374151', marginTop:3, lineHeight:17 }}>{r.areasForImprovement}</Text>
            </View>}
            {/* Skill ratings */}
            {r.ratings && (
              <View style={{ marginTop:12, paddingTop:10, borderTopWidth:1, borderTopColor:'#F8FAFC' }}>
                <Text style={{ fontSize:11, fontWeight:'800', color:'#9CA3AF', marginBottom:8 }}>SKILL RATINGS</Text>
                <View style={{ flexDirection:'row', flexWrap:'wrap', gap:8 }}>
                  {Object.entries(r.ratings).map(([key, val]) => (
                    <View key={key} style={{ backgroundColor:'#F9FAFB', borderRadius:8, padding:8, minWidth:90 }}>
                      <Text style={{ fontSize:10, color:'#6B7280', textTransform:'capitalize' }}>{key}</Text>
                      <View style={{ flexDirection:'row', marginTop:3 }}>
                        <Text>{[1,2,3,4,5].map(s=><MaterialCommunityIcons key={s} name={s<=parseInt(val)?'star':'star-outline'} size={11} color="#F59E0B"/>)}</Text>
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            )}
          </View>
        ))}
      </ScrollView>
    );
  };

  // ── CODES TAB ───────────────────────────────────────────────────────────────
  const renderCodesTab = () => {
    const codes = employeeData.employeeCodes || [];
    return (
      <ScrollView style={styles.tabContent} showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={loadEmployeeData}/>}>
        <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <Text style={styles.sectionTitle}>Employee Codes ({codes.length})</Text>
          <TouchableOpacity style={{ backgroundColor:'#06B6D4', borderRadius:10, paddingHorizontal:14, paddingVertical:8,
            flexDirection:'row', alignItems:'center', gap:5 }}
            onPress={()=>setShowEmployeeCodeModal(true)}>
            <MaterialCommunityIcons name="plus" size={16} color="#fff"/>
            <Text style={{ color:'#fff', fontWeight:'700', fontSize:12 }}>New Code</Text>
          </TouchableOpacity>
        </View>

        {/* Category breakdown */}
        {codes.length > 0 && (() => {
          const cats = [...new Set(codes.map(c=>c.category))];
          return (
            <ScrollView horizontal showsHorizontalScrollIndicator={false}
              contentContainerStyle={{ gap:8, marginBottom:16 }}>
              {cats.map(cat=>{
                const cnt = codes.filter(c=>c.category===cat).length;
                return (
                  <View key={cat} style={{ backgroundColor:'#EFF6FF', borderRadius:12, paddingHorizontal:14, paddingVertical:8,
                    flexDirection:'row', alignItems:'center', gap:6 }}>
                    <MaterialCommunityIcons name="tag" size={14} color="#3B82F6"/>
                    <Text style={{ fontSize:12, color:'#3B82F6', fontWeight:'600' }}>{cat}</Text>
                    <View style={{ backgroundColor:'#3B82F6', borderRadius:10, paddingHorizontal:6, paddingVertical:1 }}>
                      <Text style={{ fontSize:10, color:'#fff', fontWeight:'700' }}>{cnt}</Text>
                    </View>
                  </View>
                );
              })}
            </ScrollView>
          );
        })()}

        {codes.length === 0 ? (
          <View style={{ alignItems:'center', paddingVertical:60 }}>
            <MaterialCommunityIcons name="code-tags" size={56} color="#E5E7EB"/>
            <Text style={{ color:'#9CA3AF', marginTop:12, fontSize:15 }}>No employee codes defined</Text>
            <TouchableOpacity style={{ backgroundColor:'#06B6D4', borderRadius:12, paddingHorizontal:20, paddingVertical:11, marginTop:14,
              flexDirection:'row', gap:6, alignItems:'center' }} onPress={()=>setShowEmployeeCodeModal(true)}>
              <MaterialCommunityIcons name="plus" size={16} color="#fff"/>
              <Text style={{ color:'#fff', fontWeight:'700' }}>Create First Code</Text>
            </TouchableOpacity>
          </View>
        ) : codes.map((c,i)=>{
          const empCount = (employeeData.employees||[]).filter(e=>e.employeeCode===c.code).length;
          return (
            <View key={c.id||i} style={{ backgroundColor:'#fff', borderRadius:14, padding:16, marginBottom:10,
              borderLeftWidth:4, borderLeftColor:c.color||'#06B6D4',
              shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.05, shadowRadius:4, elevation:2 }}>
              <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'flex-start' }}>
                <View style={{ flex:1 }}>
                  <View style={{ flexDirection:'row', alignItems:'center', gap:10 }}>
                    <View style={{ backgroundColor:c.color||'#06B6D4', borderRadius:8, paddingHorizontal:10, paddingVertical:4 }}>
                      <Text style={{ color:'#fff', fontSize:12, fontWeight:'900' }}>{c.code}</Text>
                    </View>
                    <Text style={{ fontSize:15, fontWeight:'800', color:'#111827' }}>{c.name}</Text>
                  </View>
                  <Text style={{ fontSize:12, color:'#6B7280', marginTop:6, lineHeight:17 }}>{c.description}</Text>
                  <View style={{ flexDirection:'row', gap:8, marginTop:8 }}>
                    <View style={{ backgroundColor:'#F3F4F6', borderRadius:8, paddingHorizontal:8, paddingVertical:3 }}>
                      <Text style={{ fontSize:10, color:'#374151', fontWeight:'600' }}>{c.category}</Text>
                    </View>
                    <View style={{ backgroundColor:'#EFF6FF', borderRadius:8, paddingHorizontal:8, paddingVertical:3 }}>
                      <Text style={{ fontSize:10, color:'#3B82F6', fontWeight:'600' }}>{empCount} employees</Text>
                    </View>
                    <View style={{ backgroundColor:c.isActive?'#ECFDF5':'#F1F5F9', borderRadius:8, paddingHorizontal:8, paddingVertical:3 }}>
                      <Text style={{ fontSize:10, fontWeight:'600', color:c.isActive?'#059669':'#9CA3AF' }}>
                        <Text>{c.isActive ? 'Active' : 'Inactive'}</Text>
                      </Text>
                    </View>
                  </View>
                  {c.benefits && <View style={{ marginTop:10, padding:10, backgroundColor:'#F0FDF4', borderRadius:8 }}>
                    <Text style={{ fontSize:11, fontWeight:'700', color:'#059669' }}>Benefits</Text>
                    <Text style={{ fontSize:12, color:'#374151', marginTop:2 }}>{c.benefits}</Text>
                  </View>}
                  {c.requirements && <View style={{ marginTop:6, padding:10, backgroundColor:'#EFF6FF', borderRadius:8 }}>
                    <Text style={{ fontSize:11, fontWeight:'700', color:'#3B82F6' }}>Requirements</Text>
                    <Text style={{ fontSize:12, color:'#374151', marginTop:2 }}>{c.requirements}</Text>
                  </View>}
                </View>
                <TouchableOpacity onPress={()=>{const upd={...employeeData,employeeCodes:(employeeData.employeeCodes||[]).filter(x=>x.id!==c.id)};saveEmployeeData(upd);}}>
                  <MaterialCommunityIcons name="delete-outline" size={20} color="#EF4444"/>
                </TouchableOpacity>
              </View>
            </View>
          );
        })}
      </ScrollView>
    );
  };

  // ── ANALYTICS TAB ───────────────────────────────────────────────────────────
  const renderAnalyticsTab = () => {
    const emps = employeeData.employees || [];
    const analytics = employeeData.analytics || calculateAnalytics(emps);
    const cost = calculateTotalPayrollCost();
    const deptDist = analytics.departmentDistribution || {};
    const statusDist = analytics.statusDistribution || {};
    const tenureDist = analytics.tenureDistribution || {};
    const salaryDist = analytics.salaryDistribution || {};
    const totalEmps = emps.length || 1;

    const topSkills = (() => {
      const cnt = {};
      emps.forEach(e=>(e.skills||[]).forEach(s=>{cnt[s]=(cnt[s]||0)+1;}));
      return Object.entries(cnt).sort(([,a],[,b])=>b-a).slice(0,8);
    })();

    const avgRating = emps.length
      ? (emps.reduce((s,e)=>s+(e.performanceRating||3),0)/emps.length).toFixed(1) : '—';

    return (
      <ScrollView style={styles.tabContent} showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={loadEmployeeData}/>}>
        <Text style={styles.sectionTitle}>Analytics & Insights</Text>

        {/* Payroll cost card */}
        <View style={{ backgroundColor:'#0E4E96', borderRadius:16, padding:18, marginBottom:16 }}>
          <Text style={{ color:'rgba(255,255,255,0.7)', fontSize:12, marginBottom:4 }}>Monthly Payroll Cost</Text>
          <Text style={{ color:'#fff', fontSize:28, fontWeight:'900' }}>£{Math.round(cost.monthly).toLocaleString()}</Text>
          <View style={{ flexDirection:'row', justifyContent:'space-between', marginTop:10 }}>
            <View><Text style={{ color:'rgba(255,255,255,0.7)', fontSize:11 }}>Annual</Text><Text style={{ color:'#fff', fontWeight:'700' }}>£{Math.round(cost.annual).toLocaleString()}</Text></View>
            <View><Text style={{ color:'rgba(255,255,255,0.7)', fontSize:11 }}>Per Employee</Text><Text style={{ color:'#fff', fontWeight:'700' }}>£{cost.count?Math.round(cost.monthly/cost.count).toLocaleString():0}/mo</Text></View>
            <View><Text style={{ color:'rgba(255,255,255,0.7)', fontSize:11 }}>Avg Salary</Text><Text style={{ color:'#fff', fontWeight:'700' }}>£{Math.round(salaryDist.average||0).toLocaleString()}</Text></View>
          </View>
        </View>

        {/* Key metrics */}
        <View style={{ flexDirection:'row', flexWrap:'wrap', gap:10, marginBottom:16 }}>
          {[
            { label:'Avg Performance', val:`${avgRating}/5 ⭐`, color:'#F59E0B' },
            { label:'Avg Salary', val:`£${Math.round(salaryDist.average||0).toLocaleString()}`, color:'#3B82F6' },
            { label:'Active Rate', val:`${totalEmps?Math.round((statusDist['Active']||0)/totalEmps*100):0}%`, color:'#10B981' },
            { label:'Turnover Rate', val:`${employeeData.overview?.turnoverRate||0}%`, color:'#EF4444' },
          ].map(m=>(
            <View key={m.label} style={{ width:(width-60)/2, backgroundColor:'#fff', borderRadius:12, padding:14,
              borderTopWidth:3, borderTopColor:m.color, shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.05, shadowRadius:3, elevation:2 }}>
              <Text style={{ fontSize:18, fontWeight:'900', color:m.color }}>{m.val}</Text>
              <Text style={{ fontSize:11, color:'#6B7280', marginTop:3 }}>{m.label}</Text>
            </View>
          ))}
        </View>

        {/* Department breakdown */}
        <View style={{ backgroundColor:'#fff', borderRadius:14, padding:16, marginBottom:14,
          shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.05, shadowRadius:4, elevation:2 }}>
          <Text style={{ fontSize:14, fontWeight:'800', color:'#111827', marginBottom:12 }}>Department Distribution</Text>
          {Object.entries(deptDist).sort(([,a],[,b])=>b-a).map(([dept,cnt])=>{
            const pct = Math.round(cnt/totalEmps*100);
            const colors = ['#3B82F6','#10B981','#8B5CF6','#F59E0B','#EF4444','#0EA5E9','#EC4899','#14B8A6'];
            const col = colors[Object.keys(deptDist).indexOf(dept)%colors.length];
            return (
              <View key={dept} style={{ marginBottom:10 }}>
                <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:4 }}>
                  <Text style={{ fontSize:12, color:'#374151', fontWeight:'600' }}>{dept}</Text>
                  <Text style={{ fontSize:12, fontWeight:'700', color:col }}>{cnt} ({pct}%)</Text>
                </View>
                <View style={{ height:8, backgroundColor:'#F1F5F9', borderRadius:4, overflow:'hidden' }}>
                  <View style={{ width:`${pct}%`, height:'100%', backgroundColor:col, borderRadius:4 }}/>
                </View>
              </View>
            );
          })}
        </View>

        {/* Status distribution */}
        <View style={{ backgroundColor:'#fff', borderRadius:14, padding:16, marginBottom:14,
          shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.05, shadowRadius:4, elevation:2 }}>
          <Text style={{ fontSize:14, fontWeight:'800', color:'#111827', marginBottom:12 }}>Status Distribution</Text>
          <View style={{ flexDirection:'row', flexWrap:'wrap', gap:10 }}>
            {Object.entries(statusDist).map(([status,cnt])=>{
              const statusColors = { Active:'#10B981', Inactive:'#9CA3AF', 'On Leave':'#F59E0B', Terminated:'#EF4444', Probation:'#8B5CF6' };
              const col = statusColors[status]||'#6B7280';
              return (
                <View key={status} style={{ backgroundColor:col+'15', borderRadius:10, padding:12,
                  borderWidth:1, borderColor:col+'30', alignItems:'center', minWidth:80 }}>
                  <Text style={{ fontSize:20, fontWeight:'900', color:col }}>{cnt}</Text>
                  <Text style={{ fontSize:10, color:col, fontWeight:'600', marginTop:2 }}>{status}</Text>
                </View>
              );
            })}
          </View>
        </View>

        {/* Tenure */}
        <View style={{ backgroundColor:'#fff', borderRadius:14, padding:16, marginBottom:14,
          shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.05, shadowRadius:4, elevation:2 }}>
          <Text style={{ fontSize:14, fontWeight:'800', color:'#111827', marginBottom:12 }}>Tenure Distribution</Text>
          {Object.entries(tenureDist).map(([label,cnt])=>{
            const pct = Math.round(cnt/totalEmps*100);
            return (
              <View key={label} style={{ flexDirection:'row', alignItems:'center', marginBottom:8 }}>
                <Text style={{ width:90, fontSize:11, color:'#6B7280' }}>{label}</Text>
                <View style={{ flex:1, height:7, backgroundColor:'#F1F5F9', borderRadius:4, overflow:'hidden', marginHorizontal:8 }}>
                  <View style={{ width:`${pct}%`, height:'100%', backgroundColor:'#0E4E96', borderRadius:4 }}/>
                </View>
                <Text style={{ fontSize:11, fontWeight:'700', color:'#0E4E96', width:30, textAlign:'right' }}>{cnt}</Text>
              </View>
            );
          })}
        </View>

        {/* Top skills */}
        {topSkills.length > 0 && (
          <View style={{ backgroundColor:'#fff', borderRadius:14, padding:16, marginBottom:14,
            shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.05, shadowRadius:4, elevation:2 }}>
            <Text style={{ fontSize:14, fontWeight:'800', color:'#111827', marginBottom:12 }}>Top Skills Across Team</Text>
            {topSkills.map(([skill,cnt],i)=>(
              <View key={skill} style={{ flexDirection:'row', alignItems:'center', marginBottom:8 }}>
                <Text style={{ width:16, fontSize:11, color:'#9CA3AF' }}>{i+1}</Text>
                <Text style={{ flex:1, fontSize:12, color:'#374151', fontWeight:'600', marginLeft:6 }}>{skill}</Text>
                <View style={{ width:80, height:5, backgroundColor:'#F1F5F9', borderRadius:3, overflow:'hidden', marginRight:8 }}>
                  <View style={{ width:`${cnt/topSkills[0][1]*100}%`, height:'100%', backgroundColor:'#0EA5E9', borderRadius:3 }}/>
                </View>
                <Text style={{ fontSize:11, fontWeight:'700', color:'#0EA5E9', width:22, textAlign:'right' }}>{cnt}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Export button */}
        <TouchableOpacity style={{ backgroundColor:'#0E4E96', borderRadius:12, padding:14,
          flexDirection:'row', justifyContent:'center', gap:8, alignItems:'center', marginBottom:24 }}
          onPress={()=>setShowExportModal(true)}>
          <MaterialCommunityIcons name="export" size={18} color="#fff"/>
          <Text style={{ color:'#fff', fontWeight:'800', fontSize:14 }}>Export Analytics Report</Text>
        </TouchableOpacity>
      </ScrollView>
    );
  };



  // ── Password Gate Screen ──
  if (screenPasswordChecked && screenHasPassword && !screenPasswordVerified) {
    const isLocked = screenLockoutUntil && Date.now() < screenLockoutUntil;
    const lockMins = isLocked ? Math.ceil((screenLockoutUntil - Date.now()) / 60000) : 0;

    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F8F9FA' }}>
        <StatusBar barStyle="dark-content" />
        <LinearGradient colors={['#0e4e96', '#1e6bc2']} style={{ paddingVertical: 24, paddingHorizontal: 20, alignItems: 'center' }}>
          <TouchableOpacity onPress={() => navigation?.goBack?.()} style={{ position: 'absolute', left: 16, top: 24 }}>
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <View style={{ width: 60, height: 60, borderRadius: 30, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
            <Ionicons name="lock-closed" size={30} color="#fff" />
          </View>
          <Text style={{ fontSize: 20, fontWeight: 'bold', color: '#fff' }}>Password Required</Text>
          <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 4 }}>
            Employee Management is protected by admin password
          </Text>
        </LinearGradient>

        <View style={{ padding: 24 }}>
          {isLocked ? (
            <View style={{ alignItems: 'center', paddingVertical: 30 }}>
              <Ionicons name="clock-outline" size={48} color="#EF4444" />
              <Text style={{ fontSize: 17, fontWeight: 'bold', color: '#EF4444', marginTop: 12 }}>Locked</Text>
              <Text style={{ fontSize: 14, color: '#64748B', marginTop: 6 }}>Try again in {lockMins} min{lockMins > 1 ? 's' : ''}.</Text>
              <TouchableOpacity onPress={() => navigation?.goBack?.()}
                style={{ marginTop: 20, backgroundColor: '#0e4e96', borderRadius: 12, paddingVertical: 13, paddingHorizontal: 28 }}>
                <Text style={{ color: '#fff', fontWeight: '600' }}>Go Back</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <>
              <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12, borderWidth: 1, borderColor: screenPasswordError ? '#EF4444' : '#E2E8F0', paddingHorizontal: 14, marginBottom: 12 }}>
                <Ionicons name="lock-closed" size={18} color={screenPasswordError ? '#EF4444' : '#94A3B8'} />
                <TextInput
                  style={{ flex: 1, paddingVertical: 14, paddingHorizontal: 10, fontSize: 16, color: '#222' }}
                  placeholder="Enter admin password"
                  placeholderTextColor="#94A3B8"
                  value={screenPassword}
                  onChangeText={(t) => { setScreenPassword(t); setScreenPasswordError(''); }}
                  secureTextEntry={!showScreenPassword}
                  autoFocus
                  onSubmitEditing={verifyScreenPassword}
                  returnKeyType="go"
                />
                <TouchableOpacity onPress={() => setShowScreenPassword(v => !v)}>
                  <Ionicons name={showScreenPassword ? 'eye-off' : 'eye'} size={20} color="#94A3B8" />
                </TouchableOpacity>
              </View>

              {screenPasswordError ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 12, backgroundColor: '#FEF2F2', padding: 10, borderRadius: 10 }}>
                  <Ionicons name="alert-circle-outline" size={16} color="#EF4444" />
                  <Text style={{ fontSize: 12, color: '#EF4444', flex: 1 }}>{screenPasswordError}</Text>
                </View>
              ) : null}

              {screenPasswordAttempts > 0 && (
                <Text style={{ fontSize: 11, color: '#94A3B8', textAlign: 'center', marginBottom: 10 }}>
                  {SCREEN_MAX_ATTEMPTS - screenPasswordAttempts} attempt{SCREEN_MAX_ATTEMPTS - screenPasswordAttempts > 1 ? 's' : ''} remaining
                </Text>
              )}

              <TouchableOpacity onPress={verifyScreenPassword}
                style={{ backgroundColor: '#0e4e96', borderRadius: 12, paddingVertical: 15, alignItems: 'center', marginBottom: 10 }}>
                <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 16 }}>Unlock</Text>
              </TouchableOpacity>

              <TouchableOpacity onPress={() => navigation?.goBack?.()}
                style={{ borderRadius: 12, paddingVertical: 13, alignItems: 'center', borderWidth: 1, borderColor: '#E2E8F0' }}>
                <Text style={{ color: '#64748B', fontWeight: '600' }}>Go Back</Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </SafeAreaView>
    );
  }

  // ── Auth Gate ──
  if (authChecked && !isAdminUser) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F8F9FA' }}>
        <StatusBar barStyle="dark-content" />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 30 }}>
          <View style={{ width: 80, height: 80, borderRadius: 40, backgroundColor: '#EF444420', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
            <Ionicons name="lock-closed" size={40} color="#EF4444" />
          </View>
          <Text style={{ fontSize: 22, fontWeight: 'bold', color: '#1a1a2e', marginBottom: 8 }}>Access Restricted</Text>
          <Text style={{ fontSize: 15, color: '#64748B', textAlign: 'center', lineHeight: 22, marginBottom: 20 }}>
            Employee Management is restricted to administrators, HR managers, and business owners.
          </Text>
          <View style={{ flexDirection: 'row', gap: 12 }}>
            <TouchableOpacity onPress={() => navigation?.goBack?.()}
              style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#0e4e96', borderRadius: 12, paddingVertical: 14, paddingHorizontal: 24, gap: 8 }}>
              <Ionicons name="arrow-back" size={18} color="#fff" />
              <Text style={{ color: '#fff', fontWeight: '600', fontSize: 15 }}>Go Back</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => { setAuthChecked(false); checkAdminStatus(); }}
              style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#F0F4FF', borderRadius: 12, paddingVertical: 14, paddingHorizontal: 24, gap: 8, borderWidth: 1, borderColor: '#0e4e96' }}>
              <Ionicons name="refresh" size={18} color="#0e4e96" />
              <Text style={{ color: '#0e4e96', fontWeight: '600', fontSize: 15 }}>Retry</Text>
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    );
  }


  return (
    <SafeAreaView style={styles.container}>
      <LinearGradient colors={['#0e4e96', '#1e6bc2']} style={styles.header}>
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation?.goBack?.()}
          >
            <MaterialCommunityIcons name="arrow-left" size={24} color="#fff" />
          </TouchableOpacity>
          
          <View style={styles.headerTitleContainer}>
            {companyLogo ? (
                <TouchableOpacity onPress={() => navigation?.navigate('CompanyProfileScreen')}>
                  <Image source={{ uri: companyLogo || undefined }} style={{ width: 34, height: 34, borderRadius: 8 }} resizeMode="cover" />
                </TouchableOpacity>
              ) : null}
              <Text style={styles.headerTitle}>Employee Management</Text>
            <Text style={styles.headerSubtitle}>
              {employeeData.overview?.totalEmployees || 0} total employees
            </Text>
          </View>
          
          <View style={styles.headerActions}>
            <TouchableOpacity 
              style={styles.headerActionButton}
              onPress={() => setShowSettingsModal(true)}
            >
              <MaterialCommunityIcons name="cog" size={24} color="#fff" />
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.headerActionButton}
              onPress={createBackup}
            >
              <MaterialCommunityIcons name="backup-restore" size={24} color="#fff" />
            </TouchableOpacity>
          </View>
        </View>
      </LinearGradient>

      {/* Enhanced Tabs */}
      <View style={styles.tabsContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {tabs.map((tab) => (
            <TouchableOpacity
              key={tab.id}
              style={[styles.tab, activeTab === tab.id && styles.activeTab]}
              onPress={() => setActiveTab(tab.id)}
            >
              <MaterialCommunityIcons
                name={tab.icon}
                size={20}
                color={activeTab === tab.id ? tab.color : '#64748b'}
              />
              <Text style={[styles.tabText, activeTab === tab.id && styles.activeTabText]}>
                {tab.title}
              </Text>
              {activeTab === tab.id && (
                <View style={[styles.tabIndicator, { backgroundColor: tab.color }]} />
              )}
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Main Content */}
      {renderTabContent()}

      {/* Add Employee Modal — self-contained local state, no parent re-renders on typing */}
      <AddEmployeeModal
        visible={showAddEmployeeModal}
        isSubmitting={isSubmitting}
        existingCount={(employeeData.employees||[]).length}
        onClose={() => { setShowAddEmployeeModal(false); setFormErrors({}); }}
        onSave={async (formData) => {
          setIsSubmitting(true);
          try {
            const isEmailUnique = !(employeeData.employees||[]).find(
              e => e.email?.toLowerCase() === formData.email?.toLowerCase()
            );
            if (!isEmailUnique) {
              Alert.alert('Duplicate', 'An employee with this email already exists.');
              setIsSubmitting(false); return;
            }
            const employee = {
              ...formData,
              id: `emp_${Date.now()}`,
              warningCount: 0, promotionCount: 0, lastReviewDate: '',
              performanceRating: 3, attendanceRate: 100, leaveBalance: 25,
              createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
            };
            const updatedData = {
              ...employeeData,
              employees: [...(employeeData.employees||[]), employee],
              overview: { ...employeeData.overview,
                totalEmployees: (employeeData.overview?.totalEmployees||0) + 1,
                activeEmployees: (employeeData.overview?.activeEmployees||0) + (employee.status === 'Active' ? 1 : 0),
                newHires: (employeeData.overview?.newHires||0) + 1,
              },
            };
            updatedData.analytics = calculateAnalytics(updatedData.employees);
            await saveEmployeeData(updatedData);
            try {
              // Build server payload — map camelCase form fields → snake_case DB columns
              const rawName = (employee.name || '').trim();
              const nameParts = rawName.split(/\s+/);
              const first_name = nameParts[0] || rawName || 'Unknown';
              const last_name  = nameParts.length > 1 ? nameParts.slice(1).join(' ') : first_name;

              // Get company_id from state, AsyncStorage, or default
              let cid = companyId;
              if (!cid) {
                const stored = await AsyncStorage.getItem('company_id').catch(() => null);
                cid = stored ? parseInt(stored) : 1;
              }

              const serverPayload = {
                first_name,
                last_name,
                email:           employee.email || `${first_name.toLowerCase()}.${Date.now()}@jobeng.internal`,
                phone:           employee.phone           || null,
                department:      employee.department      || null,
                position:        employee.position        || null,
                hire_date:       employee.startDate       || employee.hireDate || null,
                status:          (employee.status || 'Active').toLowerCase().replace(' ', '_'),
                employment_type: (employee.employeeCode   || employee.employmentType || 'full_time').toLowerCase(),
                salary:          employee.salary ? parseFloat(employee.salary) : null,
                notes:           employee.notes            || null,
                profile_image:   employee.avatar && !employee.avatar.includes('ui-avatars') ? employee.avatar : null,
                company_id:      cid ? parseInt(cid) : null,
                company_id:     cid ? parseInt(cid) : null,
                manager:         employee.manager          || null,
                ni_number:       employee.niNumber         || employee.nationalId || null,
                national_id:     employee.nationalId       || null,
                bank_account:    employee.bankAccount      || null,
                address:         employee.address          || null,
              };

              console.log('[AddEmployee] Posting to server:', JSON.stringify(serverPayload));
              const res = await ManageAPI.create(serverPayload);
              
              if (res?.error && !res?.fromCache) {
                // Server rejected — log but don't block (already saved locally)
                console.warn('[AddEmployee] Server error:', res.error?.message || res.error);
                // If email duplicate error, show user
                if (res.error?.message?.includes('email') || res.error?.message?.includes('23505')) {
                  Alert.alert('Note', 'Employee saved locally. Server reported a duplicate email — the employee already exists on the server.');
                }
              } else {
                const saved = res?.data?.data ?? res?.data ?? res?.data;
                const serverId = saved?.id;
                if (serverId && String(serverId) !== String(employee.id)) {
                  const withServerId = (updatedData.employees||[]).map(e =>
                    e.id === employee.id ? { ...e, id: serverId, serverId: serverId } : e
                  );
                  await saveEmployeeData({ ...updatedData, employees: withServerId });
                  console.log('[AddEmployee] Server ID assigned:', serverId);
                }
                PayrollIntegrationAPI.syncEmployee(serverId ?? employee.id).catch(() => {});
              }
            } catch (apiErr) {
              console.warn('[AddEmployee] POST failed (saved locally):', apiErr.message);
            }
            // Upload photo to server if user picked a local file
            if (employee.avatar && !employee.avatar.startsWith('http')) {
              try {
                const empId = employee.id;
                const tok = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
                await fetch(`https://api.jobeng.ai/api/employees/${empId}`, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
                    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
                  },
                  body: JSON.stringify({ profile_image: employee.avatar }),
                });
              } catch (_) { /* photo upload non-blocking */ }
            }
            await syncEmployeeToPayroll(employee);
            setShowAddEmployeeModal(false);
            setFormErrors({});
            Alert.alert('✅ Added', `${employee.name} added successfully`, [
              { text: 'OK', onPress: () => setActiveTab('employees') }
            ]);
          } catch (err) {
            Alert.alert('Error', 'Failed to add employee');
          } finally { setIsSubmitting(false); }
        }}
      />

      {/* Bulk Actions Modal */}
      <Modal
        visible={showBulkActionsModal}
        animationType="slide"
        transparent={true}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                Bulk Actions ({selectedEmployees.length} employees)
              </Text>
              <TouchableOpacity onPress={() => setShowBulkActionsModal(false)}>
                <MaterialCommunityIcons name="close" size={24} color="#64748b" />
              </TouchableOpacity>
            </View>
            
            <Text style={styles.modalText}>
              Choose an action to apply to all selected employees:
            </Text>
            
            <View style={styles.bulkActionOptions}>
              <TouchableOpacity
                style={styles.bulkActionOption}
                onPress={() => bulkUpdateEmployees({ status: EMPLOYEE_STATUS.ACTIVE })}
              >
                <MaterialCommunityIcons name="account-check" size={24} color="#10b981" />
                <View style={styles.bulkActionTextContainer}>
                  <Text style={styles.bulkActionOptionText}>Set as Active</Text>
                  <Text style={styles.bulkActionOptionSubtext}>Mark selected employees as active</Text>
                </View>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={styles.bulkActionOption}
                onPress={() => bulkUpdateEmployees({ status: EMPLOYEE_STATUS.INACTIVE })}
              >
                <MaterialCommunityIcons name="account-off" size={24} color="#64748b" />
                <View style={styles.bulkActionTextContainer}>
                  <Text style={styles.bulkActionOptionText}>Set as Inactive</Text>
                  <Text style={styles.bulkActionOptionSubtext}>Mark selected employees as inactive</Text>
                </View>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={styles.bulkActionOption}
                onPress={() => bulkUpdateEmployees({ department: 'HR' })}
              >
                <MaterialCommunityIcons name="swap-horizontal" size={24} color="#0e4e96" />
                <View style={styles.bulkActionTextContainer}>
                  <Text style={styles.bulkActionOptionText}>Move to HR Department</Text>
                  <Text style={styles.bulkActionOptionSubtext}>Transfer to Human Resources</Text>
                </View>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={styles.bulkActionOption}
                onPress={() => can('bulk_actions') ? bulkDeleteEmployees() : Alert.alert('Restricted', 'Bulk actions are restricted.')}
              >
                <MaterialCommunityIcons name="delete" size={24} color="#dc2626" />
                <View style={styles.bulkActionTextContainer}>
                  <Text style={styles.bulkActionOptionText}>Delete Employees</Text>
                  <Text style={styles.bulkActionOptionSubtext}>Permanently remove selected employees</Text>
                </View>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Export Modal */}
      <Modal
        visible={showExportModal}
        animationType="slide"
        transparent={true}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Export Employee Data</Text>
              <TouchableOpacity onPress={() => setShowExportModal(false)}>
                <MaterialCommunityIcons name="close" size={24} color="#64748b" />
              </TouchableOpacity>
            </View>
            
            <Text style={styles.modalText}>
              Choose export format for {getFilteredEmployees.length} employees:
            </Text>
            
            <View style={styles.exportOptions}>
              <TouchableOpacity
                style={styles.exportOption}
                onPress={() => {
                  exportEmployeeData('csv');
                  setShowExportModal(false);
                }}
              >
                <MaterialCommunityIcons name="file-delimited" size={32} color="#10b981" />
                <Text style={styles.exportOptionText}>CSV Format</Text>
                <Text style={styles.exportOptionSubtext}>Spreadsheet compatible</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={styles.exportOption}
                onPress={() => {
                  exportEmployeeData('json');
                  setShowExportModal(false);
                }}
              >
                <MaterialCommunityIcons name="code-json" size={32} color="#0e4e96" />
                <Text style={styles.exportOptionText}>JSON Format</Text>
                <Text style={styles.exportOptionSubtext}>Developer friendly</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Warning Letter Modal */}
      <Modal visible={showWarningLetterModal} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity style={styles.modalCloseButton} onPress={()=>setShowWarningLetterModal(false)}><MaterialCommunityIcons name="close" size={24} color="#64748b"/></TouchableOpacity>
            <Text style={styles.modalTitle}>Issue Warning Letter</Text>
            <TouchableOpacity style={[styles.saveButton,{backgroundColor:'#DC2626'},isSubmitting&&styles.saveButtonDisabled]} onPress={saveWarningLetter} disabled={isSubmitting}>{isSubmitting?<ActivityIndicator size={16} color="#fff"/>:<Text style={styles.saveButtonText}>Issue</Text>}</TouchableOpacity>
          </View>
          <KeyboardAvoidingView style={styles.formContainer} behavior={Platform.OS==='ios'?'padding':'height'}>
            <ScrollView style={styles.formScrollView} showsVerticalScrollIndicator={false}>
              <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',textTransform:'uppercase',marginBottom:8}}>SELECT EMPLOYEE *</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{gap:8,marginBottom:16}}>
                {(employeeData.employees||[]).slice(0,15).map(e=>(
                  <TouchableOpacity key={e.id} onPress={()=>setWarningLetter(p=>({...p,employeeId:e.id||e.employeeId,employeeName:e.name}))}
                    style={{paddingHorizontal:12,paddingVertical:8,borderRadius:12,backgroundColor:warningLetter.employeeName===e.name?'#DC2626':'#F3F4F6',borderWidth:1,borderColor:warningLetter.employeeName===e.name?'#DC2626':'#E5E7EB'}}>
                    <Text style={{fontSize:12,color:warningLetter.employeeName===e.name?'#fff':'#374151',fontWeight:'600'}}>{e.name}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
              {[{label:'WARNING TYPE',opts:['Verbal Warning','Written Warning','Final Warning','Suspension','Termination Notice'],key:'type'},{label:'REASON',opts:['Attendance','Performance','Conduct','Policy Violation','Safety','Insubordination','Other'],key:'reason'},{label:'SEVERITY',opts:['Low','Medium','High','Critical'],key:'severity'}].map(f=>(
                <View key={f.key} style={{marginBottom:14}}>
                  <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',textTransform:'uppercase',marginBottom:8}}>{f.label}</Text>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{gap:6}}>
                    {f.opts.map(o=><TouchableOpacity key={o} onPress={()=>setWarningLetter(p=>({...p,[f.key]:o}))} style={{paddingHorizontal:12,paddingVertical:8,borderRadius:10,backgroundColor:warningLetter[f.key]===o?'#DC2626':'#F3F4F6',borderWidth:1,borderColor:warningLetter[f.key]===o?'#DC2626':'#E5E7EB'}}><Text style={{fontSize:12,color:warningLetter[f.key]===o?'#fff':'#6B7280',fontWeight:'600'}}>{o}</Text></TouchableOpacity>)}
                  </ScrollView>
                </View>
              ))}
              {[{label:'DESCRIPTION *',key:'description',ph:'Describe the incident…',multi:true},{label:'ISSUED BY',key:'issuedBy',ph:'HR Manager name'},{label:'ISSUED DATE',key:'issuedDate',ph:'YYYY-MM-DD'},{label:'FOLLOW-UP DATE',key:'followUpDate',ph:'YYYY-MM-DD'},{label:'NOTES',key:'notes',ph:'Additional notes…',multi:true}].map(f=>(
                <View key={f.key} style={{marginBottom:14}}>
                  <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',textTransform:'uppercase',marginBottom:6}}>{f.label}</Text>
                  <TextInput style={{backgroundColor:'#F9FAFB',borderWidth:1.5,borderColor:'#E5E7EB',borderRadius:12,paddingHorizontal:14,paddingVertical:11,fontSize:14,color:'#111827',...(f.multi?{minHeight:80,textAlignVertical:'top'}:{})}}
                    value={warningLetter[f.key]||''} onChangeText={v=>setWarningLetter(p=>({...p,[f.key]:v}))} placeholder={f.ph} placeholderTextColor="#D1D5DB" multiline={!!f.multi}/>
                </View>
              ))}
              <View style={{flexDirection:'row',alignItems:'center',justifyContent:'space-between',paddingVertical:10,marginBottom:20}}>
                <Text style={{fontSize:14,color:'#374151'}}>Acknowledgment Required</Text>
                <Switch value={warningLetter.acknowledgmentRequired} onValueChange={v=>setWarningLetter(p=>({...p,acknowledgmentRequired:v}))} trackColor={{false:'#D1D5DB',true:'#DC2626'}} thumbColor="#fff"/>
              </View>
            </ScrollView>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </Modal>

      {/* Promotion Modal */}
      <Modal visible={showPromotionModal} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity style={styles.modalCloseButton} onPress={()=>setShowPromotionModal(false)}><MaterialCommunityIcons name="close" size={24} color="#64748b"/></TouchableOpacity>
            <Text style={styles.modalTitle}>Promotion / Transfer</Text>
            <TouchableOpacity style={[styles.saveButton,{backgroundColor:'#F59E0B'},isSubmitting&&styles.saveButtonDisabled]} onPress={savePromotion} disabled={isSubmitting}>{isSubmitting?<ActivityIndicator size={16} color="#fff"/>:<Text style={styles.saveButtonText}>Save</Text>}</TouchableOpacity>
          </View>
          <KeyboardAvoidingView style={styles.formContainer} behavior={Platform.OS==='ios'?'padding':'height'}>
            <ScrollView style={styles.formScrollView} showsVerticalScrollIndicator={false}>
              <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',textTransform:'uppercase',marginBottom:8}}>SELECT EMPLOYEE *</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{gap:8,marginBottom:16}}>
                {(employeeData.employees||[]).slice(0,15).map(e=>(
                  <TouchableOpacity key={e.id} onPress={()=>setPromotion(p=>({...p,employeeId:e.id||e.employeeId,employeeName:e.name,currentPosition:e.position,currentDepartment:e.department,currentSalary:String(e.salary||0)}))}
                    style={{paddingHorizontal:12,paddingVertical:8,borderRadius:12,backgroundColor:promotion.employeeName===e.name?'#F59E0B':'#F3F4F6',borderWidth:1,borderColor:promotion.employeeName===e.name?'#F59E0B':'#E5E7EB'}}>
                    <Text style={{fontSize:12,color:promotion.employeeName===e.name?'#fff':'#374151',fontWeight:'600'}}>{e.name}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
              <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',textTransform:'uppercase',marginBottom:8}}>TYPE</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{gap:6,marginBottom:16}}>
                {['Promotion','Lateral Move','Demotion','Transfer','Role Change'].map(t=>(
                  <TouchableOpacity key={t} onPress={()=>setPromotion(p=>({...p,type:t}))} style={{paddingHorizontal:12,paddingVertical:8,borderRadius:10,backgroundColor:promotion.type===t?'#F59E0B':'#F3F4F6',borderWidth:1,borderColor:promotion.type===t?'#F59E0B':'#E5E7EB'}}>
                    <Text style={{fontSize:12,color:promotion.type===t?'#fff':'#6B7280',fontWeight:'600'}}>{t}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
              {[{label:'CURRENT POSITION',key:'currentPosition',ph:'Current role'},{label:'NEW POSITION *',key:'newPosition',ph:'New role title'},{label:'CURRENT DEPT',key:'currentDepartment',ph:'Current department'},{label:'NEW DEPT',key:'newDepartment',ph:'New department if moving'},{label:'CURRENT SALARY',key:'currentSalary',ph:'e.g. 50000',kb:'numeric'},{label:'NEW SALARY',key:'newSalary',ph:'e.g. 60000',kb:'numeric'},{label:'EFFECTIVE DATE',key:'effectiveDate',ph:'YYYY-MM-DD'},{label:'APPROVED BY',key:'approvedBy',ph:'Approver name'},{label:'REASON',key:'reason',ph:'Reason for change…'}].map(f=>(
                <View key={f.key} style={{marginBottom:12}}>
                  <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',textTransform:'uppercase',marginBottom:6}}>{f.label}</Text>
                  <TextInput style={{backgroundColor:'#F9FAFB',borderWidth:1.5,borderColor:'#E5E7EB',borderRadius:12,paddingHorizontal:14,paddingVertical:11,fontSize:14,color:'#111827'}}
                    value={promotion[f.key]||''} onChangeText={v=>setPromotion(p=>({...p,[f.key]:v}))} placeholder={f.ph} placeholderTextColor="#D1D5DB" keyboardType={f.kb||'default'}/>
                </View>
              ))}
              <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',textTransform:'uppercase',marginBottom:8}}>STATUS</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{gap:6,marginBottom:20}}>
                {['Pending','Approved','Rejected','Completed'].map(s=>(
                  <TouchableOpacity key={s} onPress={()=>setPromotion(p=>({...p,status:s}))} style={{paddingHorizontal:12,paddingVertical:8,borderRadius:10,backgroundColor:promotion.status===s?'#0E4E96':'#F3F4F6',borderWidth:1,borderColor:promotion.status===s?'#0E4E96':'#E5E7EB'}}>
                    <Text style={{fontSize:12,color:promotion.status===s?'#fff':'#6B7280',fontWeight:'600'}}>{s}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </ScrollView>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </Modal>

      {/* Performance Review Modal */}
      <Modal visible={showPerformanceModal} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity style={styles.modalCloseButton} onPress={()=>setShowPerformanceModal(false)}><MaterialCommunityIcons name="close" size={24} color="#64748b"/></TouchableOpacity>
            <Text style={styles.modalTitle}>Performance Review</Text>
            <TouchableOpacity style={[styles.saveButton,{backgroundColor:'#8B5CF6'},isSubmitting&&styles.saveButtonDisabled]} onPress={savePerformanceReview} disabled={isSubmitting}>{isSubmitting?<ActivityIndicator size={16} color="#fff"/>:<Text style={styles.saveButtonText}>Save</Text>}</TouchableOpacity>
          </View>
          <KeyboardAvoidingView style={styles.formContainer} behavior={Platform.OS==='ios'?'padding':'height'}>
            <ScrollView style={styles.formScrollView} showsVerticalScrollIndicator={false}>
              <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',textTransform:'uppercase',marginBottom:8}}>SELECT EMPLOYEE *</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{gap:8,marginBottom:16}}>
                {(employeeData.employees||[]).slice(0,15).map(e=>(
                  <TouchableOpacity key={e.id} onPress={()=>setPerformanceReview(p=>({...p,employeeId:e.id||e.employeeId,employeeName:e.name}))}
                    style={{paddingHorizontal:12,paddingVertical:8,borderRadius:12,backgroundColor:performanceReview.employeeName===e.name?'#8B5CF6':'#F3F4F6',borderWidth:1,borderColor:performanceReview.employeeName===e.name?'#8B5CF6':'#E5E7EB'}}>
                    <Text style={{fontSize:12,color:performanceReview.employeeName===e.name?'#fff':'#374151',fontWeight:'600'}}>{e.name}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
              <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',textTransform:'uppercase',marginBottom:8}}>OVERALL RATING</Text>
              <View style={{flexDirection:'row',justifyContent:'center',gap:12,marginBottom:8}}>
                {['1','2','3','4','5'].map(r=>(
                  <TouchableOpacity key={r} onPress={()=>setPerformanceReview(p=>({...p,overallRating:r}))}
                    style={{width:48,height:48,borderRadius:24,backgroundColor:performanceReview.overallRating===r?'#8B5CF6':'#F3F4F6',borderWidth:2,borderColor:performanceReview.overallRating===r?'#8B5CF6':'#E5E7EB',alignItems:'center',justifyContent:'center'}}>
                    <Text style={{fontSize:16,fontWeight:'900',color:performanceReview.overallRating===r?'#fff':'#6B7280'}}>{r}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              <Text style={{textAlign:'center',fontSize:12,color:'#6B7280',marginBottom:16}}>{['','Poor','Below Average','Average','Good','Excellent'][parseInt(performanceReview.overallRating)||3]}</Text>
              <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',textTransform:'uppercase',marginBottom:10}}>SKILL RATINGS</Text>
              {Object.entries(performanceReview.ratings||{}).map(([skill,val])=>(
                <View key={skill} style={{flexDirection:'row',alignItems:'center',marginBottom:10}}>
                  <Text style={{width:110,fontSize:13,color:'#374151',textTransform:'capitalize'}}>{skill}</Text>
                  <View style={{flexDirection:'row',gap:6}}>
                    {['1','2','3','4','5'].map(r=>(
                      <TouchableOpacity key={r} onPress={()=>setPerformanceReview(p=>({...p,ratings:{...p.ratings,[skill]:r}}))}
                        style={{width:30,height:30,borderRadius:15,backgroundColor:parseInt(val)>=parseInt(r)?'#8B5CF6':'#F3F4F6',alignItems:'center',justifyContent:'center'}}>
                        <Text style={{fontSize:12,fontWeight:'700',color:parseInt(val)>=parseInt(r)?'#fff':'#9CA3AF'}}>{r}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              ))}
              {[{label:'REVIEW PERIOD',key:'reviewPeriod',ph:'Q1 2026'},{label:'REVIEW DATE',key:'reviewDate',ph:'YYYY-MM-DD'},{label:'REVIEWER',key:'reviewer',ph:'Manager name'},{label:'NEXT REVIEW DATE',key:'nextReviewDate',ph:'YYYY-MM-DD'},{label:'GOALS',key:'goals',ph:'Goals for this period…',multi:true},{label:'ACHIEVEMENTS',key:'achievements',ph:'Key achievements…',multi:true},{label:'AREAS FOR IMPROVEMENT',key:'areasForImprovement',ph:'Development areas…',multi:true},{label:'DEVELOPMENT PLAN',key:'developmentPlan',ph:'Training plans…',multi:true},{label:'COMMENTS',key:'comments',ph:'Additional comments…',multi:true}].map(f=>(
                <View key={f.key} style={{marginBottom:12}}>
                  <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',textTransform:'uppercase',marginBottom:6}}>{f.label}</Text>
                  <TextInput style={{backgroundColor:'#F9FAFB',borderWidth:1.5,borderColor:'#E5E7EB',borderRadius:12,paddingHorizontal:14,paddingVertical:11,fontSize:14,color:'#111827',...(f.multi?{minHeight:80,textAlignVertical:'top'}:{})}}
                    value={performanceReview[f.key]||''} onChangeText={v=>setPerformanceReview(p=>({...p,[f.key]:v}))} placeholder={f.ph} placeholderTextColor="#D1D5DB" multiline={!!f.multi}/>
                </View>
              ))}
              <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',textTransform:'uppercase',marginBottom:8}}>STATUS</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{gap:6,marginBottom:20}}>
                {['Draft','In Progress','Completed','Approved','Archived'].map(s=>(
                  <TouchableOpacity key={s} onPress={()=>setPerformanceReview(p=>({...p,status:s}))} style={{paddingHorizontal:12,paddingVertical:8,borderRadius:10,backgroundColor:performanceReview.status===s?'#8B5CF6':'#F3F4F6',borderWidth:1,borderColor:performanceReview.status===s?'#8B5CF6':'#E5E7EB'}}>
                    <Text style={{fontSize:12,color:performanceReview.status===s?'#fff':'#6B7280',fontWeight:'600'}}>{s}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </ScrollView>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </Modal>

      {/* Employee Code Modal */}
      <Modal visible={showEmployeeCodeModal} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity style={styles.modalCloseButton} onPress={()=>setShowEmployeeCodeModal(false)}><MaterialCommunityIcons name="close" size={24} color="#64748b"/></TouchableOpacity>
            <Text style={styles.modalTitle}>New Employee Code</Text>
            <TouchableOpacity style={[styles.saveButton,{backgroundColor:'#06B6D4'},isSubmitting&&styles.saveButtonDisabled]} onPress={saveEmployeeCode} disabled={isSubmitting}>{isSubmitting?<ActivityIndicator size={16} color="#fff"/>:<Text style={styles.saveButtonText}>Create</Text>}</TouchableOpacity>
          </View>
          <KeyboardAvoidingView style={styles.formContainer} behavior={Platform.OS==='ios'?'padding':'height'}>
            <ScrollView style={styles.formScrollView} showsVerticalScrollIndicator={false}>
              {[{label:'CODE *',key:'code',ph:'e.g. FULL_TIME'},{label:'NAME *',key:'name',ph:'Full-Time Employee'},{label:'DESCRIPTION',key:'description',ph:'Brief description'},{label:'BENEFITS',key:'benefits',ph:'Health, pension, PTO…'},{label:'REQUIREMENTS',key:'requirements',ph:'40 hours/week…'}].map(f=>(
                <View key={f.key} style={{marginBottom:14}}>
                  <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',textTransform:'uppercase',marginBottom:6}}>{f.label}</Text>
                  <TextInput style={{backgroundColor:'#F9FAFB',borderWidth:1.5,borderColor:'#E5E7EB',borderRadius:12,paddingHorizontal:14,paddingVertical:11,fontSize:14,color:'#111827'}}
                    value={employeeCode[f.key]||''} onChangeText={v=>setEmployeeCode(p=>({...p,[f.key]:v}))} placeholder={f.ph} placeholderTextColor="#D1D5DB"/>
                </View>
              ))}
              <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',textTransform:'uppercase',marginBottom:8}}>CATEGORY</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{gap:6,marginBottom:14}}>
                {['Employment Type','Skill Level','Security Clearance','Department Code','Location Code','Project Code'].map(c=>(
                  <TouchableOpacity key={c} onPress={()=>setEmployeeCode(p=>({...p,category:c}))} style={{paddingHorizontal:12,paddingVertical:8,borderRadius:10,backgroundColor:employeeCode.category===c?'#06B6D4':'#F3F4F6',borderWidth:1,borderColor:employeeCode.category===c?'#06B6D4':'#E5E7EB'}}>
                    <Text style={{fontSize:12,color:employeeCode.category===c?'#fff':'#6B7280',fontWeight:'600'}}>{c}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
              <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',textTransform:'uppercase',marginBottom:8}}>COLOR</Text>
              <View style={{flexDirection:'row',flexWrap:'wrap',gap:10,marginBottom:14}}>
                {['#0E4E96','#10B981','#F59E0B','#EF4444','#8B5CF6','#06B6D4','#EC4899','#374151'].map(col=>(
                  <TouchableOpacity key={col} onPress={()=>setEmployeeCode(p=>({...p,color:col}))}
                    style={{width:40,height:40,borderRadius:20,backgroundColor:col,borderWidth:employeeCode.color===col?3:0,borderColor:'#fff',shadowColor:col,shadowOffset:{width:0,height:2},shadowOpacity:0.4,shadowRadius:4,elevation:3,alignItems:'center',justifyContent:'center'}}>
                    {employeeCode.color===col&&<MaterialCommunityIcons name="check" size={20} color="#fff"/>}
                  </TouchableOpacity>
                ))}
              </View>
              <View style={{flexDirection:'row',alignItems:'center',justifyContent:'space-between',paddingVertical:10,marginBottom:20}}>
                <Text style={{fontSize:14,color:'#374151'}}>Active</Text>
                <Switch value={employeeCode.isActive} onValueChange={v=>setEmployeeCode(p=>({...p,isActive:v}))} trackColor={{false:'#D1D5DB',true:'#06B6D4'}} thumbColor="#fff"/>
              </View>
            </ScrollView>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </Modal>

      {/* Edit Employee Modal — self-contained local state */}
      <EditEmployeeModal
        visible={showEditEmployeeModal}
        employee={selectedEmployee}
        isSubmitting={isSubmitting}
        onClose={() => { setShowEditEmployeeModal(false); setSelectedEmployee(null); setFormErrors({}); }}
        onSave={async (formData) => {
          setIsSubmitting(true);
          try {
            const updatedEmployee = {
              ...selectedEmployee, ...formData,
              updatedAt: new Date().toISOString(),
            };
            const wasActive = (employeeData.employees||[]).find(e => e.id === selectedEmployee.id)?.status === 'Active';
            const isActive  = updatedEmployee.status === 'Active';
            const updatedData = {
              ...employeeData,
              employees: (employeeData.employees||[]).map(e =>
                e.id === selectedEmployee.id ? updatedEmployee : e
              ),
              overview: { ...employeeData.overview,
                activeEmployees: wasActive && !isActive
                  ? (employeeData.overview?.activeEmployees||0) - 1
                  : !wasActive && isActive
                    ? (employeeData.overview?.activeEmployees||0) + 1
                    : employeeData.overview?.activeEmployees||0,
              },
            };
            updatedData.analytics = calculateAnalytics(updatedData.employees);
            await saveEmployeeData(updatedData);
            try {
              // Transform to server schema for PUT
              const rawName2 = (updatedEmployee.name || '').trim();
              const np2 = rawName2.split(/\s+/);
              const serverUpdate = {
                first_name:      np2[0] || rawName2 || null,
                last_name:       np2.length > 1 ? np2.slice(1).join(' ') : null,
                email:           updatedEmployee.email           || undefined,
                phone:           updatedEmployee.phone           || null,
                department:      updatedEmployee.department      || null,
                position:        updatedEmployee.position        || null,
                hire_date:       updatedEmployee.startDate       || null,
                status:          (updatedEmployee.status || 'Active').toLowerCase(),
                employment_type: (updatedEmployee.employeeCode || 'full_time').toLowerCase(),
                salary:          updatedEmployee.salary ? parseFloat(updatedEmployee.salary) : null,
                notes:           updatedEmployee.notes            || null,
                manager:         updatedEmployee.manager          || null,
                profile_image:   updatedEmployee.avatar && !updatedEmployee.avatar.includes('ui-avatars') ? updatedEmployee.avatar : undefined,
              };
              // Remove undefined fields
              Object.keys(serverUpdate).forEach(k => serverUpdate[k] === undefined && delete serverUpdate[k]);
              await ManageAPI.update(selectedEmployee.id, serverUpdate);
            } catch (updateErr) {
              console.warn('[EditEmployee] Server PUT failed:', updateErr.message);
            }
            setShowEditEmployeeModal(false);
            setSelectedEmployee(null);
            setFormErrors({});
            Alert.alert('✅ Updated', `${updatedEmployee.name} updated`);
          } catch (err) {
            Alert.alert('Error', 'Failed to update employee');
          } finally { setIsSubmitting(false); }
        }}
      />


      {/* Import Modal - CSV paste */}
      <Modal visible={showImportModal} animationType="slide" presentationStyle="pageSheet"
        onRequestClose={() => setShowImportModal(false)}>
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity style={styles.modalCloseButton} onPress={() => setShowImportModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#64748b"/>
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Import Employees</Text>
            <TouchableOpacity
              style={[styles.saveButton, { backgroundColor: '#10b981' }]}
              onPress={async () => {
                // Parse pasted CSV
                const csvText = importCsvText.trim();
                if (!csvText) { Alert.alert('Empty', 'Paste CSV data first.'); return; }
                try {
                  const lines = csvText.split('\n').map(l => l.trim()).filter(Boolean);
                  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
                  const rows = lines.slice(1);
                  const getCol = (row, ...names) => {
                    const cells = row.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
                    for (const name of names) {
                      const idx = headers.indexOf(name);
                      if (idx !== -1 && cells[idx]) return cells[idx];
                    }
                    return '';
                  };
                  const imported = rows.map((row, i) => ({
                    id: `csv_${Date.now()}_${i}`,
                    employeeId: getCol(row, 'employee id', 'employeeid', 'id') || `EMP${String(employeeData.employees.length + i + 1).padStart(4,'0')}`,
                    name: getCol(row, 'name', 'full name', 'fullname', 'employee name'),
                    email: getCol(row, 'email', 'email address', 'work email'),
                    phone: getCol(row, 'phone', 'phone number', 'mobile'),
                    department: getCol(row, 'department', 'dept') || 'General',
                    position: getCol(row, 'position', 'job title', 'role', 'title'),
                    salary: parseInt(getCol(row, 'salary', 'annual salary', 'pay')) || 0,
                    status: getCol(row, 'status') || 'Active',
                    startDate: getCol(row, 'start date', 'startdate', 'hire date', 'hiredate'),
                    employeeCode: getCol(row, 'type', 'employment type', 'employee type') || 'FULL_TIME',
                    location: getCol(row, 'location', 'work location', 'office') || 'Office',
                    manager: getCol(row, 'manager', 'reports to'),
                    performanceRating: parseInt(getCol(row, 'rating', 'performance')) || 3,
                    attendanceRate: parseInt(getCol(row, 'attendance', 'attendance rate')) || 95,
                    leaveBalance: 25, warningCount: 0, skills: [], notes: '',
                    avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(getCol(row,'name','full name')||'E')}&background=0e4e96&color=fff&size=128`,
                    createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
                  })).filter(e => e.name);
                  if (imported.length === 0) { Alert.alert('No Data', 'No valid rows found. Make sure the first row has column headers.'); return; }
                  const merged = [...(employeeData.employees || []), ...imported];
                  const updatedData = { ...employeeData, employees: merged };
                  updatedData.analytics = calculateAnalytics(merged);
                  updatedData.overview = { ...updatedData.overview, totalEmployees: merged.length, activeEmployees: merged.filter(e => e.status === 'Active').length };
                  await saveEmployeeData(updatedData);
                  setShowImportModal(false);
                  setImportCsvText('');
                  Alert.alert('✅ Imported', `${imported.length} employees imported successfully.`);
                } catch (err) {
                  Alert.alert('Parse Error', 'Could not parse CSV: ' + err.message);
                }
              }}>
              <Text style={styles.saveButtonText}>Import</Text>
            </TouchableOpacity>
          </View>
          <KeyboardAvoidingView style={styles.formContainer} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
            <ScrollView style={styles.formScrollView} showsVerticalScrollIndicator={false}>
              <View style={{ backgroundColor: '#EFF6FF', borderRadius: 12, padding: 14, marginBottom: 16 }}>
                <Text style={{ fontSize: 13, fontWeight: '700', color: '#0e4e96', marginBottom: 6 }}>CSV Format Guide</Text>
                <Text style={{ fontSize: 11, color: '#374151', lineHeight: 18, fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace' }}>
                  {'name,email,department,position,salary,status,start date\n'}
                  {'Jane Smith,jane@co.com,Engineering,Developer,60000,Active,2024-01-15'}
                </Text>
              </View>
              <Text style={{ fontSize: 11, fontWeight: '800', color: '#9CA3AF', textTransform: 'uppercase', marginBottom: 8 }}>
                PASTE CSV DATA
              </Text>
              <TextInput
                style={{ backgroundColor: '#F9FAFB', borderWidth: 1.5, borderColor: '#E5E7EB', borderRadius: 12,
                  paddingHorizontal: 14, paddingVertical: 12, fontSize: 13, color: '#111827', minHeight: 200,
                  textAlignVertical: 'top', fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace' }}
                value={importCsvText}
                onChangeText={setImportCsvText}
                placeholder={"name,email,department,position,salary\nJane Smith,jane@co.com,Engineering,Developer,60000"}
                placeholderTextColor="#D1D5DB"
                multiline
              />
              <Text style={{ fontSize: 11, color: '#9CA3AF', marginTop: 8 }}>
                Supported columns: name, email, phone, department, position, salary, status, start date, type, location, manager, rating
              </Text>
            </ScrollView>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </Modal>

      {/* Settings Modal */}
      <Modal visible={showSettingsModal} animationType="slide" presentationStyle="pageSheet"
        onRequestClose={() => setShowSettingsModal(false)}>
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity style={styles.modalCloseButton} onPress={() => setShowSettingsModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#64748b"/>
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Settings</Text>
            <View style={{ width: 60 }}/>
          </View>
          <ScrollView style={styles.formScrollView} showsVerticalScrollIndicator={false}>
            {/* Data Management */}
            <Text style={{ fontSize: 13, fontWeight: '900', color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Data Management</Text>
            {[
              { label: 'Export to CSV', icon: 'file-delimited', color: '#10b981', onPress: () => { setShowSettingsModal(false); exportEmployeeData('csv'); } },
              { label: 'Export to JSON', icon: 'code-json', color: '#0e4e96', onPress: () => { setShowSettingsModal(false); exportEmployeeData('json'); } },
              { label: 'Import Employees', icon: 'file-import', color: '#8b5cf6', onPress: () => { setShowSettingsModal(false); importEmployeeData(); } },
              { label: 'Create Backup', icon: 'backup-restore', color: '#f59e0b', onPress: async () => { await createBackup(); Alert.alert('✅ Backed up', 'Data backed up successfully.'); } },
              { label: 'Restore from Backup', icon: 'restore', color: '#dc2626', onPress: () => { Alert.alert('Restore Backup', 'This will overwrite current data. Continue?', [{ text: 'Cancel', style: 'cancel' }, { text: 'Restore', style: 'destructive', onPress: restoreFromBackup }]); } },
            ].map((item, i) => (
              <TouchableOpacity key={i} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 14,
                borderBottomWidth: i < 4 ? 1 : 0, borderBottomColor: '#F1F5F9', gap: 14 }}
                onPress={item.onPress}>
                <View style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: item.color + '15', alignItems: 'center', justifyContent: 'center' }}>
                  <MaterialCommunityIcons name={item.icon} size={22} color={item.color}/>
                </View>
                <Text style={{ flex: 1, fontSize: 15, color: '#374151', fontWeight: '600' }}>{item.label}</Text>
                <MaterialCommunityIcons name="chevron-right" size={18} color="#D1D5DB"/>
              </TouchableOpacity>
            ))}

            <Text style={{ fontSize: 13, fontWeight: '900', color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 24, marginBottom: 12 }}>Payroll Integration</Text>
            {[
              { label: 'Sync All to Payroll', icon: 'account-sync', color: '#3b82f6', onPress: async () => { await syncAllEmployeesToPayroll(); setShowSettingsModal(false); } },
              { label: 'Open Payroll Screen', icon: 'cash-multiple', color: '#059669', onPress: () => { setShowSettingsModal(false); navigateToPayroll(); } },
            ].map((item, i) => (
              <TouchableOpacity key={i} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 14,
                borderBottomWidth: i < 1 ? 1 : 0, borderBottomColor: '#F1F5F9', gap: 14 }}
                onPress={item.onPress}>
                <View style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: item.color + '15', alignItems: 'center', justifyContent: 'center' }}>
                  <MaterialCommunityIcons name={item.icon} size={22} color={item.color}/>
                </View>
                <Text style={{ flex: 1, fontSize: 15, color: '#374151', fontWeight: '600' }}>{item.label}</Text>
                <MaterialCommunityIcons name="chevron-right" size={18} color="#D1D5DB"/>
              </TouchableOpacity>
            ))}

            <Text style={{ fontSize: 13, fontWeight: '900', color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 24, marginBottom: 12 }}>Display Settings</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' }}>
              <Text style={{ fontSize: 15, color: '#374151' }}>Auto Backup</Text>
              <Switch
                value={employeeData.settings?.autoBackup ?? true}
                onValueChange={async v => {
                  const upd = { ...employeeData, settings: { ...employeeData.settings, autoBackup: v } };
                  await saveEmployeeData(upd);
                }}
                trackColor={{ false: '#D1D5DB', true: '#0e4e96' }} thumbColor="#fff"
              />
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 12, marginBottom: 40 }}>
              <Text style={{ fontSize: 15, color: '#374151' }}>Notifications</Text>
              <Switch
                value={employeeData.settings?.notifications ?? true}
                onValueChange={async v => {
                  const upd = { ...employeeData, settings: { ...employeeData.settings, notifications: v } };
                  await saveEmployeeData(upd);
                }}
                trackColor={{ false: '#D1D5DB', true: '#0e4e96' }} thumbColor="#fff"
              />
            </View>

            <View style={{ backgroundColor: '#F8FAFC', borderRadius: 12, padding: 14 }}>
              <Text style={{ fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>
                Employee Management v1.0{'\n'}
                Last backup: {employeeData.lastBackup ? new Date(employeeData.lastBackup).toLocaleString('en-GB') : 'Never'}
              </Text>
            </View>
          </ScrollView>
        </SafeAreaView>
      </Modal>

    </SafeAreaView>
  );
};

// Enhanced Styles
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  header: {
    paddingTop: Platform.OS === 'ios' ? 50 : 30,
    paddingBottom: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitleContainer: {
    flex: 1,
    alignItems: 'center',
    marginHorizontal: 16,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#fff',
    textAlign: 'center',
  },
  headerSubtitle: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerActionButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
  },
  tabsContainer: {
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    position: 'relative',
  },
  activeTab: {
    backgroundColor: '#f8fafc',
  },
  tabText: {
    marginLeft: 8,
    fontSize: 14,
    color: '#64748b',
    fontWeight: '500',
  },
  activeTabText: {
    color: '#0e4e96',
    fontWeight: '600',
  },
  tabIndicator: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
  },
  tabContent: {
    flex: 1,
    padding: 20,
  },
  tabContentFull: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748b',
  },
  comingSoon: {
    flex: 1,
    textAlign: 'center',
    textAlignVertical: 'center',
    fontSize: 18,
    color: '#64748b',
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  statCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    width: (width - 60) / 2,
    borderLeftWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  statCardContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statContent: {
    flex: 1,
  },
  statValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1e293b',
    marginBottom: 4,
  },
  statTitle: {
    fontSize: 14,
    color: '#64748b',
    fontWeight: '600',
  },
  statSubtitle: {
    fontSize: 12,
    color: '#94a3b8',
    marginTop: 2,
  },
  statIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  quickActionsSection: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1e293b',
    marginBottom: 16,
  },
  quickActionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  quickAction: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    width: (width - 60) / 2,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  quickActionText: {
    marginTop: 8,
    fontSize: 14,
    fontWeight: '500',
    color: '#1e293b',
    textAlign: 'center',
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  departmentList: {
    gap: 16,
  },
  departmentItem: {
    padding: 16,
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#0e4e96',
  },
  departmentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  departmentName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1e293b',
  },
  departmentCount: {
    fontSize: 14,
    color: '#64748b',
  },
  departmentStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  departmentStat: {
    fontSize: 12,
    color: '#64748b',
  },
  progressBar: {
    height: 6,
    backgroundColor: '#e2e8f0',
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#0e4e96',
    borderRadius: 3,
  },
  activityList: {
    gap: 16,
  },
  activityItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  activityContent: {
    flex: 1,
    marginLeft: 12,
  },
  activityText: {
    fontSize: 14,
    color: '#1e293b',
    marginBottom: 2,
  },
  activityTime: {
    fontSize: 12,
    color: '#94a3b8',
  },
  searchSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    gap: 12,
  },
  searchContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: '#d1d5db',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  searchInput: {
    flex: 1,
    marginLeft: 12,
    fontSize: 16,
    color: '#1e293b',
  },
  viewToggle: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 4,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  viewToggleButton: {
    padding: 8,
    borderRadius: 8,
  },
  viewToggleButtonActive: {
    backgroundColor: '#f1f5f9',
  },
  filtersSection: {
    marginBottom: 16,
  },
  filterGroup: {
    marginRight: 20,
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748b',
    marginBottom: 8,
  },
  filterOption: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: '#f1f5f9',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  filterOptionSelected: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  filterOptionText: {
    fontSize: 14,
    color: '#64748b',
    fontWeight: '500',
  },
  filterOptionTextSelected: {
    color: '#fff',
  },
  sortSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  sortLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748b',
    marginRight: 12,
  },
  sortOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: '#f1f5f9',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  sortOptionSelected: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  sortOptionText: {
    fontSize: 14,
    color: '#64748b',
    fontWeight: '500',
    marginRight: 4,
  },
  sortOptionTextSelected: {
    color: '#fff',
  },
  actionsSection: {
    marginBottom: 16,
  },
  selectionActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  selectionButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  selectAllButton: {
    backgroundColor: '#8b5cf6',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  selectAllButtonText: {
    color: '#fff',
    fontWeight: '600',
    marginLeft: 8,
    fontSize: 14,
  },
  bulkActionButton: {
    backgroundColor: '#f59e0b',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  bulkActionButtonText: {
    color: '#fff',
    fontWeight: '600',
    marginLeft: 8,
    fontSize: 14,
  },
  clearButton: {
    backgroundColor: '#64748b',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  clearButtonText: {
    color: '#fff',
    fontWeight: '600',
    marginLeft: 8,
    fontSize: 14,
  },
  addButton: {
    backgroundColor: '#10b981',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  addButtonText: {
    color: '#fff',
    fontWeight: '600',
    marginLeft: 8,
    fontSize: 14,
  },
  employeesList: {
    paddingBottom: 20,
  },
  listFooter: {
    height: 20,
  },
  employeeCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  selectedCard: {
    borderWidth: 2,
    borderColor: '#0e4e96',
    backgroundColor: '#f0f9ff',
  },
  employeeHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  selectionCheckbox: {
    marginRight: 12,
    marginTop: 2,
  },
  employeeAvatar: {
    width: 60,
    height: 60,
    borderRadius: 30,
    marginRight: 16,
    borderWidth: 2,
    borderColor: '#e2e8f0',
  },
  employeeInfo: {
    flex: 1,
    marginRight: 12,
  },
  employeeName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1e293b',
    marginBottom: 4,
  },
  employeePosition: {
    fontSize: 16,
    color: '#0e4e96',
    fontWeight: '600',
    marginBottom: 2,
  },
  employeeDepartment: {
    fontSize: 14,
    color: '#64748b',
    marginBottom: 2,
  },
  employeeEmail: {
    fontSize: 14,
    color: '#0e4e96',
    marginBottom: 12,
  },
  employeeTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  employeeCodeTag: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  employeeCodeText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  statusTag: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  statusText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  employeeActions: {
    gap: 8,
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#f1f5f9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  employeeDetails: {
    marginBottom: 16,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  detailText: {
    fontSize: 14,
    color: '#64748b',
    fontWeight: '500',
  },
  performanceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
  },
  performanceItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  performanceLabel: {
    fontSize: 14,
    color: '#64748b',
    fontWeight: '500',
  },
  warningBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#fef2f2',
    borderRadius: 12,
  },
  warningCount: {
    fontSize: 12,
    fontWeight: '600',
  },
  ratingContainer: {
    flexDirection: 'row',
    gap: 2,
  },
  skillsContainer: {
    marginBottom: 16,
  },
  skillsLabel: {
    fontSize: 14,
    color: '#64748b',
    fontWeight: '500',
    marginBottom: 8,
  },
  skillsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  skillTag: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#f1f5f9',
    borderRadius: 16,
  },
  skillText: {
    fontSize: 12,
    color: '#374151',
    fontWeight: '500',
  },
  moreSkillsTag: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#e2e8f0',
    borderRadius: 16,
  },
  moreSkillsText: {
    fontSize: 12,
    color: '#64748b',
    fontWeight: '500',
  },
  quickActions: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
  },
  quickActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#f8fafc',
    gap: 6,
  },
  quickActionText: {
    fontSize: 12,
    color: '#64748b',
    fontWeight: '500',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#64748b',
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#94a3b8',
    marginTop: 8,
    textAlign: 'center',
    marginBottom: 24,
  },
  emptyActionButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: '#0e4e96',
    borderRadius: 8,
  },
  emptyActionText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
  },
  emptyState: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyStateText: {
    fontSize: 14,
    color: '#94a3b8',
    textAlign: 'center',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  modalCloseButton: {
    padding: 8,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1e293b',
    flex: 1,
    textAlign: 'center',
    marginHorizontal: 16,
  },
  saveButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    minWidth: 80,
    alignItems: 'center',
  },
  saveButtonDisabled: {
    backgroundColor: '#94a3b8',
  },
  saveButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 16,
  },
  formContainer: {
    flex: 1,
  },
  formScrollView: {
    flex: 1,
    padding: 20,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    maxHeight: '80%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 16,
    elevation: 8,
  },
  modalText: {
    fontSize: 16,
    color: '#64748b',
    marginBottom: 24,
    lineHeight: 24,
  },
  bulkActionOptions: {
    gap: 12,
  },
  bulkActionOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    gap: 16,
  },
  bulkActionTextContainer: {
    flex: 1,
  },
  bulkActionOptionText: {
    fontSize: 16,
    color: '#1e293b',
    fontWeight: '600',
    marginBottom: 4,
  },
  bulkActionOptionSubtext: {
    fontSize: 14,
    color: '#64748b',
  },
  exportOptions: {
    flexDirection: 'row',
    gap: 16,
  },
  exportOption: {
    flex: 1,
    alignItems: 'center',
    padding: 20,
    borderRadius: 12,
    backgroundColor: '#f8fafc',
    borderWidth: 2,
    borderColor: '#e2e8f0',
    borderStyle: 'dashed',
  },
  exportOptionText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1e293b',
    marginTop: 12,
    marginBottom: 4,
  },
  exportOptionSubtext: {
    fontSize: 14,
    color: '#64748b',
    textAlign: 'center',
  },
});

// ── Inline Employee Form (used inside Add/Edit modals) ───────────────────────
// ── Shared photo picker helper ────────────────────────────────────────────────
const pickEmployeePhoto = async (onResult) => {
  try {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert('Permission Required', 'Please allow access to your photo library to upload employee photos.');
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images, allowsEditing: true, aspect: [1, 1], quality: 0.5, base64: true,
    });
    if (!result.canceled && result.assets?.[0]?.uri) {
      onResult(result.assets[0].uri);
    }
  } catch (err) {
    Alert.alert('Error', 'Could not open photo library: ' + err.message);
  }
};

const takeEmployeePhoto = async (onResult) => {
  try {
    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert('Permission Required', 'Please allow camera access to take employee photos.');
      return;
    }
    const result = await ImagePicker.launchCameraAsync({
      allowsEditing: true, aspect: [1, 1], quality: 0.5, base64: true,
    });
    if (!result.canceled && result.assets?.[0]?.uri) {
      onResult(result.assets[0].uri);
    }
  } catch (err) {
    Alert.alert('Error', 'Could not open camera: ' + err.message);
  }
};

const showPhotoOptions = (onResult) => {
  Alert.alert(
    'Employee Photo',
    'Choose photo source',
    [
      { text: 'Take Photo', onPress: () => takeEmployeePhoto(onResult) },
      { text: 'Choose from Library', onPress: () => pickEmployeePhoto(onResult) },
      { text: 'Cancel', style: 'cancel' },
    ]
  );
};

// ── Self-contained employee form modal — holds its OWN local state ───────────
// This is the key fix: typing NEVER touches the parent component's state,
// so the 4k-line screen does not re-render on every keystroke.
const AddEmployeeModal = React.memo(({ visible, onClose, onSave, initialData, isSubmitting, existingCount }) => {
  const BLANK = {
    name:'', email:'', phone:'', department:'Engineering', position:'', salary:'',
    startDate: new Date().toISOString().split('T')[0], employeeType:'Full-time',
    location:'Office', manager:'', address:'', nationalId:'', emergencyContact:'',
    emergencyPhone:'', skills:'', notes:'', employeeId:'', employeeCode:'FULL_TIME',
    status:'Active', dateOfBirth:'', bankAccount:'',
  };
  const [form,   setForm]   = React.useState(BLANK);
  const [errors, setErrors] = React.useState({});

  // Reset form each time modal opens
  React.useEffect(() => {
    if (visible) {
      setForm(initialData || BLANK);
      setErrors({});
    }
  }, [visible]);

  const set = React.useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim())     e.name     = 'Name is required';
    if (!form.email.trim())    e.email    = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Email is invalid';
    if (!form.position.trim()) e.position = 'Position is required';
    if (form.salary && isNaN(form.salary)) e.salary = 'Must be a number';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave({
      ...form,
      salary:    parseInt(form.salary) || 0,
      skills:    typeof form.skills === 'string'
                   ? form.skills.split(',').map(s => s.trim()).filter(Boolean)
                   : (form.skills || []),
      employeeId: form.employeeId || `EMP${String(existingCount + 1).padStart(4, '0')}`,
      avatar:    `https://ui-avatars.com/api/?name=${encodeURIComponent(form.name || 'E')}&background=0e4e96&color=fff&size=128`,
    });
  };

  const depts    = ['Engineering','Marketing','Sales','HR','Finance','Operations','IT','Legal','Customer Service','Research'];
  const locs     = ['Office','Remote','Hybrid','Field','On-Site','Home Office'];
  const codes    = ['FULL_TIME','PART_TIME','CONTRACT','INTERN','CONSULTANT','REMOTE','HYBRID','FREELANCE'];
  const statuses = ['Active','Inactive','On Leave','Probation','Terminated'];

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <SafeAreaView style={{ flex:1, backgroundColor:'#f8fafc' }}>
        <View style={{ flexDirection:'row', alignItems:'center', justifyContent:'space-between',
          paddingHorizontal:20, paddingVertical:14, backgroundColor:'#fff',
          borderBottomWidth:1, borderBottomColor:'#E5E7EB' }}>
          <TouchableOpacity onPress={onClose} style={{ padding:4 }}>
            <MaterialCommunityIcons name="close" size={24} color="#64748b"/>
          </TouchableOpacity>
          <Text style={{ fontSize:17, fontWeight:'800', color:'#111827' }}>Add New Employee</Text>
          <TouchableOpacity
            onPress={handleSave}
            disabled={isSubmitting}
            style={{ backgroundColor:'#10b981', borderRadius:10, paddingHorizontal:18,
              paddingVertical:9, opacity: isSubmitting ? 0.6 : 1 }}>
            {isSubmitting
              ? <ActivityIndicator size={16} color="#fff"/>
              : <Text style={{ color:'#fff', fontWeight:'800', fontSize:14 }}>Save</Text>}
          </TouchableOpacity>
        </View>

        <KeyboardAvoidingView style={{ flex:1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <ScrollView style={{ flex:1 }} contentContainerStyle={{ padding:20, paddingBottom:40 }} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">

            {/* Tappable avatar — tap to change photo */}
            <View style={{ alignItems:'center', marginBottom:20 }}>
              <TouchableOpacity
                onPress={() => showPhotoOptions(uri => set('avatar', uri))}
                style={{ position:'relative' }}>
                <Image
                  source={{ uri: form.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(form.name||'Employee')}&background=0e4e96&color=fff&size=128` }}
                  style={{ width:90, height:90, borderRadius:45, borderWidth:3, borderColor:'#0e4e96' }}/>
                <View style={{
                  position:'absolute', bottom:0, right:0,
                  width:28, height:28, borderRadius:14,
                  backgroundColor:'#0e4e96', borderWidth:2, borderColor:'#fff',
                  alignItems:'center', justifyContent:'center' }}>
                  <MaterialCommunityIcons name="camera" size={14} color="#fff"/>
                </View>
              </TouchableOpacity>
              <Text style={{ fontSize:12, color:'#0e4e96', marginTop:8, fontWeight:'600' }}>
                <Text>{form.avatar && !form.avatar.startsWith('https://ui-avatars') ? 'Tap to change photo' : 'Tap to add photo'}</Text>
              </Text>
            </View>

            {[
              { label:'Full Name *',             field:'name',             ph:'John Smith',                        kb:'default',       err: errors.name },
              { label:'Employee ID',             field:'employeeId',       ph:'EMP0001' },
              { label:'Work Email *',            field:'email',            ph:'john@company.com',                  kb:'email-address', err: errors.email },
              { label:'Phone',                   field:'phone',            ph:'+44 7xxx xxxxxx',                   kb:'phone-pad' },
              { label:'Job Title / Position *',  field:'position',         ph:'Senior Developer',                  err: errors.position },
              { label:'Annual Salary (£)',        field:'salary',           ph:'50000',                             kb:'numeric',       err: errors.salary },
              { label:'Start Date',              field:'startDate',        ph:'YYYY-MM-DD' },
              { label:'Manager / Reports To',    field:'manager',          ph:'Jane Manager' },
              { label:'Address',                 field:'address',          ph:'123 Main Street, London' },
              { label:'Date of Birth',           field:'dateOfBirth',      ph:'YYYY-MM-DD' },
              { label:'National ID',             field:'nationalId',       ph:'National Insurance / ID' },
              { label:'Bank Account (last 4)',   field:'bankAccount',      ph:'****1234' },
              { label:'Emergency Contact Name',  field:'emergencyContact', ph:'Jane Smith' },
              { label:'Emergency Contact Phone', field:'emergencyPhone',   ph:'+44 7xxx xxxxxx',                   kb:'phone-pad' },
              { label:'Skills (comma-separated)',field:'skills',           ph:'JavaScript, React, Python' },
            ].map(({ label, field, ph, kb, err }) => (
              <View key={field} style={{ marginBottom:14 }}>
                <Text style={{ fontSize:11, fontWeight:'800', color:'#9CA3AF',
                  textTransform:'uppercase', letterSpacing:0.5, marginBottom:6 }}>{label}</Text>
                <TextInput
                  style={{ backgroundColor:'#F9FAFB', borderWidth:1.5,
                    borderColor: err ? '#EF4444' : '#E5E7EB', borderRadius:12,
                    paddingHorizontal:14, paddingVertical:11, fontSize:14, color:'#111827' }}
                  value={String(form[field] || '')}
                  onChangeText={v => set(field, v)}
                  placeholder={ph} placeholderTextColor="#D1D5DB"
                  keyboardType={kb || 'default'}
                  autoCapitalize={kb === 'email-address' ? 'none' : 'sentences'}
                />
                {err ? <Text style={{ fontSize:11, color:'#EF4444', marginTop:3 }}>{err}</Text> : null}
              </View>
            ))}

            {/* Notes multiline */}
            <View style={{ marginBottom:14 }}>
              <Text style={{ fontSize:11, fontWeight:'800', color:'#9CA3AF', textTransform:'uppercase', letterSpacing:0.5, marginBottom:6 }}>NOTES</Text>
              <TextInput
                style={{ backgroundColor:'#F9FAFB', borderWidth:1.5, borderColor:'#E5E7EB',
                  borderRadius:12, paddingHorizontal:14, paddingVertical:11, fontSize:14,
                  color:'#111827', minHeight:80, textAlignVertical:'top' }}
                value={form.notes || ''} onChangeText={v => set('notes', v)}
                placeholder="Additional notes…" placeholderTextColor="#D1D5DB" multiline/>
            </View>

            {/* Chip pickers */}
            {[
              { label:'DEPARTMENT', field:'department', opts: depts    },
              { label:'LOCATION',   field:'location',   opts: locs     },
              { label:'EMP TYPE',   field:'employeeCode',opts: codes   },
              { label:'STATUS',     field:'status',     opts: statuses },
            ].map(({ label, field, opts }) => (
              <View key={field} style={{ marginBottom:14 }}>
                <Text style={{ fontSize:11, fontWeight:'800', color:'#9CA3AF', textTransform:'uppercase', letterSpacing:0.5, marginBottom:8 }}>{label}</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap:6 }}>
                  {opts.map(opt => (
                    <TouchableOpacity key={opt} onPress={() => set(field, opt)}
                      style={{ paddingHorizontal:14, paddingVertical:9, borderRadius:12,
                        backgroundColor: form[field] === opt ? '#0E4E96' : '#F3F4F6',
                        borderWidth:1, borderColor: form[field] === opt ? '#0E4E96' : '#E5E7EB' }}>
                      <Text style={{ fontSize:12, color: form[field] === opt ? '#fff' : '#6B7280', fontWeight:'600' }}>{opt}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            ))}

          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );
});

// ── Same pattern for EditEmployeeModal ─────────────────────────────────────────
const EditEmployeeModal = React.memo(({ visible, employee, onClose, onSave, isSubmitting }) => {
  const [form,   setForm]   = React.useState(employee || {});
  const [errors, setErrors] = React.useState({});

  React.useEffect(() => {
    if (visible && employee) {
      setForm({ ...employee, skills: Array.isArray(employee.skills) ? employee.skills.join(', ') : (employee.skills || '') });
      setErrors({});
    }
  }, [visible, employee]);

  const set = React.useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name?.trim())  e.name  = 'Name is required';
    if (!form.email?.trim()) e.email = 'Email is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  if (!employee) return null;

  const depts    = ['Engineering','Marketing','Sales','HR','Finance','Operations','IT','Legal','Customer Service','Research'];
  const locs     = ['Office','Remote','Hybrid','Field','On-Site','Home Office'];
  const codes    = ['FULL_TIME','PART_TIME','CONTRACT','INTERN','CONSULTANT','REMOTE','HYBRID','FREELANCE'];
  const statuses = ['Active','Inactive','On Leave','Probation','Terminated'];

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <SafeAreaView style={{ flex:1, backgroundColor:'#f8fafc' }}>
        <View style={{ flexDirection:'row', alignItems:'center', justifyContent:'space-between',
          paddingHorizontal:20, paddingVertical:14, backgroundColor:'#fff',
          borderBottomWidth:1, borderBottomColor:'#E5E7EB' }}>
          <TouchableOpacity onPress={onClose} style={{ padding:4 }}>
            <MaterialCommunityIcons name="close" size={24} color="#64748b"/>
          </TouchableOpacity>
          <Text style={{ fontSize:17, fontWeight:'800', color:'#111827' }}>Edit Employee</Text>
          <TouchableOpacity
            onPress={() => {
              if (!validate()) return;
              onSave({ ...form,
                salary: parseInt(form.salary) || form.salary || 0,
                skills: typeof form.skills === 'string'
                  ? form.skills.split(',').map(s => s.trim()).filter(Boolean)
                  : (form.skills || []),
              });
            }}
            disabled={isSubmitting}
            style={{ backgroundColor:'#0e4e96', borderRadius:10, paddingHorizontal:18, paddingVertical:9, opacity: isSubmitting ? 0.6 : 1 }}>
            {isSubmitting ? <ActivityIndicator size={16} color="#fff"/> : <Text style={{ color:'#fff', fontWeight:'800', fontSize:14 }}>Update</Text>}
          </TouchableOpacity>
        </View>
        <KeyboardAvoidingView style={{ flex:1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <ScrollView style={{ flex:1 }} contentContainerStyle={{ padding:20, paddingBottom:40 }} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">

            {/* Tappable avatar */}
            <View style={{ alignItems:'center', marginBottom:20 }}>
              <TouchableOpacity
                onPress={() => showPhotoOptions(uri => set('avatar', uri))}
                style={{ position:'relative' }}>
                <Image
                  source={{ uri: form.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent((form.name||'E'))}&background=0e4e96&color=fff&size=128` }}
                  style={{ width:90, height:90, borderRadius:45, borderWidth:3, borderColor:'#0e4e96' }}/>
                <View style={{
                  position:'absolute', bottom:0, right:0,
                  width:28, height:28, borderRadius:14,
                  backgroundColor:'#0e4e96', borderWidth:2, borderColor:'#fff',
                  alignItems:'center', justifyContent:'center' }}>
                  <MaterialCommunityIcons name="camera" size={14} color="#fff"/>
                </View>
              </TouchableOpacity>
              <Text style={{ fontSize:12, color:'#0e4e96', marginTop:8, fontWeight:'600' }}>
                <Text>{form.avatar && !form.avatar.startsWith('https://ui-avatars') ? 'Tap to change photo' : 'Tap to add photo'}</Text>
              </Text>
            </View>

            {[
              { label:'Full Name *',             field:'name',             ph:'John Smith',               err: errors.name },
              { label:'Work Email *',            field:'email',            ph:'john@company.com',         kb:'email-address', err: errors.email },
              { label:'Phone',                   field:'phone',            ph:'+44 7xxx xxxxxx',          kb:'phone-pad' },
              { label:'Job Title / Position',    field:'position',         ph:'Senior Developer' },
              { label:'Annual Salary (£)',        field:'salary',           ph:'50000',                    kb:'numeric' },
              { label:'Start Date',              field:'startDate',        ph:'YYYY-MM-DD' },
              { label:'Manager / Reports To',    field:'manager',          ph:'Jane Manager' },
              { label:'Skills (comma-separated)',field:'skills',           ph:'JavaScript, React, Python' },
              { label:'Notes',                   field:'notes',            ph:'Notes…',                   multi: true },
            ].map(({ label, field, ph, kb, err, multi }) => (
              <View key={field} style={{ marginBottom:14 }}>
                <Text style={{ fontSize:11, fontWeight:'800', color:'#9CA3AF', textTransform:'uppercase', letterSpacing:0.5, marginBottom:6 }}>{label}</Text>
                <TextInput
                  style={{ backgroundColor:'#F9FAFB', borderWidth:1.5,
                    borderColor: err ? '#EF4444' : '#E5E7EB', borderRadius:12,
                    paddingHorizontal:14, paddingVertical:11, fontSize:14, color:'#111827',
                    ...(multi ? { minHeight:70, textAlignVertical:'top' } : {}) }}
                  value={String(form[field] || '')} onChangeText={v => set(field, v)}
                  placeholder={ph} placeholderTextColor="#D1D5DB"
                  keyboardType={kb || 'default'} multiline={!!multi}
                  autoCapitalize={kb === 'email-address' ? 'none' : 'sentences'}/>
                {err ? <Text style={{ fontSize:11, color:'#EF4444', marginTop:3 }}>{err}</Text> : null}
              </View>
            ))}
            {[
              { label:'DEPARTMENT', field:'department', opts:depts    },
              { label:'LOCATION',   field:'location',   opts:locs     },
              { label:'EMP TYPE',   field:'employeeCode',opts:codes   },
              { label:'STATUS',     field:'status',     opts:statuses },
            ].map(({ label, field, opts }) => (
              <View key={field} style={{ marginBottom:14 }}>
                <Text style={{ fontSize:11, fontWeight:'800', color:'#9CA3AF', textTransform:'uppercase', letterSpacing:0.5, marginBottom:8 }}>{label}</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap:6 }}>
                  {opts.map(opt => (
                    <TouchableOpacity key={opt} onPress={() => set(field, opt)}
                      style={{ paddingHorizontal:14, paddingVertical:9, borderRadius:12,
                        backgroundColor: form[field] === opt ? '#0E4E96' : '#F3F4F6',
                        borderWidth:1, borderColor: form[field] === opt ? '#0E4E96' : '#E5E7EB' }}>
                      <Text style={{ fontSize:12, color: form[field] === opt ? '#fff' : '#6B7280', fontWeight:'600' }}>{opt}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            ))}
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );
});

// ── EmployeeForm kept for backward compat (used nowhere now) ──────────────────
const EmployeeForm = ({ employee, setEmployee, errors = {} }) => null;

export default EmployeeManagementScreen;